import Box from "@mui/material/Box";
import React, {useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {reaction} from "mobx";
import Split from "react-split";
import Timeline from "../../librettoEditor/modules/timeline/Timeline";
import V2EditorSidebar, {SidebarItems} from "./V2EditorSidebar";
import V2AssistantSidebar from "./V2AssistantSidebar";
import {V2ProjectSidebar} from "./V2ProjectSidebar";
import V2PropertiesSidebar from "./V2PropertiesSidebar";
import V2ElementsSidebar from "./V2ElementsSidebar";
import V2StockSidebar from "./V2StockSidebar";
import V2AIToolsSidebar from "./V2AIToolsSidebar";
import V2CaptionsSidebar from "./V2CaptionsSidebar";
import AddChaptersSidebar from "./AddChaptersSidebar";
import AutomaticMulticamSidebar from "./AutomaticMulticamSidebar";
import CreateClipsSidebar, { type CreateClipsSettings } from "./CreateClipsSidebar";
import CreateClipsResultsSidebar, { type CreateClipResult } from "./CreateClipsResultsSidebar";
import DecomposeClipSidebar from "./DecomposeClipSidebar";
import FindHighlightsSidebar from "./FindHighlightsSidebar";
import FindHighlightsResultsSidebar from "./FindHighlightsResultsSidebar";
import DecomposeClipActivityCard from "./DecomposeClipActivityCard";
import ProjectSettingsModal from "./ProjectSettingsModal";
import EditForClarityCleanupDialog from "./EditForClarityCleanupDialog";
import { buildEditedTranscriptBlocksForChapters } from "./ai/chaptersTranscript";
import { applyAutomaticMulticam } from "./ai/automaticMulticam";
import {
    buildEditedTranscriptWordsForCreateClips,
    buildEditedTranscriptUnitsForCreateClips,
    materializeClipCandidatesToCompositions,
    type TranscriptTimingWord,
    type TranscriptUnit,
} from "./ai/createClips";
import { buildDetachedExportSeed, type DetachedExportSeed } from "./decompose/buildDetachedExportSeed";
import { detectRetakes, applyRetakeDetections } from "./ai/retakes";
import {
    createInitialEditForClarityCleanupProgress,
    runEditForClarityCleanupStub,
    type EditForClarityCleanupProgress,
    type EditForClarityCleanupSettings,
} from "./ai/editForClarityCleanup";
import type { EditForClarityIntensity } from "./ai/editForClarity";
import {
    DisplayToClipSizeId,
    GetLibrettoPlanDetails,
    IsVideoTrackType,
    LibrettoPlan,
    LibrettoStudioTheme,
    LibrettoRecordingMode,
    TypeOfTrack
} from "../../utils/utils";
import { fitToCanvas, maybeLockCanvasToFirstVideo } from "../../utils/canvasSizing";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import HomeIcon from "@mui/icons-material/Home";
import SettingsIcon from "@mui/icons-material/Settings";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {Alert, Button, CircularProgress, LinearProgress, Menu, MenuItem, Snackbar, TextField, Typography} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {CheckmarkIcon} from "../NewStudio/SettingsIcon";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {CompositionService} from "../../librettoEditor/services/CompositionService";
import { CompositionPersistenceService } from "../../librettoEditor/services/CompositionPersistenceService";
import { LayoutService } from "../../librettoEditor/services/LayoutService";
import CompositionBridge from "../../librettoEditor/CompositionBridge";
import CompositionCanvasSection from "./CompositionCanvasSection";
import {CompositionStateStore} from "../../librettoEditor/store/CompositionStateStore";
import {CompositionSessionService} from "../../librettoEditor/services/CompositionSessionService";
import {AspectRatio, Crop169, CropPortrait, CropSquare} from "@mui/icons-material";
import AspectRatioSelectCard from "../NewEditor/ClipSizeCard";
import ContextMenu from "../../librettoEditor/components/ContextMenu/ContextMenu";
import OfflineBanner from "../../components/OfflineBanner";
import {ProjectAssetsStore} from "../../state/ProjectAssetsStore";
import { FocusStore } from "../../state/FocusStore";
import { LayoutPacksStore } from "../../state/LayoutPacksStore";
import {AuthContext} from "../../context/AuthContext";
import {FetchContext} from "../../context/FetchContext";
import {DialogContent} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import {SequenceEditor} from "../../sequenceEditor/SequenceEditor";
import {MbEncoder} from "../../engine/encoders/mediabunny.encoder";
import {MbAudioEncoder} from "../../engine/encoders/audio.encoder";
import {V2TextEditor} from "./V2TextEditor";
import {SceneBar, SceneItem, SceneMarkerItem} from "./SceneBar";
import {observer} from "mobx-react-lite";
import {ScriptLayerStore} from "../../librettoEditor/store/ScriptLayerStore";
import {useAssetStore} from "../../storage/AssetStore";
import V2EditorLayoutToggle from "./components/V2EditorLayoutToggle";
import CompositionPresence from "./components/CompositionPresence";
import ZoomImportDialog from "./components/ZoomImportDialog";
import {useEditorAssistant} from "./hooks/useEditorAssistant";
import {buildEditorAssistantContext} from "./hooks/editorAssistantContext";
import {useAssistantDiscovery} from "./hooks/useAssistantDiscovery";
import CreateCompositionView from "./CreateCompositionView";
import {ApplicationStore} from "../../librettoEditor/store/ApplicationStore";
import {MediaClip} from "../../engine/clips";
import CanvasClipToolbar from "./components/CanvasClipToolbar";
import CaptionsCanvasToolbar from "./components/CaptionsCanvasToolbar";
import TextCanvasToolbar from "./components/TextCanvasToolbar";
import TextClipEditorOverlay from "./components/TextClipEditorOverlay";
import CanvasSceneScopeToggle from "./components/CanvasSceneScopeToggle";
import LocalStudio from "../LocalStudio/LocalStudio";
import { FillerWordStore, FillerWordToolbar, FillerWordSidebar } from "./FillerWords";
import { GapStore, GapToolbar, GapSidebar } from "./Gaps";
import ExportCard from "./ExportCard";
import ExportProgressCard from "./ExportProgressCard";
import { ExportStore, ExportType, QualityLevel, VIDEO_QUALITY_PRESETS } from "../../librettoEditor/store/ExportStore";
import { DEFAULT_TRANSCRIPT_EXPORT_OPTIONS, type TranscriptExportOptions, type TranscriptExporter } from "./transcriptExport";
import {
    DEFAULT_AUDIO_EXPORT_SETTINGS,
    normalizeAudioExportFormat,
    normalizeAudioExportSampleRate,
    type AudioExportSettings,
} from "./exportAudioOptions";
import { CreateScriptClipForComposition } from "./utils";
import type { ScriptLayerInputConfig } from "../../engine/clips/script/script.interfaces";
import { Input, ALL_FORMATS, UrlSource } from "mediabunny";
import { UndoManager } from "../../librettoEditor/services/UndoManager";
import { trackSessionEvent } from "../../analytics/sessionEvents";
import { createExportLog, updateExportLog } from "../../analytics/exportLogs";
import { ContextMenuStore } from "../../librettoEditor/store/ContextMenuStore";
import { createDetachedExportComposition } from "../../librettoEditor/services/DetachedExportCompositionService";
import { doc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../../Firebase";
import { useTheme } from "@mui/material/styles";
import {
    kProjectsCollectionName,
    kSequencesCollectionName,
    kSequenceLayerClipsSubcollectionName,
    kSequenceLayersSubcollectionName,
} from "../../utils/CollectionConstants";
import {
    buildCompositionFolderName,
    COMPOSITION_FOLDER_TYPE,
} from "../../utils/folders";
import { populateSequenceFromTracks } from "./sequenceAutoCreate";
import { GetTracksLocalAvailability, GetTracksUploadSnapshots, type TrackUploadSnapshot } from "../../storage/LocalAssetStore";
import BackgroundActivityCard, { type BackgroundActivityRow } from "./BackgroundActivityCard";
import EditorActivationRunner from "./activation/EditorActivationRunner";
import type { ActivationPreStepAction } from "./activation/types";
import { useActivationProgressReporter } from "./activation/useActivationProgressReporter";
import { useDecomposeClipWorkflow } from "./decompose/useDecomposeClipWorkflow";
import SidebarSpotlightOverlay from "./components/SidebarSpotlightOverlay";
import EditForClarityToolSidebar from "./components/EditForClarityToolSidebar";
import RetakesToolSidebar from "./components/RetakesToolSidebar";
import { useDismissPendingAddMediaFlow } from "./hooks/useDismissPendingAddMediaFlow";
import { useAutoCompositionTitle } from "./hooks/useAutoCompositionTitle";
import { useCreateSocialClipsAutostart } from "./hooks/useCreateSocialClipsAutostart";
import { useDeleteCompositionsPreservingProject } from "./hooks/useDeleteCompositionsPreservingProject";
import { useEditForClarityReviewWorkflow } from "./hooks/useEditForClarityReviewWorkflow";
import { useFindHighlightsWorkflow } from "./hooks/useFindHighlightsWorkflow";
import { useMultiSpeakerAutocreateWorkflow } from "./hooks/useMultiSpeakerAutocreateWorkflow";
import { useWorkflowGuidance } from "./hooks/useWorkflowGuidance";
import { useRetakesReviewWorkflow } from "./hooks/useRetakesReviewWorkflow";
import { insertTrackIntoScriptLayer } from "./scriptInsertion/insertTrackIntoScriptLayer";
import { insertTrackIntoScriptScene } from "./scriptInsertion/insertTrackIntoScriptScene";
import {
    RECORD_INTO_TRANSCRIPT_REQUEST_EVENT,
    resolveRecordIntoTranscriptCompositionId,
    type RecordIntoTranscriptRequestDetail,
} from "./V2TextEditor/recordIntoTranscriptRequest";
import WorkflowGuidanceCard from "./workflowGuidance/WorkflowGuidanceCard";
import type { WorkflowGuidanceSession, WorkflowGuidanceType } from "./workflowGuidance/types";
import { beginScriptAddMediaFlow } from "../../librettoEditor/utils/scriptAddMediaFlow";
import { isScriptSceneEmpty } from "../../librettoEditor/utils/scriptSceneUtils";
import { DUPLICATE_RANGE_TO_NEW_COMPOSITION_EVENT } from "../../librettoEditor/utils/duplicateRangeToNewCompositionEvent";
import { getCompatibleScriptChunkClipboard, pasteScriptChunkIntoScene } from "./utils/scriptChunkScenePaste";
import {
    ContributionCapture,
    contributionCaptureAuthorityKey,
    resolveContributionCaptureConfig,
} from "../../instrumentation/contribution";

export const V2ExportButton = ({onClick, disabled}) => {
    const buttonStyle = {
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        height: "30px",
        width: "70px",
        padding: "0.75rem 1rem",
        border: "1px solid var(--app-border-subtle)",
        borderRadius: "10px",
        backgroundColor: "var(--app-bg-surface)",
        cursor: "pointer",
        transition: "border 0.2s ease, background-color 0.2s ease",
        fontSize: "0.95rem",
        background: !disabled ? 'var(--app-text-primary)' : 'var(--app-bg-subtle)',
        opacity: !disabled ? 1 : 0.6,
        "&:hover": {
            border: !disabled ? "1px solid var(--app-border-muted)" : "1px solid var(--app-border-subtle)",
            backgroundColor: !disabled ? "var(--app-text-primary)" : 'var(--app-bg-subtle)',
            opacity: !disabled ? 0.9 : 0.6,
        },
        justifyContent: "center",
    };

    return (
        <Box data-tour="editor-export-button" sx={buttonStyle} onClick={onClick}>
            <Box sx={{color: !disabled ? "var(--app-bg-surface)" : "var(--app-text-subtle)", fontSize: "0.8rem"}}>Export</Box>
        </Box>
    );
};

export const PAGE_MODES = {
    EDITOR: 'editor',
    RECORDING_STUDIO: 'recording_studio'
}

type StudioAutocreatePhase = "waiting" | "creating" | "error";

type StudioAutocreateSession = {
    sessionKey: string;
    assetId: string;
    originCompositionId: string;
    phase: StudioAutocreatePhase;
    errorMessage?: string | null;
};

type StudioAutocreateTrackStatus = {
    id: string;
    name: string;
    statusRaw: string;
    statusLabel: "Ready" | "Processing" | "Failed";
    isReady: boolean;
    isFailed: boolean;
    track: any;
};

type EmbeddedStudioStatus = {
    recordingActive: boolean;
    recordingInProgress: boolean;
    recordingStarting: boolean;
    recordingPaused: boolean;
    countdown: number | null;
    busy: boolean;
    isStopping: boolean;
    isDiscarding: boolean;
    canRequestClose: boolean;
};

type EmbeddedLocalStudioHandle = {
    requestClose: () => Promise<boolean>;
    discardAndLeave: () => Promise<boolean>;
    getStatus: () => EmbeddedStudioStatus;
};

type RecordIntoTranscriptPhase = "recording" | "preparing" | "error";

type RecordIntoTranscriptSession = {
    request: RecordIntoTranscriptRequestDetail;
    originProjectId: string;
    originCompositionId: string;
    originScriptClipId: string;
    originCompositionSessionVersion: number;
    phase: RecordIntoTranscriptPhase;
    assetId?: string;
    filetype?: "audio" | "video";
    preparingStartedAtMs?: number;
    errorMessage?: string;
    canRetry?: boolean;
};

type RecordIntoTranscriptNotice = {
    severity: "success" | "error";
    message: string;
};

const RECORDING_TRACK_READY_TIMEOUT_MS = 30_000;
const RECORDING_TRACK_RETRY_DELAY_MS = 500;

type ExportRequestArgs = {
    tab: ExportType;
    filename: string;
    resolution?: { width: number; height: number };
    quality?: QualityLevel;
    audioSettings?: AudioExportSettings;
    transcriptOptions?: TranscriptExportOptions;
};

type EditForClarityRunSettings = {
    intensity: EditForClarityIntensity;
};

const STUDIO_AUTOCREATE_TRACK_TYPES = new Set<number>([
    TypeOfTrack.LocalAudioRecording,
    TypeOfTrack.LocalVideoRecording,
    TypeOfTrack.SoundboardLocalRecording,
]);

const normalizeTrackStatus = (value: unknown): string => String(value ?? "").trim().toLowerCase();

const isTrackStatusReady = (track: any): boolean => normalizeTrackStatus(track?.status) === "ready";

const isTranscriptionInProgress = (value: unknown): boolean => {
    const state = String(value ?? "").trim().toLowerCase();
    return state === "queued" || state === "processing" || state === "client_extracting" || state === "pending";
};

const isTranscriptionCompleted = (value: unknown): boolean =>
    normalizeTrackStatus(value) === "completed";

const isTranscriptionTerminalWithoutTranscript = (value: unknown): boolean => {
    const state = normalizeTrackStatus(value);
    return state === "failed" || state === "cancelled" || state === "silent" || state === "not_needed";
};

const isStudioAutocreateTrack = (track: any): boolean => {
    if (!track) return false;
    const trackType = Number(track.trackType);
    return STUDIO_AUTOCREATE_TRACK_TYPES.has(trackType);
};

const isTrackReadyForAutocreate = (track: any): boolean =>
    isTrackStatusReady(track);

const isTrackUsableForMediaInsertion = (track: any, localAvailability: Map<string, boolean>): boolean => {
    const status = normalizeTrackStatus(track?.status);
    if (status === "ready") return true;

    const trackId = String(track?.trackId || track?.id || "").trim();
    if (!trackId) return false;

    return localAvailability.get(trackId) === true;
};

const isTrackFailedForAutocreate = (track: any): boolean => {
    const status = normalizeTrackStatus(track?.status);
    if (!status) return false;
    return status.includes("fail") || status.includes("error");
};

const getCurrentCompositionLocalStorageKey = (args: { userId: string; projectId: string }): string =>
    `libretto:v2:currentCompositionId:${args.userId}:${args.projectId}`;

const safeLocalStorageGetItem = (key: string): string | null => {
    if (typeof window === "undefined") return null;
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
};

const safeLocalStorageSetItem = (key: string, value: string) => {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(key, value);
    } catch {}
};

const V2EditorBackButton = ({handleClick, disabled}) => {
    const color = "var(--app-icon-primary)";
    return (
        <Tooltip title="Back to projects">
            <IconButton onClick={handleClick} disabled={disabled} sx={{
                color: color,
                fontSize: '1.2rem',
                '& .MuiSvgIcon-root': {fontSize: '1.4rem'}
            }}>
                <HomeIcon/>
            </IconButton>
        </Tooltip>
    )
}

const V2EditorSettingsButton = ({
    handleClick,
    disabled,
}: {
    handleClick: () => void;
    disabled?: boolean;
}) => {
    const subtleIconStyle = {
        fontSize: "18px",
        color: "var(--app-icon-primary)",
    };
    const color = "var(--app-icon-primary)";
    return (
        <Tooltip title="Settings">
            <IconButton onClick={handleClick} disabled={disabled} sx={{
                color: color,
                fontSize: '1.2rem',
                '& .MuiSvgIcon-root': {fontSize: '1.4rem'}
            }}>
                <SettingsIcon style={subtleIconStyle}/>
            </IconButton>
        </Tooltip>
    );
};

// Project name and composition selector for topbar
const ProjectCompositionSelector = observer(({
    projectName,
    compositions,
    selectedCompositionId,
    selectedCompositionTitle,
    onCompositionSelect,
    onCreateComposition,
    onRenameProject,
    onRenameComposition,
    readOnly,
}: {
    projectName: string;
    compositions: any[];
    selectedCompositionId: string | null;
    selectedCompositionTitle: string | null;
    onCompositionSelect: (compId: string, compTitle: string) => void;
    onCreateComposition: () => Promise<void>;
    onRenameProject: (newName: string) => Promise<void>;
    onRenameComposition: (compositionId: string, newName: string) => Promise<void>;
    readOnly?: boolean;
}) => {
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [isEditingProjectName, setIsEditingProjectName] = useState(false);
    const [editedProjectName, setEditedProjectName] = useState(projectName);
    const [isEditingCompositionName, setIsEditingCompositionName] = useState(false);
    const [editedCompositionName, setEditedCompositionName] = useState(selectedCompositionTitle || "");
    
    const menuOpen = Boolean(menuAnchor);

    useEffect(() => {
        if (!isEditingCompositionName) {
            setEditedCompositionName(selectedCompositionTitle || "");
        }
    }, [selectedCompositionTitle, isEditingCompositionName]);

    const handleCompositionClick = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleSelectComposition = (comp: any) => {
        onCompositionSelect(comp.id, comp.title || comp.name || 'Untitled');
        handleMenuClose();
    };

    const handleNewComposition = async () => {
        handleMenuClose();
        await onCreateComposition();
    };

    const handleProjectNameClick = () => {
        setEditedProjectName(projectName);
        setIsEditingProjectName(true);
    };

    const handleProjectNameBlur = async () => {
        const trimmedName = editedProjectName.trim();
        if (trimmedName && trimmedName !== projectName) {
            await onRenameProject(trimmedName);
        }
        setIsEditingProjectName(false);
    };

    const handleProjectNameKeyDown = async (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            await handleProjectNameBlur();
        } else if (e.key === 'Escape') {
            setIsEditingProjectName(false);
            setEditedProjectName(projectName);
        }
    };

    const handleCompositionNameClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (readOnly) return;
        setEditedCompositionName(selectedCompositionTitle || "Untitled");
        setIsEditingCompositionName(true);
    };

    const handleCompositionNameBlur = async () => {
        const trimmedName = editedCompositionName.trim();
        if (selectedCompositionId && trimmedName && trimmedName !== (selectedCompositionTitle || "")) {
            await onRenameComposition(selectedCompositionId, trimmedName);
        }
        setIsEditingCompositionName(false);
    };

    const handleCompositionNameKeyDown = async (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            await handleCompositionNameBlur();
        } else if (e.key === 'Escape') {
            setIsEditingCompositionName(false);
            setEditedCompositionName(selectedCompositionTitle || "");
        }
    };

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
        }}>
            {/* Project Name */}
            {isEditingProjectName ? (
                <TextField
                    value={editedProjectName}
                    onChange={(e) => setEditedProjectName(e.target.value)}
                    onBlur={handleProjectNameBlur}
                    onKeyDown={handleProjectNameKeyDown}
                    autoFocus
                    size="small"
                    variant="standard"
                    sx={{
                        '& .MuiInput-input': {
                            fontSize: '14px',
                            fontWeight: 500,
                            color: 'var(--app-text-primary)',
                            padding: '2px 4px',
                            minWidth: '100px',
                        },
                        '& .MuiInput-underline:before': {
                            borderBottomColor: 'var(--app-border-subtle)',
                        },
                        '& .MuiInput-underline:after': {
                            borderBottomColor: '#3B82F6',
                        },
                    }}
                />
            ) : (
                <Typography
                    onClick={handleProjectNameClick}
                    sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: 'var(--app-text-muted)',
                        cursor: 'pointer',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        '&:hover': {
                            backgroundColor: 'var(--app-bg-subtle)',
                            color: 'var(--app-text-primary)',
                        },
                    }}
                >
                    {projectName || 'Untitled Project'}
                </Typography>
            )}

            {/* Separator */}
            <Typography sx={{ fontSize: '14px', color: 'var(--app-text-subtle)', userSelect: 'none' }}>/</Typography>

            {/* Composition Selector */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                }}
            >
                {isEditingCompositionName ? (
                    <TextField
                        value={editedCompositionName}
                        onChange={(e) => setEditedCompositionName(e.target.value)}
                        onBlur={handleCompositionNameBlur}
                        onKeyDown={handleCompositionNameKeyDown}
                        autoFocus
                        size="small"
                        variant="standard"
                        sx={{
                            '& .MuiInput-input': {
                                fontSize: '14px',
                                fontWeight: 500,
                                color: 'var(--app-text-primary)',
                                padding: '2px 4px',
                                minWidth: '100px',
                            },
                            '& .MuiInput-underline:before': {
                                borderBottomColor: 'var(--app-border-subtle)',
                            },
                            '& .MuiInput-underline:after': {
                                borderBottomColor: '#3B82F6',
                            },
                        }}
                    />
                ) : (
                    <Box
                        onClick={handleCompositionNameClick}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            cursor: readOnly ? 'default' : 'pointer',
                            '&:hover': {
                                backgroundColor: 'var(--app-bg-subtle)',
                            },
                        }}
                    >
                        <Typography sx={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: 'var(--app-text-primary)',
                        }}>
                            {selectedCompositionTitle || 'Untitled'}
                        </Typography>
                    </Box>
                )}
                <IconButton
                    data-tour="composition-selector-button"
                    size="small"
                    onClick={handleCompositionClick}
                    sx={{
                        color: 'var(--app-text-muted)',
                        padding: '4px',
                        borderRadius: '6px',
                        '&:hover': { backgroundColor: 'var(--app-bg-subtle)' },
                    }}
                >
                    <KeyboardArrowDownIcon sx={{ fontSize: '18px' }} />
                </IconButton>
            </Box>

            {/* Compositions Dropdown Menu */}
            <Menu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        minWidth: 200,
                        borderRadius: '12px',
                        boxShadow: '0px 20px 60px rgba(15, 23, 42, 0.12)',
                        border: '1px solid var(--app-border-subtle)',
                    }
                }}
            >
                <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid var(--app-border-subtle)' }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'var(--app-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Compositions
                    </Typography>
                </Box>
                
                {compositions.map((comp: any) => (
                    <MenuItem
                        key={comp.id}
                        onClick={() => handleSelectComposition(comp)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 1,
                            px: 1.5,
                            '&:hover': {
                                backgroundColor: 'var(--app-bg-subtle)',
                            },
                        }}
                    >
                        <Box sx={{ width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {selectedCompositionId === comp.id && <CheckmarkIcon />}
                        </Box>
                        <Typography sx={{ fontSize: '14px', fontWeight: selectedCompositionId === comp.id ? 500 : 400 }}>
                            {comp.title || comp.name || 'Untitled'}
                        </Typography>
                    </MenuItem>
                ))}
                
                <Box sx={{ borderTop: '1px solid var(--app-border-subtle)', mt: 0.5, pt: 0.5 }}>
                    <MenuItem
                        data-tour="composition-selector-new-composition"
                        onClick={handleNewComposition}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 1,
                            px: 1.5,
                            '&:hover': {
                                backgroundColor: 'rgba(59, 130, 246, 0.08)',
                            },
                        }}
                    >
                        <AddIcon sx={{ fontSize: 18, color: '#3B82F6' }} />
                        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#3B82F6' }}>
                            New composition
                        </Typography>
                    </MenuItem>
                </Box>
            </Menu>
        </Box>
    );
})

const V2Editor = observer(() => {
    const muiTheme = useTheme();
    const isDarkMode = muiTheme.palette.mode === "dark";
    const [sidebarItemSelected, setSidebarItemSelected] = useState<string | null>(null);
    const [highlightProjectFilesPanel, setHighlightProjectFilesPanel] = useState<boolean>(false);
    const highlightProjectFilesTimerRef = useRef<number | null>(null);
    const [showClipSizeCard, setShowClipSizeCard] = useState(false);
    const [canUndo, setCanUndo] = useState<boolean>(UndoManager.canUndo);
    const [canRedo, setCanRedo] = useState<boolean>(UndoManager.canRedo);

    const [sequenceEditorOpen, setSequenceEditorOpen] = useState(false);
    const [sequenceId, setSequenceId] = useState<string | null>(null);
    const [exportOpen, setExportOpen] = useState(false);
    const [isStartingTrialForExport, setIsStartingTrialForExport] = useState(false);
    const [trialStartedToastOpen, setTrialStartedToastOpen] = useState(false);
    const [trialStartErrorToast, setTrialStartErrorToast] = useState<string | null>(null);
    const [trialStartedForSession, setTrialStartedForSession] = useState(false);
    const [exportCardPosition, setExportCardPosition] = useState<{top: number; left: number}>({ top: 52, left: 8 });
    const transcriptExporterRef = useRef<TranscriptExporter | null>(null);
    const exportButtonRef = useRef<HTMLDivElement | null>(null);

    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const [editorLayoutConfiguration, setEditorLayoutConfiguration] = useState<"split" | "transcript" | "canvas">("split");
    const [aiToolsView, setAiToolsView] = useState<
        "list" | "add_chapters" | "automatic_multicam" | "remove_retakes" | "edit_for_clarity" | "create_clips" | "create_clips_results" | "find_highlights" | "find_highlights_results" | "decompose_clip"
    >("list");
    const [isGeneratingChapters, setIsGeneratingChapters] = useState(false);
    const [generateChaptersError, setGenerateChaptersError] = useState<string | null>(null);
    const [isCreatingClips, setIsCreatingClips] = useState(false);
    const [createClipsDraft, setCreateClipsDraft] = useState<CreateClipResult[]>([]);
    const [selectedCreateClipId, setSelectedCreateClipId] = useState<string | null>(null);
    const [createClipsSourceCompositionId, setCreateClipsSourceCompositionId] = useState<string | null>(null);
    const [createClipsSourceCompositionTitle, setCreateClipsSourceCompositionTitle] = useState<string | null>(null);
    const [createClipsFolderId, setCreateClipsFolderId] = useState<string | null>(null);
    const createClipsRunSeqRef = useRef(0);
    const createClipsSourceTranscriptUnitsRef = useRef<TranscriptUnit[] | null>(null);
    const createClipsSourceTranscriptWordsRef = useRef<TranscriptTimingWord[] | null>(null);
    const createClipsSourceLiveSeedRef = useRef<DetachedExportSeed | null>(null);
    const [isApplyingMulticam, setIsApplyingMulticam] = useState(false);
    const [applyMulticamError, setApplyMulticamError] = useState<string | null>(null);
    const [editForClarityCleanupProgress, setEditForClarityCleanupProgress] = useState<EditForClarityCleanupProgress | null>(null);
    const [isEditForClarityCleanupDialogOpen, setIsEditForClarityCleanupDialogOpen] = useState(false);
    const [editForClarityCleanupStopRequested, setEditForClarityCleanupStopRequested] = useState(false);
    const editForClarityCleanupStopRequestedRef = useRef(false);
    const [aiChaptersDraft, setAiChaptersDraft] = useState<
        Array<{
            title: string;
            timeSec: number;
            snippet: string;
            afterWordPayload: any;
            beforeWordPayload?: any;
        }>
    >([]);
    
    // State for pending recording assets to auto-insert into script layer
    const [pendingRecordingAssetIds, setPendingRecordingAssetIds] = useState<Array<{
        assetId: string;
        filetype: string;
        editForClaritySettings?: EditForClarityRunSettings;
        openPropertiesSidebarOnInsert?: boolean;
        autoEnableStudioSoundOnInsert?: boolean;
        openCaptionsSidebarOnInsert?: boolean;
        workflowGuidanceType?: WorkflowGuidanceType;
    }>>([]);
    const pendingRecordingInFlightRef = useRef<Set<string>>(new Set());
    const [recordIntoTranscriptSession, setRecordIntoTranscriptSession] = useState<RecordIntoTranscriptSession | null>(null);
    const recordIntoTranscriptSessionRef = useRef<RecordIntoTranscriptSession | null>(null);
    const recordIntoTranscriptStudioRef = useRef<EmbeddedLocalStudioHandle | null>(null);
    const recordIntoTranscriptInsertionInFlightRef = useRef<Set<string>>(new Set());
    const recordIntoTranscriptCompletionHandoffRef = useRef(false);
    const recordIntoTranscriptRetryTimerRef = useRef<number | null>(null);
    const [recordIntoTranscriptRetryRevision, setRecordIntoTranscriptRetryRevision] = useState(0);
    const [recordIntoTranscriptStudioStatus, setRecordIntoTranscriptStudioStatus] = useState<EmbeddedStudioStatus | null>(null);
    const [recordIntoTranscriptDiscardConfirmOpen, setRecordIntoTranscriptDiscardConfirmOpen] = useState(false);
    const [recordIntoTranscriptNotice, setRecordIntoTranscriptNotice] = useState<RecordIntoTranscriptNotice | null>(null);
    const [pendingEditForClarityAutoRuns, setPendingEditForClarityAutoRuns] = useState<Array<{
        assetId: string;
        trackId: string;
        intensity: EditForClarityIntensity;
    }>>([]);
    const pendingEditForClarityAutoRunInFlightRef = useRef<Set<string>>(new Set());
    const [localTrackAvailability, setLocalTrackAvailability] = useState<Map<string, boolean>>(new Map());
    const [localTrackUploadSnapshots, setLocalTrackUploadSnapshots] = useState<Map<string, TrackUploadSnapshot>>(new Map());
    const [showBackgroundActivityCard, setShowBackgroundActivityCard] = useState(false);
    const [isProjectSettingsOpen, setIsProjectSettingsOpen] = useState(false);
    const [zoomImportDialogOpen, setZoomImportDialogOpen] = useState(false);
    const [zoomImportNotice, setZoomImportNotice] = useState<string | null>(null);
    const [activationRunId, setActivationRunId] = useState<string | null>(null);
    const [workflowGuidanceSession, setWorkflowGuidanceSession] = useState<WorkflowGuidanceSession | null>(null);

    // Screen recording sessions produce up to two assets (screen + secondary webcam/mic)
    const [pendingScreenRecordingSessions, setPendingScreenRecordingSessions] = useState<Array<{screenAssetId: string; secondaryAssetId?: string | null}>>([]);
    const pendingScreenRecordingInFlightRef = useRef<Set<string>>(new Set());
    const insertedScreenRecordingSessionsRef = useRef<Set<string>>(new Set());
    const screenRecordingSequenceStateRef = useRef<Map<string, {
        sequenceId: string;
        screenLayerId: string;
        secondaryLayerId?: string | null;
        screenClipId: string;
        secondaryClipId?: string | null;
        populated: boolean;
    }>>(new Map());
    const [studioAutocreateSession, setStudioAutocreateSession] = useState<StudioAutocreateSession | null>(null);
    const [studioAutocreatePreferredCompositionId, setStudioAutocreatePreferredCompositionId] = useState<string | null>(null);
    const studioAutocreateInFlightRef = useRef<Set<string>>(new Set());
		    const [selectedCompositionId, setSelectedCompositionId] = useState<string | null>(null);
	    const [selectedCompositionTitle, setSelectedCompositionTitle] = useState<string | null>(null);
	    const [isSelectedCompositionEmpty, setIsSelectedCompositionEmpty] = useState<boolean>(true);
	    const [isRestoringComposition, setIsRestoringComposition] = useState<boolean>(false);
	    const [restoredCompositionId, setRestoredCompositionId] = useState<string | null>(null);
	    const compositionLoadSeqRef = useRef(0);
		    const selectedCompositionRecord = (ProjectAssetsStore.projectCompositions || []).find(
		        (c: any) => c?.id === selectedCompositionId
		    );
		    const selectedCompositionEmptyFlag = Boolean((selectedCompositionRecord as any)?.isEmpty);

		    const navigate = useNavigate();
		    const {projectId} = useParams();
		    const [searchParams, setSearchParams] = useSearchParams();
		    const lastTrackedEditorProjectIdRef = useRef<string | null>(null);
    
			    const { CreateComposition, CreateSequence, RenameProject, RenameComposition, DuplicateComposition, ResolveAssetUrl, GetEnhancedAudioUrl, GetTrackUrl, GetFocusChunk, UploadAsset, CreateSpeaker, DeleteComposition, DeleteCompositionsBatch, PostAiCreateClips, CreateFolder, AddCompositionsToFolder, RemoveCompositionsFromFolder } = useAssetStore();
    const {
        isFindingHighlights,
        findHighlightsDraft,
        selectedFindHighlightId,
        transcriptHighlights,
        hasFindHighlightsDraft,
        handleFindHighlightsSubmit,
        handleSelectFindHighlight,
        resetFindHighlightsReview,
    } = useFindHighlightsWorkflow({
        selectedCompositionId,
        postAiCreateClips: PostAiCreateClips,
        onShowResults: () => setAiToolsView("find_highlights_results"),
    });

    const pendingDuplicateRangeRef = useRef<{
        newCompositionId: string;
        rangeStartSec: number;
        rangeEndSec: number;
    } | null>(null);

    const handleDuplicateRangeToNewComposition = useCallback(
        async (rangeStartSec: number, rangeEndSec: number) => {
            if (!selectedCompositionId) return;

            const start = Math.min(Number(rangeStartSec ?? 0), Number(rangeEndSec ?? 0));
            const end = Math.max(Number(rangeStartSec ?? 0), Number(rangeEndSec ?? 0));
            if (!Number.isFinite(start) || !Number.isFinite(end) || end - start <= 1e-3) return;

            const baseTitle =
                String(selectedCompositionTitle ?? selectedCompositionRecord?.title ?? selectedCompositionRecord?.name ?? "Untitled").trim() ||
                "Untitled";
            const nextTitle = `Clip of ${baseTitle}`;

            try {
                const newId = await DuplicateComposition({
                    compositionId: selectedCompositionId,
                    titleOverride: nextTitle,
                });

                if (!newId) return;
	                pendingDuplicateRangeRef.current = {
	                    newCompositionId: newId,
	                    rangeStartSec: start,
	                    rangeEndSec: end,
	                };

	                // If we're in a studio-autocreate flow, respect the explicit user action to open a new
	                // composition instead of being forced back to the origin composition.
	                setStudioAutocreatePreferredCompositionId(null);

	                setSelectedCompositionId(newId);
	                setSelectedCompositionTitle(nextTitle);
	            } catch (e) {
	                console.error("[V2Editor] Failed to duplicate composition range", e);
	            }
        },
        [DuplicateComposition, selectedCompositionId, selectedCompositionRecord, selectedCompositionTitle]
    );

	    // High-level usage signal (session-level aggregation).
	    useEffect(() => {
	        if (!projectId) return;
	        if (lastTrackedEditorProjectIdRef.current === projectId) return;
	        lastTrackedEditorProjectIdRef.current = projectId;
	        trackSessionEvent("editor_opened");
	        void (async () => {
	            try {
	                const pid = String(projectId ?? "").trim();
	                if (!pid) return;
	                const now = Math.floor(Date.now() / 1000);
	                await updateDoc(doc(db, kProjectsCollectionName, pid), { lastViewed: now });
	            } catch (e) {
	                console.warn("[V2Editor] Failed to update project lastViewed", e);
	            }
	        })();
	    }, [projectId]);

    // Determine initial page mode from URL param: ?mode=recording&type=video
    const initialMode = searchParams.get('mode') === 'recording' ? PAGE_MODES.RECORDING_STUDIO : PAGE_MODES.EDITOR;
    const initialRecordingTypeParam = searchParams.get('type');
    const initialRecordingType = initialRecordingTypeParam === 'audio' ? LibrettoRecordingMode.Audio : LibrettoRecordingMode.Video;
    const initialIsScreenRecordingMode = initialRecordingTypeParam === 'screen';

    const [pageMode, setPageMode] = useState<string>(initialMode);
	    const [recordingMode, setRecordingMode] = useState<number>(initialRecordingType);
	    const [isScreenRecordingMode, setIsScreenRecordingMode] = useState<boolean>(initialIsScreenRecordingMode);

    const exportPreviewThumbnailUrl = useMemo(() => {
        const scenes = Array.isArray(ScriptLayerStore.scenes) ? ScriptLayerStore.scenes : [];
        const firstScene = scenes[0];
        return String(firstScene?.thumbnailUrl ?? "").trim();
    }, [ScriptLayerStore.scenesVersion]);

    const startCompositionSwitch = useCallback((args: {
        compositionId: string;
        compositionTitle?: string | null;
    }) => {
        const nextCompositionId = String(args?.compositionId || "").trim();
        if (!nextCompositionId) return;
        if (selectedCompositionId === nextCompositionId) {
            if (args.compositionTitle !== undefined && selectedCompositionTitle !== args.compositionTitle) {
                setSelectedCompositionTitle(args.compositionTitle ?? null);
            }
            return;
        }

        setIsRestoringComposition(true);
        if (args.compositionTitle !== undefined && selectedCompositionTitle !== args.compositionTitle) {
            setSelectedCompositionTitle(args.compositionTitle ?? null);
        }
        setSelectedCompositionId(nextCompositionId);
    }, [selectedCompositionId, selectedCompositionTitle]);
    const deleteCompositionsPreservingProject = useDeleteCompositionsPreservingProject({
        projectId,
        selectedCompositionId,
        deleteCompositionsBatch: DeleteCompositionsBatch,
        startCompositionSwitch,
        setIsRestoringComposition,
    });

    const ensureCreateClipsCompositionFolder = useCallback(async (args: {
        sourceCompositionId: string;
        sourceCompositionTitle?: string | null;
        createdClips: CreateClipResult[];
    }) => {
        const normalizedProjectId =
            String(
                ProjectAssetsStore.projectId ||
                selectedCompositionRecord?.projectId ||
                ""
            ).trim();
        const normalizedSourceCompositionId = String(args.sourceCompositionId || "").trim();
        const compositionIds = args.createdClips
            .map((clip) => String(clip?.compositionId ?? clip?.id ?? "").trim())
            .filter(Boolean);
        if (!normalizedProjectId || !normalizedSourceCompositionId || compositionIds.length === 0) return;

        try {
            const folderId = await CreateFolder({
                projectId: normalizedProjectId,
                name: buildCompositionFolderName(args.sourceCompositionTitle),
                folderType: COMPOSITION_FOLDER_TYPE,
                sourceCompositionId: normalizedSourceCompositionId,
            });
            await AddCompositionsToFolder({
                folderId,
                compositionIds,
            });
            setCreateClipsFolderId(folderId);
        } catch (error) {
            console.error("[V2Editor] Failed to update create-clips composition group", error);
        }
    }, [AddCompositionsToFolder, CreateFolder, selectedCompositionRecord]);

    const resetCreateClipsReview = useCallback(() => {
        createClipsRunSeqRef.current += 1;
        setIsCreatingClips(false);
        setCreateClipsDraft([]);
        setSelectedCreateClipId(null);
        setCreateClipsFolderId(null);
        setCreateClipsSourceCompositionId(null);
        setCreateClipsSourceCompositionTitle(null);
        createClipsSourceTranscriptUnitsRef.current = null;
        createClipsSourceTranscriptWordsRef.current = null;
        createClipsSourceLiveSeedRef.current = null;
    }, []);

    const deleteCreateClipsDraftCompositions = useCallback(async (
        draft: CreateClipResult[],
        opts?: {
            restoreSourceComposition?: boolean;
        },
    ) => {
        const compositionIds = draft
            .map((clip) => String(clip?.compositionId ?? clip?.id ?? "").trim())
            .filter(Boolean);
        if (compositionIds.length === 0) return;

        const folderIdForDraft = String(createClipsFolderId || "").trim();
        await deleteCompositionsPreservingProject({
            compositionIds,
            preferredFallbackCompositionId:
                opts?.restoreSourceComposition && createClipsSourceCompositionId
                    ? createClipsSourceCompositionId
                    : undefined,
            onBeforeDelete: async () => {
                if (!folderIdForDraft) return;
                await RemoveCompositionsFromFolder({
                    folderId: folderIdForDraft,
                    compositionIds,
                    deleteIfEmpty: true,
                });
            },
        });
    }, [
        RemoveCompositionsFromFolder,
        createClipsFolderId,
        createClipsSourceCompositionId,
        deleteCompositionsPreservingProject,
    ]);

    const handleCreateClipsSubmit = useCallback(async (settings: CreateClipsSettings) => {
        if (!selectedCompositionId) return;

        const runSeq = createClipsRunSeqRef.current + 1;
        createClipsRunSeqRef.current = runSeq;
        const shouldReuseSourceContext = createClipsDraft.length > 0 && Boolean(createClipsSourceCompositionId);
        const sourceCompositionIdForRun =
            (shouldReuseSourceContext ? createClipsSourceCompositionId : null) ?? selectedCompositionId;
        const sourceCompositionTitleForRun =
            (shouldReuseSourceContext ? createClipsSourceCompositionTitle : null) ??
            selectedCompositionTitle ??
            selectedCompositionRecord?.title ??
            selectedCompositionRecord?.name ??
            null;

        if (!sourceCompositionIdForRun) return;

        if (createClipsDraft.length > 0) {
            await deleteCreateClipsDraftCompositions(createClipsDraft, {
                restoreSourceComposition: true,
            });
        }

        setIsCreatingClips(true);
        setCreateClipsDraft([]);
        setSelectedCreateClipId(null);
        setCreateClipsFolderId(null);
        setCreateClipsSourceCompositionId(sourceCompositionIdForRun);
        setCreateClipsSourceCompositionTitle(sourceCompositionTitleForRun);

        try {
            let transcriptUnits = shouldReuseSourceContext ? createClipsSourceTranscriptUnitsRef.current ?? [] : [];
            let transcriptWords = shouldReuseSourceContext ? createClipsSourceTranscriptWordsRef.current ?? [] : [];
            let liveSeed = shouldReuseSourceContext ? createClipsSourceLiveSeedRef.current : null;

            if (transcriptUnits.length === 0 || transcriptWords.length === 0 || !liveSeed) {
                await CompositionBridge.syncScriptLayerStore();
                transcriptUnits = buildEditedTranscriptUnitsForCreateClips();
                transcriptWords = buildEditedTranscriptWordsForCreateClips();
                liveSeed = buildDetachedExportSeed();
            }
            if (transcriptUnits.length === 0) {
                throw new Error("Transcript content is not ready yet.");
            }
            if (transcriptWords.length === 0) {
                throw new Error("Transcript timing is not ready yet.");
            }
            if (!liveSeed) {
                throw new Error("Composition content is not ready yet.");
            }

            createClipsSourceTranscriptUnitsRef.current = transcriptUnits.map((unit) => ({ ...unit }));
            createClipsSourceTranscriptWordsRef.current = transcriptWords.map((word) => ({ ...word }));
            createClipsSourceLiveSeedRef.current = liveSeed;

            const response = await PostAiCreateClips({
                compositionId: sourceCompositionIdForRun,
                maxClips: settings.clipCount,
                clipDurationSec: settings.clipDurationSec,
                topicPrompt: settings.topic || undefined,
                transcriptUnits,
            });

            if (createClipsRunSeqRef.current !== runSeq) return;

            const candidates = Array.isArray(response?.candidates) ? response.candidates : [];
            if (candidates.length === 0) {
                throw new Error("No clip candidates were returned.");
            }

            const createdClips = await materializeClipCandidatesToCompositions({
                sourceCompositionId: sourceCompositionIdForRun,
                sourceCompositionTitle: sourceCompositionTitleForRun,
                settings,
                candidates,
                transcriptUnits,
                transcriptWords,
                liveSeed,
                duplicateComposition: DuplicateComposition,
                deleteComposition: DeleteComposition,
                resolveAssetUrl: ResolveAssetUrl,
                resolveEnhancedAudioUrl: async (trackId: string) => GetEnhancedAudioUrl({ trackId }),
            });

            if (createClipsRunSeqRef.current !== runSeq) {
                await deleteCreateClipsDraftCompositions(createdClips);
                return;
            }

            await ensureCreateClipsCompositionFolder({
                sourceCompositionId: sourceCompositionIdForRun,
                sourceCompositionTitle: sourceCompositionTitleForRun,
                createdClips,
            });

            setCreateClipsDraft(createdClips);
            setSelectedCreateClipId(createdClips[0]?.id ?? null);
            setAiToolsView("create_clips_results");
        } catch (error) {
            console.error("[V2Editor] Failed to create clips", error);
            const message = String((error as any)?.message ?? "Failed to create clips.").trim();
            if (message) {
                window.alert(message);
            }
        } finally {
            if (createClipsRunSeqRef.current === runSeq) {
                setIsCreatingClips(false);
            }
        }
    }, [
        DeleteComposition,
        DuplicateComposition,
        GetEnhancedAudioUrl,
        PostAiCreateClips,
        ResolveAssetUrl,
        CreateFolder,
        createClipsDraft,
        deleteCreateClipsDraftCompositions,
        createClipsSourceCompositionId,
        createClipsSourceCompositionTitle,
        ensureCreateClipsCompositionFolder,
        selectedCompositionId,
        selectedCompositionRecord,
        selectedCompositionTitle,
    ]);

    useEffect(() => {
        return () => {
            createClipsRunSeqRef.current += 1;
            createClipsSourceTranscriptUnitsRef.current = null;
            createClipsSourceTranscriptWordsRef.current = null;
            createClipsSourceLiveSeedRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (sidebarItemSelected !== SidebarItems.AI_TOOLS && aiToolsView !== "list") {
            resetCreateClipsReview();
            setAiToolsView("list");
        }
    }, [sidebarItemSelected, aiToolsView, resetCreateClipsReview]);

    const handleDiscardAllCreateClips = useCallback(async () => {
        await deleteCreateClipsDraftCompositions(createClipsDraft, {
            restoreSourceComposition: true,
        });
        resetCreateClipsReview();
        setAiToolsView("create_clips");
    }, [createClipsDraft, deleteCreateClipsDraftCompositions, resetCreateClipsReview]);
	    const authContext = useContext(AuthContext);
    const fetchContext = useContext(FetchContext);
    const currentUserId = String(authContext.authState?.userId ?? authContext.getUserId?.() ?? "").trim();
    const {showAssistantDiscovery, markAssistantOpened} = useAssistantDiscovery(currentUserId);
    const getEditorContext = useCallback(() => buildEditorAssistantContext({
        compositionDurationSec: CompositionStateStore.compositionDuration,
        hasVideo: ScriptLayerStore.hasVideo,
        scriptClip: CompositionService.getScriptLayerClip?.(),
        projectTracks: ProjectAssetsStore.projectTracks,
    }), []);
    const {
        messages: assistantMessages,
        sendMessage: sendAssistantMessage,
        isSending: isAssistantSending,
        error: assistantError,
    } = useEditorAssistant({
        authAxios: fetchContext?.authAxios,
        getFreshToken: authContext?.getFreshToken,
        projectId,
        compositionId: selectedCompositionId,
        getEditorContext,
    });
    const currentWorkspaceId = authContext.getCurrentWorkspaceId?.();
    const currentWorkspaceRole = authContext.getWorkspaces?.()
        .find((workspace: any) => workspace?.workspaceId === currentWorkspaceId)?.role;
    const compositionEditingDisabled = Boolean(selectedCompositionId) && currentWorkspaceRole === "viewer";
    const zoomImportEnabled = currentWorkspaceRole !== "viewer";

    const authInstrumentationConfig = authContext.authState?.instrumentationCaptureConfig;
    const hasAuthInstrumentationConfig = Boolean(
        authContext.authState &&
        Object.prototype.hasOwnProperty.call(authContext.authState, "instrumentationCaptureConfig")
    );
    const instrumentationOwnerUid = String(authContext.authState?.userId ?? "").trim() || null;
    const [serverInstrumentationConfig, setServerInstrumentationConfig] = useState<unknown>(undefined);
    const [serverInstrumentationConfigResolved, setServerInstrumentationConfigResolved] = useState(false);
    const [serverInstrumentationConfigOwnerUid, setServerInstrumentationConfigOwnerUid] = useState<string | null>(null);
    const signedInForInstrumentation = Boolean(
        authContext.authReady &&
        authContext.authState?.userId &&
        fetchContext?.authAxios
    );

    useEffect(() => {
        if (hasAuthInstrumentationConfig || !signedInForInstrumentation) {
            setServerInstrumentationConfig(undefined);
            setServerInstrumentationConfigResolved(false);
            setServerInstrumentationConfigOwnerUid(null);
            return;
        }
        let cancelled = false;
        setServerInstrumentationConfig(undefined);
        setServerInstrumentationConfigResolved(false);
        setServerInstrumentationConfigOwnerUid(null);
        const requestedOwnerUid = instrumentationOwnerUid;
        const refreshGrant = () => {
            fetchContext.authAxios.get("/instrumentation/config")
                .then((response: any) => {
                    if (!cancelled) {
                        setServerInstrumentationConfig(response?.data ?? { enabled: false });
                        setServerInstrumentationConfigResolved(true);
                        setServerInstrumentationConfigOwnerUid(requestedOwnerUid);
                    }
                })
                .catch(() => {
                    // Initial failure keeps capture off. A later transient failure
                    // preserves the last server decision until the next poll. An
                    // unresolved request is not evidence of revocation, so it must
                    // never trigger destructive abandonment reconciliation.
                });
        };
        refreshGrant();
        const intervalId = typeof window !== "undefined"
            ? window.setInterval(refreshGrant, 60_000)
            : null;
        return () => {
            cancelled = true;
            if (intervalId != null) window.clearInterval(intervalId);
        };
    }, [
        hasAuthInstrumentationConfig,
        signedInForInstrumentation,
        fetchContext?.authAxios,
        authContext.authState?.userId,
        instrumentationOwnerUid,
    ]);

    const remoteInstrumentationConfig = hasAuthInstrumentationConfig
        ? authInstrumentationConfig
        : serverInstrumentationConfig;
    // While the server grant is loading, an undefined remote value is still
    // authoritative and therefore keeps capture off instead of trusting URL state.
    const hasRemoteInstrumentationConfig = hasAuthInstrumentationConfig || signedInForInstrumentation;
    const instrumentationConfigAuthoritative = hasAuthInstrumentationConfig ||
        !signedInForInstrumentation || (
            serverInstrumentationConfigResolved &&
            serverInstrumentationConfigOwnerUid === instrumentationOwnerUid
        );
    const instrumentationCaptureConfig = useMemo(() => resolveContributionCaptureConfig({
        remoteConfig: remoteInstrumentationConfig,
        hasRemoteConfig: hasRemoteInstrumentationConfig,
        locationSearch: typeof window !== "undefined" ? window.location.search : "",
        storage: typeof window !== "undefined" ? window.localStorage : null,
    }), [remoteInstrumentationConfig, hasRemoteInstrumentationConfig, searchParams]);
    const instrumentationConfigKey = useMemo(
        () => JSON.stringify({ ownerUid: instrumentationOwnerUid, config: instrumentationCaptureConfig }),
        [instrumentationCaptureConfig, instrumentationOwnerUid],
    );
    const instrumentationAuthorityKey = useMemo(
        () => JSON.stringify({
            ownerUid: instrumentationOwnerUid,
            authority: contributionCaptureAuthorityKey(instrumentationCaptureConfig),
        }),
        [instrumentationCaptureConfig, instrumentationOwnerUid],
    );
    const appliedInstrumentationConfigKeyRef = useRef<string | null>(null);
    const appliedInstrumentationAuthorityRef = useRef<{
        key: string;
        ownerUid: string | null;
    } | null>(null);
    const [appliedInstrumentationConfigKey, setAppliedInstrumentationConfigKey] = useState<string | null>(null);

    useEffect(() => {
        if (!instrumentationConfigAuthoritative) {
            ContributionCapture.suspendCapture();
            return;
        }
        let cancelled = false;
        const applyConfiguration = async () => {
            const previousAuthority = appliedInstrumentationAuthorityRef.current;
            if (previousAuthority && previousAuthority.key !== instrumentationAuthorityKey) {
                const reason = !instrumentationCaptureConfig.enabled
                    ? "capture_disabled"
                    : previousAuthority.ownerUid !== instrumentationOwnerUid
                        ? "capture_authority_changed"
                        : "capture_config_replaced";
                // A revoked/replaced authority discards its local payload queue and
                // sends only the owner-bound abandonment control request.
                await ContributionCapture.abandonCaptureAuthority(reason);
            }
            if (cancelled) return;
            ContributionCapture.configure({
                rawConfig: instrumentationCaptureConfig,
                http: fetchContext?.authAxios ?? null,
                getToken: authContext?.getToken ?? null,
                ownerUid: instrumentationOwnerUid,
            });
            ContributionCapture.startTransport();
            await ContributionCapture.reconcileCaptureAuthority();
            if (cancelled) return;
            appliedInstrumentationConfigKeyRef.current = instrumentationConfigKey;
            appliedInstrumentationAuthorityRef.current = {
                key: instrumentationAuthorityKey,
                ownerUid: instrumentationOwnerUid,
            };
            setAppliedInstrumentationConfigKey(instrumentationConfigKey);
        };
        void applyConfiguration();
        return () => { cancelled = true; };
    }, [
        instrumentationCaptureConfig,
        instrumentationAuthorityKey,
        instrumentationConfigKey,
        instrumentationConfigAuthoritative,
        fetchContext?.authAxios,
        authContext?.getToken,
        instrumentationOwnerUid,
    ]);

    useEffect(() => {
        if (!instrumentationCaptureConfig.enabled) return;
        if (appliedInstrumentationConfigKey !== instrumentationConfigKey) return;
        if (!selectedCompositionId || restoredCompositionId !== selectedCompositionId) return;
        if (!projectId || isRestoringComposition || !CompositionService.isInitialized()) return;
        void CompositionSessionService.ensureContributionCaptureSession({
            workspaceId: currentWorkspaceId,
            projectId,
            compositionId: selectedCompositionId,
            workspaceRole: currentWorkspaceRole,
            readOnly: compositionEditingDisabled,
            sessionSeq: CompositionSessionService.sessionSeq,
        });
    }, [
        instrumentationCaptureConfig,
        instrumentationConfigKey,
        appliedInstrumentationConfigKey,
        selectedCompositionId,
        restoredCompositionId,
        projectId,
        isRestoringComposition,
        currentWorkspaceId,
        currentWorkspaceRole,
        compositionEditingDisabled,
    ]);

    useEffect(() => () => {
        void (async () => {
            await CompositionSessionService.closeContributionCaptureSession("editor_unmounted");
            await ContributionCapture.flush();
            ContributionCapture.stopTransport();
        })();
    }, []);

    useLayoutEffect(() => {
        CompositionPersistenceService.setReadOnly(compositionEditingDisabled);
    }, [compositionEditingDisabled]);

    useEffect(() => {
        recordIntoTranscriptSessionRef.current = recordIntoTranscriptSession;
    }, [recordIntoTranscriptSession]);

    const clearRecordIntoTranscriptSession = useCallback(() => {
        if (recordIntoTranscriptRetryTimerRef.current != null) {
            window.clearTimeout(recordIntoTranscriptRetryTimerRef.current);
            recordIntoTranscriptRetryTimerRef.current = null;
        }
        recordIntoTranscriptSessionRef.current = null;
        recordIntoTranscriptCompletionHandoffRef.current = false;
        setRecordIntoTranscriptSession(null);
        setRecordIntoTranscriptStudioStatus(null);
        setRecordIntoTranscriptDiscardConfirmOpen(false);
    }, []);

    useEffect(() => {
        return () => {
            recordIntoTranscriptSessionRef.current = null;
            recordIntoTranscriptCompletionHandoffRef.current = false;
            if (recordIntoTranscriptRetryTimerRef.current != null) {
                window.clearTimeout(recordIntoTranscriptRetryTimerRef.current);
                recordIntoTranscriptRetryTimerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        const handleRecordIntoTranscriptRequest = (event: Event) => {
            const detail = (event as CustomEvent<RecordIntoTranscriptRequestDetail>)?.detail;
            if (
                !detail ||
                detail.version !== 1 ||
                (detail.mediaKind !== "audio" && detail.mediaKind !== "video") ||
                !Number.isFinite(Number(detail.anchor?.scriptLocalSec))
            ) {
                return;
            }

            if (compositionEditingDisabled) {
                setRecordIntoTranscriptNotice({
                    severity: "error",
                    message: "You need edit access to record into this transcript.",
                });
                return;
            }
            if (recordIntoTranscriptSessionRef.current || pageMode === PAGE_MODES.RECORDING_STUDIO) {
                setRecordIntoTranscriptNotice({
                    severity: "error",
                    message: "Finish the current recording before starting another one.",
                });
                return;
            }
            if (isRestoringComposition || !CompositionService.isInitialized()) {
                setRecordIntoTranscriptNotice({
                    severity: "error",
                    message: "The composition is still loading. Try that insertion point again in a moment.",
                });
                return;
            }

            const currentProjectId = String(projectId || ProjectAssetsStore.projectId || "").trim();
            const currentCompositionId = resolveRecordIntoTranscriptCompositionId({
                selectedCompositionId,
                engineCompositionId: (CompositionService.composition as any)?.compositionId,
                projectCompositionId: ProjectAssetsStore.projectCurrentCompositionId,
            });
            const scriptClip: any = CompositionService.getScriptLayerClip?.();
            const currentScriptClipId = String(scriptClip?.id ?? "").trim();

            const requestProjectId = String(detail.projectId ?? "").trim();
            const requestCompositionId = String(detail.compositionId ?? "").trim();
            const requestScriptClipId = String(detail.scriptClipId ?? "").trim();
            const requestIsStale =
                !currentProjectId ||
                !currentCompositionId ||
                !currentScriptClipId ||
                (requestProjectId && requestProjectId !== currentProjectId) ||
                (requestCompositionId && requestCompositionId !== currentCompositionId) ||
                (requestScriptClipId && requestScriptClipId !== currentScriptClipId);
            if (requestIsStale) {
                setRecordIntoTranscriptNotice({
                    severity: "error",
                    message: "That insertion point is no longer available. Right-click the transcript or timeline again.",
                });
                return;
            }

            const clipInSec = Number(scriptClip?.range?.[0]?.seconds ?? 0);
            const clipOutSec = Number(
                scriptClip?.range?.[1]?.seconds ?? scriptClip?.duration?.seconds ?? clipInSec,
            );
            const requestedAtSec = Number(detail.anchor.scriptLocalSec);
            if (
                !Number.isFinite(clipInSec) ||
                !Number.isFinite(clipOutSec) ||
                requestedAtSec < clipInSec - 1e-3 ||
                requestedAtSec > clipOutSec + 1e-3
            ) {
                setRecordIntoTranscriptNotice({
                    severity: "error",
                    message: "That insertion point falls outside the current script clip.",
                });
                return;
            }
            const request: RecordIntoTranscriptRequestDetail = {
                ...detail,
                projectId: currentProjectId,
                compositionId: currentCompositionId,
                scriptClipId: currentScriptClipId,
                anchor: {
                    ...detail.anchor,
                    scriptLocalSec: Math.max(clipInSec, Math.min(clipOutSec, requestedAtSec)),
                },
            };
            const nextSession: RecordIntoTranscriptSession = {
                request,
                originProjectId: currentProjectId,
                originCompositionId: currentCompositionId,
                originScriptClipId: currentScriptClipId,
                originCompositionSessionVersion: CompositionService.getSessionVersion(),
                phase: "recording",
            };

            recordIntoTranscriptSessionRef.current = nextSession;
            recordIntoTranscriptCompletionHandoffRef.current = false;
            setRecordIntoTranscriptDiscardConfirmOpen(false);
            setRecordIntoTranscriptStudioStatus(null);
            setRecordIntoTranscriptSession(nextSession);
            ContextMenuStore.hide();
            void CompositionService.pause().catch(() => undefined);
        };

        window.addEventListener(
            RECORD_INTO_TRANSCRIPT_REQUEST_EVENT,
            handleRecordIntoTranscriptRequest,
        );
        return () => {
            window.removeEventListener(
                RECORD_INTO_TRANSCRIPT_REQUEST_EVENT,
                handleRecordIntoTranscriptRequest,
            );
        };
    }, [
        compositionEditingDisabled,
        isRestoringComposition,
        pageMode,
        projectId,
        selectedCompositionId,
    ]);

    const subscriptionStatus = String(authContext.authState?.subscriptionStatus ?? "").trim().toLowerCase();
    const userPlan = Number(authContext.authState?.userPlan ?? authContext.getUserPlan?.());
    const isBeforeTrial = subscriptionStatus === "not_created" &&
        userPlan !== LibrettoPlan.StudioFree &&
        !GetLibrettoPlanDetails(userPlan).isLifetime;
	    const navigateToHome = () => navigate("/dashboard");
    const { hasPendingMultiSpeakerAutocreate } = useMultiSpeakerAutocreateWorkflow({
        searchParams,
        setSearchParams,
        projectId,
        selectedCompositionId,
        setSelectedCompositionId,
        selectedCompositionTitle,
        isRestoringComposition,
        localTrackAvailability,
        setStudioAutocreatePreferredCompositionId,
        createSequence: CreateSequence,
        getTrackUrl: GetTrackUrl,
        createSpeaker: CreateSpeaker,
        authContext,
    });

    const studioAutocreateTrackStatuses = useMemo<StudioAutocreateTrackStatus[]>(() => {
        if (!studioAutocreateSession?.assetId) return [];
        const targetAssetId = String(studioAutocreateSession.assetId);
        const tracks = (ProjectAssetsStore.projectTracks || [])
            .filter((track: any) => String(track?.assetId ?? "") === targetAssetId)
            .filter((track: any) => isStudioAutocreateTrack(track))
            .sort((a: any, b: any) => {
                const aStart = Number(a?.recordingStartMs ?? 0);
                const bStart = Number(b?.recordingStartMs ?? 0);
                if (Number.isFinite(aStart) && Number.isFinite(bStart) && aStart !== bStart) {
                    return aStart - bStart;
                }
                const aName = String(a?.title || a?.name || "");
                const bName = String(b?.title || b?.name || "");
                const byName = aName.localeCompare(bName, undefined, { sensitivity: "base" });
                if (byName !== 0) return byName;
                const aId = String(a?.trackId || a?.id || "");
                const bId = String(b?.trackId || b?.id || "");
                return aId.localeCompare(bId);
            });

        return tracks.map((track: any) => {
            const statusRaw = String(track?.status ?? "");
            const isReady = isTrackReadyForAutocreate(track);
            const isFailed = isTrackFailedForAutocreate(track);
            return {
                id: String(track?.trackId || track?.id || `${targetAssetId}:${track?.title || track?.name || "track"}`),
                name: String(track?.title || track?.name || "Unnamed track"),
                statusRaw,
                statusLabel: isReady ? "Ready" : (isFailed ? "Failed" : "Processing"),
                isReady,
                isFailed,
                track,
            };
        });
    }, [studioAutocreateSession, ProjectAssetsStore.projectTracks]);

    const studioAutocreateReadyCount = useMemo(
        () => studioAutocreateTrackStatuses.filter((track) => track.isReady).length,
        [studioAutocreateTrackStatuses]
    );
    const studioAutocreateHasFailures = useMemo(
        () => studioAutocreateTrackStatuses.some((track) => track.isFailed),
        [studioAutocreateTrackStatuses]
    );

    // Match file-usability semantics from V2ProjectSidebar:
    // a track is usable if Firestore status is ready OR local Dexie/OPFS bytes are present.
    useEffect(() => {
        let cancelled = false;
        const tracks = ProjectAssetsStore.projectTracks || [];
        const trackIds = tracks
            .map((tr: any) => String(tr?.id || tr?.trackId || "").trim())
            .filter(Boolean);

        if (trackIds.length === 0) {
            setLocalTrackAvailability(new Map());
            return;
        }

        (async () => {
            const availability = await GetTracksLocalAvailability(trackIds);
            if (!cancelled) {
                setLocalTrackAvailability(availability);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [ProjectAssetsStore.projectTracks]);

    useEffect(() => {
        let cancelled = false;
        const tracks = ProjectAssetsStore.projectTracks || [];
        const trackIds = tracks
            .map((tr: any) => String(tr?.id || tr?.trackId || "").trim())
            .filter(Boolean);

        if (trackIds.length === 0) {
            setLocalTrackUploadSnapshots(new Map());
            return;
        }

        const refreshSnapshots = async () => {
            const snapshots = await GetTracksUploadSnapshots(trackIds);
            if (!cancelled) {
                setLocalTrackUploadSnapshots(snapshots);
            }
        };

        void refreshSnapshots();
        const intervalId = window.setInterval(() => {
            void refreshSnapshots();
        }, 900);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [ProjectAssetsStore.projectTracks]);

    const handleRecordIntoTranscriptRecordingComplete = useCallback((result: any) => {
        const current = recordIntoTranscriptSessionRef.current;
        if (!current || current.phase !== "recording") return;

        recordIntoTranscriptCompletionHandoffRef.current = true;
        const assetId = String(result?.assetId ?? "").trim();
        if (!assetId || result?.screenAssetId) {
            const nextSession: RecordIntoTranscriptSession = {
                ...current,
                phase: "error",
                errorMessage: "The recording finished, but no audio or camera file was created.",
                canRetry: false,
            };
            recordIntoTranscriptSessionRef.current = nextSession;
            setRecordIntoTranscriptSession(nextSession);
            return;
        }

        const nextSession: RecordIntoTranscriptSession = {
            ...current,
            phase: "preparing",
            assetId,
            filetype: result?.filetype === "video" ? "video" : current.request.mediaKind,
            preparingStartedAtMs: Date.now(),
            errorMessage: undefined,
            canRetry: true,
        };
        recordIntoTranscriptSessionRef.current = nextSession;
        setRecordIntoTranscriptSession(nextSession);
        setRecordIntoTranscriptRetryRevision((value) => value + 1);
    }, []);

    const handleRecordIntoTranscriptStudioLeave = useCallback(() => {
        if (recordIntoTranscriptCompletionHandoffRef.current) {
            recordIntoTranscriptCompletionHandoffRef.current = false;
            return;
        }
        clearRecordIntoTranscriptSession();
    }, [clearRecordIntoTranscriptSession]);

    const handleRecordIntoTranscriptStudioError = useCallback((details: any) => {
        const stage = String(details?.stage ?? "recording");
        const message = stage === "upload"
            ? "The recording could not be saved. You can try recording again."
            : "The recorder hit a problem. Check your camera or microphone and try again.";
        setRecordIntoTranscriptNotice({ severity: "error", message });
    }, []);

    const requestRecordIntoTranscriptClose = useCallback((reportedStatus?: EmbeddedStudioStatus) => {
        const current = recordIntoTranscriptSessionRef.current;
        if (!current || current.phase === "preparing") return;
        if (current.phase === "error") {
            clearRecordIntoTranscriptSession();
            return;
        }

        const status = reportedStatus ?? recordIntoTranscriptStudioRef.current?.getStatus?.() ?? null;
        if (status?.busy || status?.isStopping || status?.isDiscarding) return;
        if (status?.recordingActive) {
            setRecordIntoTranscriptDiscardConfirmOpen(true);
            return;
        }

        const studio = recordIntoTranscriptStudioRef.current;
        if (!studio) {
            clearRecordIntoTranscriptSession();
            return;
        }
        void studio.discardAndLeave().then((left) => {
            if (!left && recordIntoTranscriptSessionRef.current?.request.requestId === current.request.requestId) {
                setRecordIntoTranscriptNotice({
                    severity: "error",
                    message: "The recorder is busy. Try closing it again in a moment.",
                });
            }
        });
    }, [clearRecordIntoTranscriptSession]);

    const discardRecordIntoTranscriptRecording = useCallback(() => {
        const studio = recordIntoTranscriptStudioRef.current;
        setRecordIntoTranscriptDiscardConfirmOpen(false);
        if (!studio) {
            clearRecordIntoTranscriptSession();
            return;
        }
        void studio.discardAndLeave().then((left) => {
            if (!left) {
                setRecordIntoTranscriptNotice({
                    severity: "error",
                    message: "The recording could not be discarded yet. Try again in a moment.",
                });
            }
        });
    }, [clearRecordIntoTranscriptSession]);

    const retryRecordIntoTranscriptInsertion = useCallback(() => {
        const current = recordIntoTranscriptSessionRef.current;
        if (!current?.assetId || current.phase !== "error" || current.canRetry === false) return;
        const nextSession: RecordIntoTranscriptSession = {
            ...current,
            phase: "preparing",
            preparingStartedAtMs: Date.now(),
            errorMessage: undefined,
            canRetry: true,
        };
        recordIntoTranscriptSessionRef.current = nextSession;
        setRecordIntoTranscriptSession(nextSession);
        setRecordIntoTranscriptRetryRevision((value) => value + 1);
    }, []);

    useEffect(() => {
        const session = recordIntoTranscriptSession;
        if (!session || session.phase !== "preparing" || !session.assetId) return;

        const requestId = session.request.requestId;
        const isCurrentSession = () =>
            recordIntoTranscriptSessionRef.current?.request.requestId === requestId;
        const failPermanently = (message: string, canRetry: boolean) => {
            const current = recordIntoTranscriptSessionRef.current;
            if (!current || current.request.requestId !== requestId) return;
            const nextSession: RecordIntoTranscriptSession = {
                ...current,
                phase: "error",
                errorMessage: message,
                canRetry,
            };
            recordIntoTranscriptSessionRef.current = nextSession;
            setRecordIntoTranscriptSession(nextSession);
        };
        const scheduleRetry = () => {
            if (!isCurrentSession()) return;
            const startedAt = Number(session.preparingStartedAtMs ?? Date.now());
            if (Date.now() - startedAt >= RECORDING_TRACK_READY_TIMEOUT_MS) {
                failPermanently(
                    "The recording is saved in Project Files, but it could not be inserted yet.",
                    true,
                );
                return;
            }
            if (recordIntoTranscriptRetryTimerRef.current != null) {
                window.clearTimeout(recordIntoTranscriptRetryTimerRef.current);
            }
            recordIntoTranscriptRetryTimerRef.current = window.setTimeout(() => {
                recordIntoTranscriptRetryTimerRef.current = null;
                setRecordIntoTranscriptRetryRevision((value) => value + 1);
            }, RECORDING_TRACK_RETRY_DELAY_MS);
        };

        const currentProjectId = String(projectId || ProjectAssetsStore.projectId || "").trim();
        const currentCompositionId = resolveRecordIntoTranscriptCompositionId({
            selectedCompositionId,
            engineCompositionId: CompositionService.isInitialized()
                ? (CompositionService.composition as any)?.compositionId
                : "",
            projectCompositionId: ProjectAssetsStore.projectCurrentCompositionId,
        });
        const currentScriptClip: any = CompositionService.isInitialized()
            ? CompositionService.getScriptLayerClip?.()
            : null;
        const currentScriptClipId = String(currentScriptClip?.id ?? "").trim();
        if (
            !CompositionService.isInitialized() ||
            CompositionService.getSessionVersion() !== session.originCompositionSessionVersion ||
            currentProjectId !== session.originProjectId ||
            currentCompositionId !== session.originCompositionId ||
            currentScriptClipId !== session.originScriptClipId
        ) {
            failPermanently(
                "The composition changed while the recording was being prepared. The recording is still available in Project Files.",
                false,
            );
            return;
        }

        const track = (ProjectAssetsStore.projectTracks || []).find(
            (candidate: any) => String(candidate?.assetId ?? "") === session.assetId,
        );
        const trackId = String(track?.trackId ?? track?.id ?? "").trim();
        if (!track || !trackId) {
            scheduleRetry();
            return;
        }

        if (recordIntoTranscriptInsertionInFlightRef.current.has(requestId)) return;
        recordIntoTranscriptInsertionInFlightRef.current.add(requestId);

        void (async () => {
            let insertionCommitted = false;
            try {
                const inserted = await insertTrackIntoScriptScene({
                    trackId,
                    trackType: session.filetype === "video" ? "video" : "audio",
                    trackTitle: String(track?.title ?? track?.name ?? ""),
                    trackResolution: String(track?.resolution ?? ""),
                    atClipLocalSec: session.request.anchor.scriptLocalSec,
                    preferExistingEmptyScene: false,
                    expectedScriptClipId: session.originScriptClipId,
                    expectedCompositionSessionVersion: session.originCompositionSessionVersion,
                    resolveTrackUrl: async (id) => GetTrackUrl({ trackId: id }),
                });

                if (!isCurrentSession()) return;
                if (!inserted) {
                    scheduleRetry();
                    return;
                }

                // The composition mutation is complete at this point. Selection and
                // seeking are convenience UI updates; a failure in either must never
                // retry the insertion and duplicate the recorded take.
                insertionCommitted = true;
                setRecordIntoTranscriptNotice({
                    severity: "success",
                    message: "Recording inserted. Its transcript will appear as processing finishes.",
                });
                clearRecordIntoTranscriptSession();

                const mappedTimelineStartSec =
                    ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(
                        inserted.sceneStartSec,
                    );
                const mappedTimelineEndSec =
                    ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(
                        inserted.sceneEndSec,
                    );
                const timelineStartSec =
                    mappedTimelineStartSec != null && Number.isFinite(mappedTimelineStartSec)
                        ? mappedTimelineStartSec
                        : inserted.timelineStartSec;
                const timelineEndSec =
                    mappedTimelineEndSec != null && Number.isFinite(mappedTimelineEndSec)
                        ? mappedTimelineEndSec
                        : inserted.timelineEndSec;

                try {
                    ApplicationStore.setSelectedClipId(inserted.scriptClipId);
                    ApplicationStore.setSelectedSceneIndex(inserted.sceneIndex, {
                        start: timelineStartSec,
                        end: timelineEndSec,
                    });
                    await CompositionService.seek(
                        Math.min(timelineEndSec, timelineStartSec + 0.001),
                    );
                } catch (selectionError) {
                    console.warn(
                        "[V2Editor] Recording was inserted, but the new scene could not be focused",
                        selectionError,
                    );
                }
            } catch (error) {
                console.warn("[V2Editor] Could not insert recording into transcript", error);
                if (!insertionCommitted) scheduleRetry();
            } finally {
                recordIntoTranscriptInsertionInFlightRef.current.delete(requestId);
            }
        })();
    }, [
        GetTrackUrl,
        clearRecordIntoTranscriptSession,
        projectId,
        recordIntoTranscriptRetryRevision,
        recordIntoTranscriptSession,
        selectedCompositionId,
    ]);

    const backgroundActivityRows = useMemo<BackgroundActivityRow[]>(() => {
        const tracks = ProjectAssetsStore.projectTracks || [];
        const rows: BackgroundActivityRow[] = [];

        for (const track of tracks) {
            const trackId = String(track?.trackId || track?.id || "").trim();
            if (!trackId) continue;

            const remoteSynced = isTrackStatusReady(track);
            const localUsable = remoteSynced || localTrackAvailability.get(trackId) === true;
            if (!localUsable) continue;

            const syncing = !remoteSynced;
            const transcribing = isTranscriptionInProgress(track?.transcriptionState);
            if (!syncing && !transcribing) continue;

            const snapshot = localTrackUploadSnapshots.get(trackId);
            const snapshotProgress = snapshot?.uploadProgressPercent;
            const syncProgressPercent = syncing
                ? (
                    snapshot?.state === "UPLOADED"
                        ? 100
                        : (typeof snapshotProgress === "number" && Number.isFinite(snapshotProgress)
                            ? Math.max(0, Math.min(99, Math.round(snapshotProgress)))
                            : null)
                )
                : null;

            rows.push({
                trackId,
                name: String(track?.title || track?.name || "Untitled file"),
                syncing,
                syncProgressPercent,
                transcribing,
            });
        }

        return rows.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
    }, [ProjectAssetsStore.projectTracks, localTrackAvailability, localTrackUploadSnapshots]);

    const syncingTracksCount = useMemo(
        () => backgroundActivityRows.filter((row) => row.syncing).length,
        [backgroundActivityRows]
    );
    const transcribingTracksCount = useMemo(
        () => backgroundActivityRows.filter((row) => row.transcribing).length,
        [backgroundActivityRows]
    );

    useEffect(() => {
        const hasBackgroundActivity = backgroundActivityRows.length > 0;
        if (!hasBackgroundActivity) {
            setShowBackgroundActivityCard(false);
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setShowBackgroundActivityCard(true);
        }, 700);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [backgroundActivityRows.length]);

    const backgroundActivityCardBottom = ExportStore.state.status !== "idle" ? 116 : 24;

		    const openProjectSidebarFiles = useCallback(() => {
		        setSidebarItemSelected(SidebarItems.PROJECT);
		        setHighlightProjectFilesPanel(true);

	        if (highlightProjectFilesTimerRef.current != null) {
	            window.clearTimeout(highlightProjectFilesTimerRef.current);
	        }
	        highlightProjectFilesTimerRef.current = window.setTimeout(() => {
	            setHighlightProjectFilesPanel(false);
	            highlightProjectFilesTimerRef.current = null;
	        }, 1400);
	    }, []);

	    // Allow deep-linked UI actions (e.g. timeline context menu) to open the Project sidebar.
	    useEffect(() => {
	        window.addEventListener('openProjectSidebarFiles', openProjectSidebarFiles as any);
	        return () => {
		            window.removeEventListener('openProjectSidebarFiles', openProjectSidebarFiles as any);
		            if (highlightProjectFilesTimerRef.current != null) {
		                window.clearTimeout(highlightProjectFilesTimerRef.current);
		                highlightProjectFilesTimerRef.current = null;
		            }
		        };
	    }, [openProjectSidebarFiles]);

	    useEffect(() => {
	        const handler = (event: any) => {
	            const startSec = Number(event?.detail?.startSec);
	            const endSec = Number(event?.detail?.endSec);
	            if (!Number.isFinite(startSec) || !Number.isFinite(endSec)) return;
	            if (Math.abs(endSec - startSec) <= 1e-3) return;
	            void handleDuplicateRangeToNewComposition(startSec, endSec);
	        };

	        window.addEventListener(DUPLICATE_RANGE_TO_NEW_COMPOSITION_EVENT, handler as any);
	        return () => {
	            window.removeEventListener(DUPLICATE_RANGE_TO_NEW_COMPOSITION_EVENT, handler as any);
	        };
	    }, [handleDuplicateRangeToNewComposition]);

	    const pendingInsertIntoScriptContext = ApplicationStore.peekPendingInsertIntoScriptContext?.() ?? null;
	    const isAddMediaSidebarSpotlightActive =
	        sidebarItemSelected === SidebarItems.PROJECT &&
	        Boolean(pendingInsertIntoScriptContext);
	    useDismissPendingAddMediaFlow({ active: Boolean(isAddMediaSidebarSpotlightActive) });

			    const openPropertiesSidebarLayout = useCallback(
			        (
			            sceneIndex: number | null,
			            opts?: {
			                // When false, avoid selecting the full scene range in the transcript (preserves current word selection).
			                syncTranscriptSelection?: boolean;
			            }
			        ) => {
			        setSidebarItemSelected(SidebarItems.PROPERTIES);
			        try { ApplicationStore.clearSelectedClipId(); } catch {}

			        if (sceneIndex == null) return;
			        const idx = Number(sceneIndex);
			        if (!Number.isFinite(idx) || idx < 0) return;

			        const syncTranscriptSelection = opts?.syncTranscriptSelection !== false;

			        // Prefer ScriptLayerStore scene timing for transcript sync when selecting the scene.
			        const info = ScriptLayerStore.scenes.find((s: any) => Number(s?.index) === idx) ?? ScriptLayerStore.scenes[idx];
			        if (syncTranscriptSelection && info && Number.isFinite(Number(info.startSec)) && Number.isFinite(Number(info.endSec))) {
			            try {
			                const startTimeline = ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(Number(info.startSec));
			                const endTimeline = ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(Number(info.endSec));
			                ApplicationStore.setSelectedSceneIndex(
			                    idx,
			                    startTimeline != null && endTimeline != null
			                        ? { start: startTimeline, end: endTimeline }
			                        : undefined,
			                );
			            } catch {}
			        } else {
			            // Still select the scene (for UI targeting) but don't force a transcript range selection.
			            try { ApplicationStore.setSelectedSceneIndex(idx); } catch {}
			        }
			        try { ApplicationStore.setPendingSceneLayoutPickerContext({ sceneIndex: idx }); } catch {}
			    },
			    []
			);

			    // Allow deep-linked UI actions to open the Properties sidebar and focus the Layout picker for a given scene.
			    useEffect(() => {
			        const handler = (event: any) => {
			            const idxRaw = event?.detail?.sceneIndex;
			            const idx = idxRaw == null ? null : Number(idxRaw);
			            const syncTranscriptSelection = event?.detail?.syncTranscriptSelection;
			            openPropertiesSidebarLayout(Number.isFinite(idx as any) ? (idx as number) : null, {
			                syncTranscriptSelection: syncTranscriptSelection == null ? undefined : Boolean(syncTranscriptSelection),
			            });
			        };
			        window.addEventListener('openPropertiesSidebarLayout', handler as any);
			        return () => {
			            window.removeEventListener('openPropertiesSidebarLayout', handler as any);
			        };
			    }, [openPropertiesSidebarLayout]);

    // Zoom OAuth returns to /editor/:projectId?zoomImport=1. Reopen the picker and
    // immediately clean the one-shot query parameter.
    useEffect(() => {
        const shouldOpen = searchParams.get("zoomImport");
        if (shouldOpen !== "1" && shouldOpen !== "true") return;

        setZoomImportDialogOpen(true);
        const next = new URLSearchParams(searchParams);
        next.delete("zoomImport");
        setSearchParams(next, {replace: true});
    }, [searchParams, setSearchParams]);

	    // URL-driven UI actions: /editor/:projectId?openProjectSidebarFiles=1
    useEffect(() => {
        const shouldOpen = searchParams.get('openProjectSidebarFiles');
        if (shouldOpen !== '1' && shouldOpen !== 'true') return;

	        openProjectSidebarFiles();

	        const next = new URLSearchParams(searchParams);
	        next.delete('openProjectSidebarFiles');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams, openProjectSidebarFiles]);

    // Edit for Clarity return flow: queue the uploaded asset for script-clip auto insertion
    // without opening the studio auto-create modal.
    useEffect(() => {
        const shouldAutoinsert = searchParams.get("clarityAutoinsert");
        const clarityAssetId = String(searchParams.get("clarityAssetId") || "").trim();
        const clarityCompositionId = String(searchParams.get("clarityCompositionId") || "").trim();
        const rawClarityIntensity = String(searchParams.get("clarityIntensity") || "").trim().toLowerCase();
        const claritySettings: EditForClarityRunSettings = {
            intensity:
                rawClarityIntensity === "low" || rawClarityIntensity === "medium" || rawClarityIntensity === "heavy"
                    ? rawClarityIntensity
                    : "medium",
        };
        if ((shouldAutoinsert !== "1" && shouldAutoinsert !== "true") || !clarityAssetId) return;

        if (clarityCompositionId) {
            setStudioAutocreatePreferredCompositionId(clarityCompositionId);
        }

        setPendingRecordingAssetIds((prev) => {
            if (prev.some((p) => String(p?.assetId || "").trim() === clarityAssetId)) {
                return prev;
            }
            return [
                ...prev,
                {
                    assetId: clarityAssetId,
                    filetype: "audio",
                    editForClaritySettings: claritySettings,
                    workflowGuidanceType: "clean_up_recording",
                },
            ];
        });

        const next = new URLSearchParams(searchParams);
        next.delete("clarityAutoinsert");
        next.delete("clarityAssetId");
        next.delete("clarityCompositionId");
        next.delete("clarityIntensity");
        next.delete("clarityRemoveFillerWords");
        next.delete("clarityTightenSilenceGaps");
        next.delete("clarityRemoveRetakes");
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    // Enhance-audio return flow: queue uploaded asset for script-clip auto insertion
    // and open the Properties sidebar with the inserted clip selected.
    useEffect(() => {
        const shouldAutoinsert = searchParams.get("enhanceAudioAutoinsert");
        const enhanceAudioAssetId = String(searchParams.get("enhanceAudioAssetId") || "").trim();
        const enhanceAudioCompositionId = String(searchParams.get("enhanceAudioCompositionId") || "").trim();
        if ((shouldAutoinsert !== "1" && shouldAutoinsert !== "true") || !enhanceAudioAssetId) return;

        if (enhanceAudioCompositionId) {
            setStudioAutocreatePreferredCompositionId(enhanceAudioCompositionId);
        }

        setPendingRecordingAssetIds((prev) => {
            if (prev.some((p) => String(p?.assetId || "").trim() === enhanceAudioAssetId)) {
                return prev;
            }
            return [
                ...prev,
                {
                    assetId: enhanceAudioAssetId,
                    filetype: "audio",
                    openPropertiesSidebarOnInsert: true,
                    autoEnableStudioSoundOnInsert: true,
                    workflowGuidanceType: "enhance_audio",
                },
            ];
        });

        const next = new URLSearchParams(searchParams);
        next.delete("enhanceAudioAutoinsert");
        next.delete("enhanceAudioAssetId");
        next.delete("enhanceAudioCompositionId");
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    // Add-captions return flow: queue uploaded asset for script-clip auto insertion
    // and open the Captions sidebar once the script clip is created.
    useEffect(() => {
        const shouldAutoinsert = searchParams.get("captionsAutoinsert");
        const captionsAssetId = String(searchParams.get("captionsAssetId") || "").trim();
        const captionsCompositionId = String(searchParams.get("captionsCompositionId") || "").trim();
        if ((shouldAutoinsert !== "1" && shouldAutoinsert !== "true") || !captionsAssetId) return;

        if (captionsCompositionId) {
            setStudioAutocreatePreferredCompositionId(captionsCompositionId);
        }

        setPendingRecordingAssetIds((prev) => {
            if (prev.some((p) => String(p?.assetId || "").trim() === captionsAssetId)) {
                return prev;
            }
            return [
                ...prev,
                {
                    assetId: captionsAssetId,
                    filetype: "audio",
                    openCaptionsSidebarOnInsert: true,
                    workflowGuidanceType: "add_captions",
                },
            ];
        });

        const next = new URLSearchParams(searchParams);
        next.delete("captionsAutoinsert");
        next.delete("captionsAssetId");
        next.delete("captionsCompositionId");
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    // Upload-and-edit return flow: queue uploaded asset for standard script-clip auto insertion.
    useEffect(() => {
        const shouldAutoinsert = searchParams.get("uploadAutoinsert");
        const uploadAssetId = String(searchParams.get("uploadAssetId") || "").trim();
        const uploadCompositionId = String(searchParams.get("uploadCompositionId") || "").trim();
        const uploadWorkflowHint = String(searchParams.get("uploadWorkflowHint") || "").trim().toLowerCase();
        if ((shouldAutoinsert !== "1" && shouldAutoinsert !== "true") || !uploadAssetId) return;

        if (uploadCompositionId) {
            setStudioAutocreatePreferredCompositionId(uploadCompositionId);
        }

        setPendingRecordingAssetIds((prev) => {
            if (prev.some((p) => String(p?.assetId || "").trim() === uploadAssetId)) {
                return prev;
            }
            return [
                ...prev,
                {
                    assetId: uploadAssetId,
                    filetype: "audio",
                    workflowGuidanceType:
                        uploadWorkflowHint === "edit_by_transcript"
                            ? "edit_by_transcript"
                            : (uploadWorkflowHint === "create_social_clips" ? "create_social_clips" : undefined),
                },
            ];
        });

        const next = new URLSearchParams(searchParams);
        next.delete("uploadAutoinsert");
        next.delete("uploadAssetId");
        next.delete("uploadCompositionId");
        next.delete("uploadWorkflowHint");
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    // Onboarding activation flow handoff from dashboard/modal entry points.
    useEffect(() => {
        const incomingRunId = String(searchParams.get("activationRunId") || "").trim();
        if (!incomingRunId) return;
        setActivationRunId((prev) => (prev === incomingRunId ? prev : incomingRunId));
    }, [searchParams]);

    // Studio return flow: auto-create a sequence and script clip for recordings started from an empty composition.
    useEffect(() => {
        const shouldAutocreate = searchParams.get("studioAutocreate");
        const studioOrigin = searchParams.get("studioOrigin");
        const studioAssetId = searchParams.get("studioAssetId");
        const originCompositionId = searchParams.get("originCompositionId");
        if ((shouldAutocreate !== "1" && shouldAutocreate !== "true") || studioOrigin !== "editor") return;
        if (!studioAssetId || !originCompositionId) return;

        const sessionKey = `${originCompositionId}:${studioAssetId}`;
        setStudioAutocreateSession((prev) => {
            if (prev?.sessionKey === sessionKey) return prev;
            return {
                sessionKey,
                assetId: studioAssetId,
                originCompositionId,
                phase: "waiting",
                errorMessage: null,
            };
        });
        setStudioAutocreatePreferredCompositionId(originCompositionId);

        const next = new URLSearchParams(searchParams);
        next.delete("studioAutocreate");
        next.delete("studioOrigin");
        next.delete("studioAssetId");
        next.delete("originCompositionId");
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        if (!studioAutocreatePreferredCompositionId) return;
        const compositions = ProjectAssetsStore.projectCompositions || [];
        const comp = compositions.find((candidate: any) => candidate?.id === studioAutocreatePreferredCompositionId);
        if (!comp) return;

        if (selectedCompositionId !== comp.id) {
            setSelectedCompositionId(comp.id);
        }
        const nextTitle = comp.title ?? null;
        if (selectedCompositionTitle !== nextTitle) {
            setSelectedCompositionTitle(nextTitle);
        }
    }, [
        studioAutocreatePreferredCompositionId,
        ProjectAssetsStore.projectCompositions,
        selectedCompositionId,
        selectedCompositionTitle,
    ]);

    // Composition preference is one-shot for URL-driven handoffs outside studio autocreate.
    useEffect(() => {
        if (!studioAutocreatePreferredCompositionId) return;
        if (studioAutocreateSession) return;
        if (hasPendingMultiSpeakerAutocreate) return;
        if (selectedCompositionId !== studioAutocreatePreferredCompositionId) return;
        setStudioAutocreatePreferredCompositionId(null);
    }, [
        studioAutocreatePreferredCompositionId,
        studioAutocreateSession,
        hasPendingMultiSpeakerAutocreate,
        selectedCompositionId,
    ]);

    // Switching projects does not always cause a full page reload; clear editor state so the previous
    // project's composition/store data cannot leak into the next project.
	    useLayoutEffect(() => {
	        setSelectedCompositionId(null);
	        setSelectedCompositionTitle(null);
	        setIsRestoringComposition(false);
            setStudioAutocreateSession(null);
            setStudioAutocreatePreferredCompositionId(null);
            setWorkflowGuidanceSession(null);
            setEditForClarityCleanupProgress(null);
            setIsEditForClarityCleanupDialogOpen(false);
            setEditForClarityCleanupStopRequested(false);
            editForClarityCleanupStopRequestedRef.current = false;
	        compositionLoadSeqRef.current += 1;
	        try { FillerWordStore.deactivate(); } catch {}
	        try { GapStore.deactivate(); } catch {}
	        try { ProjectAssetsStore.subscribe(null); } catch {}
	        void CompositionSessionService.unloadComposition();
	    }, [projectId]);

    useEffect(() => {
        if (!projectId) return;
        if (!authContext.authReady) return;
        const workspaceId = authContext.getCurrentWorkspaceId?.();
        if (!workspaceId) return;
        try {
            LayoutPacksStore.subscribe({
                workspaceId,
                userId: authContext.getUserId?.(),
                onAuthError: () => {
                    console.warn("[V2Editor] Layout packs subscription permission error");
                },
            });
        } catch {}
        ProjectAssetsStore.subscribe(projectId, {
            workspaceId,
            onAuthError: () => {
                // Firestore handles auth/token refresh internally; permission errors here are
                // typically rules/index issues, not an expired token.
                console.warn('[V2Editor] Firestore subscription permission error');
            },
            getFocusChunk: async ({ trackId, chunkId }) => {
                try {
                    return await GetFocusChunk({ trackId, chunkId });
                } catch {
                    return undefined;
                }
            },
        });
        return () => {
            ProjectAssetsStore.teardown();
            LayoutPacksStore.teardown();
        };
    }, [projectId, authContext.authReady, authContext.authState?.userId]);

    // Set page title to project name
    useEffect(() => {
        const name = ProjectAssetsStore.projectName;
        document.title = name ? name : "Libretto";
    }, [ProjectAssetsStore.projectName]);

    useEffect(() => {
        const update = () => {
            setCanUndo(UndoManager.canUndo);
            setCanRedo(UndoManager.canRedo);
        };
        update();
        const unsubscribe = UndoManager.subscribe(update);
        return () => unsubscribe();
    }, []);

    // Global keyboard shortcuts for undo/redo
	    useEffect(() => {
	        const handleKeyDown = async (e: KeyboardEvent) => {
            if (compositionEditingDisabled) return;
            // Check if user is typing in an input field (don't intercept in those cases)
            const target = e.target as HTMLElement;
            const isInputField = target.tagName === 'INPUT' || 
                                 target.tagName === 'TEXTAREA' || 
                                 target.isContentEditable;
            
            // Allow default behavior in input fields unless it's explicitly our editor
            if (isInputField && !target.closest('.v2editor-scroll')) {
                return;
            }

            const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
            const modifierKey = isMac ? e.metaKey : e.ctrlKey;
            
            if (!modifierKey) return;
            
            // Undo: Ctrl+Z / Cmd+Z
	            if (e.key === 'z' && !e.shiftKey) {
	                e.preventDefault();
	                if (UndoManager.canUndo) {
	                    await UndoManager.undo();
	                    trackSessionEvent("undo_redo");
	                }
	                return;
	            }
            
            // Redo: Ctrl+Shift+Z / Cmd+Shift+Z OR Ctrl+Y / Cmd+Y
	            if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
	                e.preventDefault();
	                if (UndoManager.canRedo) {
	                    await UndoManager.redo();
	                    trackSessionEvent("undo_redo");
	                }
	                return;
	            }
	        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [compositionEditingDisabled]);

    // Load and restore composition whenever selectedCompositionId changes.
	    useEffect(() => {
	        const compId = selectedCompositionId;
	        if (!compId) {
	            setIsRestoringComposition(false);
	            setRestoredCompositionId(null);
	            compositionLoadSeqRef.current += 1;
	            return;
	        }
	        if (!authContext.authReady) return;
	        const userId = authContext.authState?.userId || authContext.getUserId?.();
	        if (!userId) return;

	        // Cancel/clear mode-specific editor overlays that may hold stale references.
	        try { FillerWordStore.deactivate(); } catch {}
	        try { GapStore.deactivate(); } catch {}

	        const mySeq = ++compositionLoadSeqRef.current;
	        setRestoredCompositionId(null);
	        setIsRestoringComposition(true);

	        (async () => {
	            try {
	                const comps = ProjectAssetsStore.projectCompositions || [];
	                const compRec = comps.find((c: any) => c?.id === compId);
	                const expectedEmpty = Boolean(compRec?.isEmpty);
	                const displayWidth = Number(compRec?.canvasWidth ?? compRec?.width);
	                const displayHeight = Number(compRec?.canvasHeight ?? compRec?.height);
	                const displayBg = typeof compRec?.backgroundColor === "string" ? String(compRec.backgroundColor) : undefined;

	                await CompositionSessionService.loadComposition({
	                    userId,
                        workspaceId: authContext.getCurrentWorkspaceId?.(),
	                    compositionId: compId,
                    projectId: projectId as string,
                    workspaceRole: currentWorkspaceRole,
                    resolveAssetUrl: ResolveAssetUrl,
                    resolveEnhancedAudioUrl: async (trackId: string) => {
                        try {
                            return await GetEnhancedAudioUrl({ trackId });
                        } catch {
                            return undefined;
                        }
                    },
                    expectedEmpty,
                    displaySettings: {
                        width: Number.isFinite(displayWidth) && displayWidth > 0 ? displayWidth : undefined,
                        height: Number.isFinite(displayHeight) && displayHeight > 0 ? displayHeight : undefined,
                        backgroundColor: displayBg,
                    },
	                    readOnly: compositionEditingDisabled,
	                });
	                if (compositionLoadSeqRef.current === mySeq) {
	                    setRestoredCompositionId(compId);
	                }
	            } catch (e) {
	                console.error("[V2Editor] Failed to load composition", e);
	            } finally {
	                if (compositionLoadSeqRef.current === mySeq) {
	                    setIsRestoringComposition(false);
	                }
	            }
	        })();
	        // eslint-disable-next-line react-hooks/exhaustive-deps
	    }, [
            selectedCompositionId,
            authContext.authReady,
            authContext.authState?.userId,
            compositionEditingDisabled,
        ]);

    // Inject transcript fetcher for bridge (auth-aware)
    const assetStore = useAssetStore();
    const handleSelectCreateClip = useCallback((clipId: string) => {
        setSelectedCreateClipId(clipId);

        const clip = createClipsDraft.find((item) => item.id === clipId);
        const compositionId = String(clip?.compositionId ?? clip?.id ?? "").trim();
        if (!compositionId) return;

        setStudioAutocreatePreferredCompositionId(null);
        const nextTitle = clip?.title ?? null;
        startCompositionSwitch({
            compositionId,
            compositionTitle: nextTitle,
        });
    }, [
        createClipsDraft,
        startCompositionSwitch,
    ]);
    const handleRenameSelectedComposition = useCallback(async (compositionId: string, newName: string) => {
        if (compositionEditingDisabled) return;
        await RenameComposition({ compositionId, newName });
        setSelectedCompositionTitle(newName);
    }, [RenameComposition, compositionEditingDisabled]);
    useAutoCompositionTitle({
        enabled: Boolean(projectId) && !compositionEditingDisabled,
        projectId,
        compositionId: selectedCompositionId,
        compositionTitle: selectedCompositionTitle,
        compositionRecord: selectedCompositionRecord,
        isCompositionEmpty: isSelectedCompositionEmpty,
        projectTracks: ProjectAssetsStore.projectTracks || [],
        getTranscript: assetStore.GetTranscript,
        postAiCompositionTitle: assetStore.PostAiCompositionTitle,
        renameComposition: assetStore.RenameComposition,
        renameProjectIfUntitled: assetStore.RenameProjectIfUntitled,
        onTitleUpdated: (nextTitle) => {
            setSelectedCompositionTitle(nextTitle);
        },
    });
    const retakesReview = useRetakesReviewWorkflow({
        getTranscript: assetStore.GetTranscript,
        postAiRetakes: assetStore.PostAiRetakes,
        onCloseSidebar: () => {
            setAiToolsView("list");
        },
    });
    const editForClarityReview = useEditForClarityReviewWorkflow({
        getTranscript: assetStore.GetTranscript,
        postAiRetakes: assetStore.PostAiRetakes,
        postAiFillerWords: assetStore.PostAiFillerWords,
        postAiEditForClarity: assetStore.PostAiEditForClarity,
        onCloseSidebar: () => {
            setAiToolsView("list");
        },
    });
    const openWorkflowGuidanceEditForClarity = useCallback(() => {
        setSidebarItemSelected(SidebarItems.AI_TOOLS);
        setAiToolsView("edit_for_clarity");
    }, []);
    const openWorkflowGuidancePropertiesClip = useCallback((clipId?: string | null) => {
        const safeClipId = String(clipId || "").trim();
        if (safeClipId) {
            try { ApplicationStore.setSelectedClipId(safeClipId); } catch {}
        }
        setSidebarItemSelected(SidebarItems.PROPERTIES);
    }, []);
    const openWorkflowGuidanceCreateClips = useCallback(() => {
        resetCreateClipsReview();
        setSidebarItemSelected(SidebarItems.AI_TOOLS);
        setAiToolsView("create_clips");
    }, [resetCreateClipsReview]);
    useEffect(() => {
        const projectId = (ProjectAssetsStore.projectId as string) || undefined;
        // Expose current project id globally for bridge fallback (best-effort)
        (window as any).__librettoProjectId = projectId;
        CompositionBridge.setTranscriptFetcher(async ({trackId, projectId: pid}) => {
            const pidEff = pid || projectId;
            if (!trackId || !pidEff) return undefined;
            try {
                // Look up track to get activeTranscriptId
                const track = ProjectAssetsStore.projectTracks?.find(
                    (t: any) => t.id === trackId || t.trackId === trackId
                );
                const transcriptId = track?.activeTranscriptId;
                if (!transcriptId) return undefined;
                return await assetStore.GetTranscript({transcriptId, projectId: pidEff});
            } catch {
                return undefined;
            }
        });
    }, [ProjectAssetsStore.projectId]);

    const decomposeClip = useDecomposeClipWorkflow({
        isEnabled: sidebarItemSelected === SidebarItems.AI_TOOLS || aiToolsView === "decompose_clip",
        selectedCompositionId,
        selectedCompositionTitle,
        getTranscript: assetStore.GetTranscript,
        resolveAssetUrl: ResolveAssetUrl,
        getEnhancedAudioUrl: GetEnhancedAudioUrl,
        uploadAsset: UploadAsset,
    });
    const isDecomposeSidebarVisible =
        sidebarItemSelected === SidebarItems.AI_TOOLS && aiToolsView === "decompose_clip";
    const showDecomposeActivityCard =
        decomposeClip.hasWorkflow && decomposeClip.isProcessing && !isDecomposeSidebarVisible;
    const decomposeActivityCardBottom =
        backgroundActivityCardBottom +
        (showBackgroundActivityCard ? 92 : 0) +
        (ExportStore.state.status !== "idle" ? 92 : 0);
    const { activeCard: activeWorkflowGuidanceCard, dismissCard: dismissWorkflowGuidanceCard } = useWorkflowGuidance({
        enabled: Boolean(isBeforeTrial) && !activationRunId,
        session: workflowGuidanceSession,
        projectTracks: ProjectAssetsStore.projectTracks || [],
        transcriptWords: ScriptLayerStore.transcriptWords || [],
        aiToolsView,
        editForClarityState: {
            isDetecting: editForClarityReview.isDetecting,
            hasRun: editForClarityReview.hasRun,
            draft: editForClarityReview.draft,
        },
        onOpenEditForClarityReview: openWorkflowGuidanceEditForClarity,
        onOpenPropertiesClip: openWorkflowGuidancePropertiesClip,
    });
    useCreateSocialClipsAutostart({
        session: workflowGuidanceSession,
        projectTracks: ProjectAssetsStore.projectTracks || [],
        transcriptWords: ScriptLayerStore.transcriptWords || [],
        onOpenCreateClips: openWorkflowGuidanceCreateClips,
    });

    const handleGenerateAiChapters = useCallback(
        async (desiredCount: number | null) => {
            if (isGeneratingChapters) return;
            setIsGeneratingChapters(true);
            setGenerateChaptersError(null);

            try {
                if (!CompositionService.isInitialized()) {
                    throw new Error("Composition is not ready yet.");
                }

                const projectId = String(ProjectAssetsStore.projectId ?? "").trim();
                if (!projectId) {
                    throw new Error("Missing project id.");
                }

                const { blocks } = await buildEditedTranscriptBlocksForChapters({
                    getTranscript: assetStore.GetTranscript,
                    projectId,
                });

                if (!blocks.length) {
                    throw new Error("Transcript is not ready yet.");
                }

                const res = await assetStore.PostAiChapters({
                    projectId,
                    desiredCount,
                    blocks: blocks.map((b) => ({ id: b.id, startSec: b.startSec, text: b.text })),
                    minSpacingSec: 30,
                    maxChapters: 30,
                });

                if (!res) {
                    throw new Error("Failed to generate chapters. Please try again.");
                }

                const chapters = Array.isArray(res?.chapters) ? res!.chapters : [];
                if (!chapters.length) {
                    throw new Error("No chapters were generated. Please try again.");
                }
                const blockById = new Map(blocks.map((b) => [b.id, b]));

                const seen = new Set<string>();
                const candidates: Array<{
                    timeSec: number;
                    title: string;
                    snippet: string;
                    afterWordPayload: any;
                    beforeWordPayload: any;
                }> = [];

                for (const ch of chapters) {
                    const blockId = String((ch as any)?.blockId ?? "").trim();
                    if (!blockId || seen.has(blockId)) continue;
                    seen.add(blockId);

                    const block = blockById.get(blockId);
                    if (!block) continue;

                    const t = Number(block.boundaryTimeSec ?? 0);
                    if (!Number.isFinite(t)) continue;

                    const title = String((ch as any)?.title ?? "").trim() || "Marker";
                    const snippet = String((block as any)?.text ?? "")
                        .replace(/\s+/g, " ")
                        .trim();
                    candidates.push({
                        timeSec: t,
                        title,
                        snippet,
                        afterWordPayload: block.afterWordPayload,
                        beforeWordPayload: block.beforeWordPayload,
                    });
                }

                candidates.sort((a, b) => a.timeSec - b.timeSec);

                // Enforce min spacing + max chapters.
                const filtered: typeof candidates = [];
                let lastT = -Infinity;
                const MIN_SPACING = 30;
                const MAX_CHAPTERS =
                    desiredCount != null && Number.isFinite(desiredCount)
                        ? Math.min(30, Math.max(2, Math.floor(desiredCount)))
                        : 30;
                for (const c of candidates) {
                    if (c.timeSec - lastT < MIN_SPACING) continue;
                    filtered.push(c);
                    lastT = c.timeSec;
                    if (filtered.length >= MAX_CHAPTERS) break;
                }

                setAiChaptersDraft(
                    filtered.map((m) => ({
                        title: m.title,
                        timeSec: m.timeSec,
                        snippet: m.snippet,
                        afterWordPayload: m.afterWordPayload,
                        beforeWordPayload: m.beforeWordPayload,
                    })),
                );
            } catch (e: any) {
                const msg = String(e?.message ?? e ?? "Failed to add chapters.");
                console.error("[V2Editor] Add chapters failed:", e);
                setGenerateChaptersError(msg);
            } finally {
                setIsGeneratingChapters(false);
            }
        },
        [assetStore, isGeneratingChapters],
    );

    const handleApplyAiChaptersDraft = useCallback(() => {
        if (!aiChaptersDraft.length) return;
        try {
            CompositionService.replaceTranscriptMarkersByOrigin({
                origin: "ai_chapters",
                markers: aiChaptersDraft.map((m) => ({
                    title: m.title,
                    timeSec: m.timeSec,
                    afterWordPayload: m.afterWordPayload,
                    beforeWordPayload: m.beforeWordPayload,
                })),
            });
            setAiChaptersDraft([]);
            setGenerateChaptersError(null);
            setAiToolsView("list");
        } catch (e) {
            console.error("[V2Editor] Failed to apply AI chapters", e);
        }
    }, [aiChaptersDraft]);

    const handleApplyAutomaticMulticam = useCallback(
        async (options: { style: "automatic" | "active_speaker_only"; cutaways: "occasional" | "frequent"; layoutPackId: string }) => {
            if (isApplyingMulticam) return;
            setIsApplyingMulticam(true);
            setApplyMulticamError(null);

            try {
                if (!CompositionService.isInitialized()) {
                    throw new Error("Composition is not ready yet.");
                }
                const projectId = String(ProjectAssetsStore.projectId ?? "").trim();
                if (!projectId) {
                    throw new Error("Missing project id.");
                }

                await applyAutomaticMulticam({
                    getTranscript: assetStore.GetTranscript,
                    projectId,
                    style: options.style,
                    cutaways: options.cutaways,
                    layoutPackId: options.layoutPackId,
                });

                setAiToolsView("list");
            } catch (e: any) {
                const msg = String(e?.message ?? e ?? "Failed to apply automatic multicam.");
                console.error("[V2Editor] Automatic multicam failed:", e);
                setApplyMulticamError(msg);
            } finally {
                setIsApplyingMulticam(false);
            }
        },
        [assetStore, isApplyingMulticam],
    );

    const handleRunEditForClarityCleanup = useCallback(
        async (settings: EditForClarityCleanupSettings) => {
            const normalizedSettings: EditForClarityCleanupSettings = {
                removeFillerWords: settings?.removeFillerWords !== false,
                tightenSilenceGaps: settings?.tightenSilenceGaps !== false,
                removeRetakes: settings?.removeRetakes !== false,
            };

            const yieldToEventLoop = () =>
                new Promise<void>((resolve) => {
                    if (typeof MessageChannel === "undefined") {
                        window.setTimeout(resolve, 0);
                        return;
                    }
                    const channel = new MessageChannel();
                    channel.port1.onmessage = () => resolve();
                    channel.port2.postMessage(null);
                });

            editForClarityCleanupStopRequestedRef.current = false;
            setEditForClarityCleanupStopRequested(false);
            setIsEditForClarityCleanupDialogOpen(true);
            const initial = createInitialEditForClarityCleanupProgress(normalizedSettings);
            setEditForClarityCleanupProgress(initial);

            await runEditForClarityCleanupStub({
                settings: normalizedSettings,
                onProgress: setEditForClarityCleanupProgress,
                isCancelled: () => editForClarityCleanupStopRequestedRef.current,
                executeRemoveRetakes: async (stepContext) => {
                    const currentProjectId = String(ProjectAssetsStore.projectId ?? "").trim();
                    if (!currentProjectId) {
                        return {
                            detail: "Missing project id",
                            metrics: { retakesDetected: 0 },
                        };
                    }

                    stepContext.throwIfCancelled();
                    const detection = await detectRetakes({
                        getTranscript: assetStore.GetTranscript,
                        postAiRetakes: assetStore.PostAiRetakes,
                        projectId: currentProjectId,
                        minConfidence: 0.9,
                        maxGapSec: 10,
                        maxCandidates: 40,
                        useAudioVeto: true,
                    });

                    const detections = Array.isArray(detection?.detections) ? detection.detections : [];
                    stepContext.updateMetrics({ retakesDetected: detections.length });
                    stepContext.throwIfCancelled();

                    if (!detections.length) {
                        return {
                            detail: "No retakes detected",
                            metrics: { retakesDetected: 0 },
                        };
                    }

                    trackSessionEvent("retake_removal");
                    const applied = await applyRetakeDetections({
                        detections,
                        transcriptMode: "retake-strikethrough",
                    });

                    const appliedCount = Number(applied?.appliedCount ?? 0);
                    return {
                        detail: `${appliedCount} ${appliedCount === 1 ? "retake" : "retakes"} marked`,
                        metrics: { retakesDetected: appliedCount },
                    };
                },
                executeRemoveFillerWords: async (stepContext) => {
                    await FillerWordStore.activateAsync(assetStore.GetTranscript, assetStore.PostAiFillerWords);
                    FillerWordStore.setCutMode("ignore");
                    FillerWordStore.selectAllCategories();
                    if (FillerWordStore.includeRepeatedWords) {
                        FillerWordStore.toggleRepeatedWords();
                    }
                    if (!FillerWordStore.avoidHarshCuts) {
                        FillerWordStore.toggleAvoidHarshCuts();
                    }
                    if (FillerWordStore.avoidHarshCuts) {
                        const analysisWaitStartedAt = Date.now();
                        while (FillerWordStore.isAnalyzing && !stepContext.isCancelled()) {
                            if (Date.now() - analysisWaitStartedAt > 45000) break;
                            await yieldToEventLoop();
                        }
                    }

                    const occurrences = FillerWordStore.getAllForRemoval();
                    const total = occurrences.length;
                    if (!total) {
                        FillerWordStore.deactivate();
                        return {
                            detail: "No filler words found",
                            metrics: { fillerWordsRemoved: 0 },
                        };
                    }

                    trackSessionEvent("filler_word_removal");
                    FillerWordStore.startRemoval();
                    CompositionService.beginScriptLayerBatch();
                    let processed = 0;
                    try {
                        const reversedOccurrences = [...occurrences].reverse();
                        for (const occurrence of reversedOccurrences) {
                            if (stepContext.isCancelled()) break;
                            const pad = occurrence.paddingSec;
                            let cutStart = occurrence.startSec - pad;
                            let cutEnd = occurrence.endSec + pad;

                            if (occurrence.prevWordEndSec !== null) {
                                cutStart = Math.max(cutStart, occurrence.prevWordEndSec);
                            }
                            if (occurrence.nextWordStartSec !== null) {
                                cutEnd = Math.min(cutEnd, occurrence.nextWordStartSec);
                            }

                            try {
                                await CompositionService.cutScriptLayerRangeWithAnalysis(
                                    cutStart,
                                    cutEnd,
                                    occurrence.sourceIndex,
                                    {
                                        transcriptMode: "strikethrough",
                                        prevWordEndSec: occurrence.prevWordEndSec,
                                        nextWordStartSec: occurrence.nextWordStartSec,
                                    },
                                );
                            } catch {
                                CompositionService.cutScriptLayerRange(cutStart, cutEnd, {
                                    transcriptMode: "strikethrough",
                                });
                            }

                            processed += 1;
                            FillerWordStore.updateRemovalProgress((processed / total) * 100);
                            stepContext.updateMetrics({ fillerWordsRemoved: processed });

                            if (processed % 8 === 0 || processed === total) {
                                await yieldToEventLoop();
                            }
                        }
                    } finally {
                        await CompositionService.endScriptLayerBatch();
                        FillerWordStore.finishRemoval();
                        FillerWordStore.deactivate();
                    }

                    stepContext.throwIfCancelled();
                    return {
                        detail: `${processed} ${processed === 1 ? "filler word" : "filler words"} removed`,
                        metrics: { fillerWordsRemoved: processed },
                    };
                },
                executeTightenSilenceGaps: async (stepContext) => {
                    const targetDuration = 2.5;
                    await GapStore.activateAsync(assetStore.GetTranscript);
                    await GapStore.setMinGapThreshold(targetDuration);
                    GapStore.setShortenToSeconds(targetDuration);

                    const gaps = GapStore
                        .getAllGaps()
                        .filter((gap) => Number(gap?.durationSec) > targetDuration);
                    const total = gaps.length;
                    if (!total) {
                        GapStore.deactivate();
                        return {
                            detail: "No long silences found",
                            metrics: { silenceSecondsRemoved: 0 },
                        };
                    }

                    trackSessionEvent("silence_removal");
                    GapStore.startRemoval();
                    CompositionService.beginScriptLayerBatch();
                    let processed = 0;
                    let removedSeconds = 0;
                    try {
                        const reversedGaps = [...gaps].reverse();
                        for (const gap of reversedGaps) {
                            if (stepContext.isCancelled()) break;
                            const currentDuration = Number(gap?.durationSec ?? 0);
                            if (!Number.isFinite(currentDuration) || currentDuration <= targetDuration) {
                                continue;
                            }

                            const keepAtStart = targetDuration / 2;
                            const keepAtEnd = targetDuration / 2;
                            const cutStart = Number(gap.startSec) + keepAtStart;
                            const cutEnd = Number(gap.endSec) - keepAtEnd;
                            if (Number.isFinite(cutStart) && Number.isFinite(cutEnd) && cutEnd > cutStart) {
                                CompositionService.cutScriptLayerRange(cutStart, cutEnd, {
                                    transcriptMode: "delete",
                                });
                                removedSeconds += currentDuration - targetDuration;
                            }

                            processed += 1;
                            GapStore.updateRemovalProgress((processed / total) * 100);
                            stepContext.updateMetrics({
                                silenceSecondsRemoved: Number(removedSeconds.toFixed(3)),
                            });
                            if (processed % 8 === 0 || processed === total) {
                                await yieldToEventLoop();
                            }
                        }
                    } finally {
                        await CompositionService.endScriptLayerBatch();
                        GapStore.finishRemoval();
                        GapStore.deactivate();
                    }

                    stepContext.throwIfCancelled();
                    return {
                        detail: `${Number(removedSeconds.toFixed(1))}s silence removed`,
                        metrics: {
                            silenceSecondsRemoved: Number(removedSeconds.toFixed(3)),
                        },
                    };
                },
            });
        },
        [assetStore],
    );

    const handleCloseEditForClarityCleanupDialog = useCallback(() => {
        setIsEditForClarityCleanupDialogOpen(false);
    }, []);

    const handleStopEditForClarityCleanup = useCallback(() => {
        editForClarityCleanupStopRequestedRef.current = true;
        setEditForClarityCleanupStopRequested(true);
    }, []);

	    // Choose which composition to open.
	    // Prefer per-user localStorage, then project-level currentCompositionId, then a deterministic fallback.
	    useEffect(() => {
	        const list: any[] = ProjectAssetsStore.projectCompositions || [];
	        if (!list || list.length === 0) {
	            setSelectedCompositionId(null);
	            setSelectedCompositionTitle(null);
	            return;
	        }

	        // If we just duplicated a composition and immediately selected it, the Firestore-backed
	        // compositions list may not include it yet. Don't clobber the selection while we wait.
	        const pendingDupId = pendingDuplicateRangeRef.current?.newCompositionId;
	        if (pendingDupId && selectedCompositionId === pendingDupId) return;

	        if (studioAutocreatePreferredCompositionId) {
	            const preferred = list.find((composition) => composition?.id === studioAutocreatePreferredCompositionId);
	            if (preferred) {
	                if (selectedCompositionId !== preferred.id) setSelectedCompositionId(preferred.id);
	                const nextTitle = preferred.title ?? preferred.name ?? null;
	                if (selectedCompositionTitle !== nextTitle) setSelectedCompositionTitle(nextTitle);
	                return;
	            }
	        }

	        // Preserve an explicit in-session selection.
	        if (selectedCompositionId && list.some((c) => c?.id === selectedCompositionId)) return;

	        const pid = String(projectId ?? "").trim();
	        const uid = String(authContext.authState?.userId ?? authContext.getUserId?.() ?? "").trim();

	        // 1) Per-user persisted selection (client-only).
	        if (pid && uid) {
	            const localKey = getCurrentCompositionLocalStorageKey({ userId: uid, projectId: pid });
	            const localId = String(safeLocalStorageGetItem(localKey) ?? "").trim();
	            if (localId) {
	                const localPreferred = list.find((c) => c?.id === localId);
	                if (localPreferred) {
	                    setSelectedCompositionId(localPreferred.id);
	                    setSelectedCompositionTitle(localPreferred.title ?? localPreferred.name ?? null);
	                    return;
	                }
	            }
	        }

	        // 2) Project-level preferred selection (shared).
	        const projectPreferredId = String(ProjectAssetsStore.projectCurrentCompositionId ?? "").trim();
	        if (projectPreferredId) {
	            const projectPreferred = list.find((c) => c?.id === projectPreferredId);
	            if (projectPreferred) {
	                setSelectedCompositionId(projectPreferred.id);
	                setSelectedCompositionTitle(projectPreferred.title ?? projectPreferred.name ?? null);
	                return;
	            }
	        }

	        // 3) Deterministic fallback (newest updatedAt/createdAt).
	        const getSortKey = (c: any) => {
	            const updatedAt = Number(c?.updatedAt ?? c?.updateTime ?? c?.updated_at ?? NaN);
	            if (Number.isFinite(updatedAt) && updatedAt > 0) return updatedAt;
	            const createdAt = Number(c?.createdAt ?? c?.createTime ?? c?.created_at ?? NaN);
	            if (Number.isFinite(createdAt) && createdAt > 0) return createdAt;
	            return 0;
	        };
	        const sorted = [...list].sort((a, b) => getSortKey(b) - getSortKey(a));
	        const fallback = sorted[0] ?? list[0];
	        setSelectedCompositionId(fallback.id);
	        setSelectedCompositionTitle(fallback.title ?? fallback.name ?? null);
	    }, [
	        ProjectAssetsStore.projectCompositions,
	        ProjectAssetsStore.projectCurrentCompositionId,
	        studioAutocreatePreferredCompositionId,
	        selectedCompositionId,
	        selectedCompositionTitle,
	        projectId,
	        authContext.authState?.userId,
	    ]);

    // Persist current composition selection:
    // - per-user in localStorage
    // - project-level on the Firestore project doc (best-effort)
    const persistCurrentCompositionTimerRef = useRef<number | null>(null);
    const lastPersistedProjectCompositionRef = useRef<string | null>(null);
    useEffect(() => {
        const pid = String(projectId ?? "").trim();
        const compId = String(selectedCompositionId ?? "").trim();
        if (!pid || !compId) return;

        const uid = String(authContext.authState?.userId ?? authContext.getUserId?.() ?? "").trim();
        if (!uid) return;

        // Local, per-user resume state.
        safeLocalStorageSetItem(getCurrentCompositionLocalStorageKey({ userId: uid, projectId: pid }), compId);

        // Avoid repeated writes for the same selection in a session.
        const persistedKey = `${pid}:${compId}`;
        if (lastPersistedProjectCompositionRef.current === persistedKey) return;

        if (persistCurrentCompositionTimerRef.current) {
            window.clearTimeout(persistCurrentCompositionTimerRef.current);
        }

        persistCurrentCompositionTimerRef.current = window.setTimeout(() => {
            void (async () => {
                try {
                    if (String(ProjectAssetsStore.projectCurrentCompositionId ?? "").trim() === compId) {
                        lastPersistedProjectCompositionRef.current = persistedKey;
                        return;
                    }
                    const projectRef = doc(db, kProjectsCollectionName, pid);
                    await updateDoc(projectRef, { currentCompositionId: compId });
                    lastPersistedProjectCompositionRef.current = persistedKey;
                } catch (e) {
                    console.warn("[V2Editor] Failed to persist currentCompositionId on project", e);
                }
            })();
        }, 400);

        return () => {
            if (persistCurrentCompositionTimerRef.current) {
                window.clearTimeout(persistCurrentCompositionTimerRef.current);
                persistCurrentCompositionTimerRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCompositionId, projectId, authContext.authState?.userId]);

    // Apply pending "duplicate range" trim after the duplicated composition is restored.
    useEffect(() => {
        if (isRestoringComposition) return;
        const pending = pendingDuplicateRangeRef.current;
        if (!pending) return;
        if (!selectedCompositionId || pending.newCompositionId !== selectedCompositionId) return;
        if (!CompositionService.isInitialized()) return;

        pendingDuplicateRangeRef.current = null;
        try {
            CompositionService.trimCompositionToRange(pending.rangeStartSec, pending.rangeEndSec);
            CompositionService.seek(0);
        } catch (e) {
            console.error("[V2Editor] Failed to trim duplicated composition to range", e);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRestoringComposition, selectedCompositionId]);

	    // Keep local empty-state flag in sync for export button disabling.
	    useEffect(() => {
	        const disposer = reaction(
	            () => CompositionStateStore.isCompositionEmpty,
	            (empty) => setIsSelectedCompositionEmpty(Boolean(empty)),
	            { fireImmediately: true }
	        );
	        return () => disposer();
	        // eslint-disable-next-line react-hooks/exhaustive-deps
	    }, []);

        // When focus analysis arrives after an auto-default placement, re-apply placement once
        // (only if the user hasn't edited the script clip visuals).
        useEffect(() => {
            const disposer = reaction(
                () => FocusStore.version,
                () => {
                    try { CompositionService.maybeReapplyDefaultScriptClipPlacementFromFocus(); } catch {}
                }
            );
            return () => disposer();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

    // When a recording asset appears as a track, auto-create a script clip and add to composition
    useEffect(() => {
        if (compositionEditingDisabled) return;
        if (!pendingRecordingAssetIds.length) return;
        if (isRestoringComposition) return;
        if (!CompositionService.isInitialized()) return;
        
        const tracks = ProjectAssetsStore.projectTracks || [];
        const nextPending = pendingRecordingAssetIds.find((p) => 
            tracks.some((t: any) => t.assetId === p.assetId)
        );
        if (!nextPending) return;

        const tr = tracks.find((t: any) => t.assetId === nextPending.assetId);
        if (!tr) return;

        if (!isTrackUsableForMediaInsertion(tr, localTrackAvailability)) {
            // Wait until the same "usable" condition as V2ProjectSidebar is met.
            return;
        }

        const inflightKey = `${selectedCompositionId || 'none'}:${nextPending.assetId}`;
        if (pendingRecordingInFlightRef.current.has(inflightKey)) return;
        pendingRecordingInFlightRef.current.add(inflightKey);

        void (async () => {
            try {
                const trackId = String(tr?.trackId || tr?.id || "").trim();
                if (!trackId) {
                    console.warn("[V2Editor] Pending recording track is missing an id");
                    return;
                }
                const assetTitle = tr.title || '';
                const isVideo = IsVideoTrackType(tr.trackType);
                const url = await GetTrackUrl({ trackId });
                if (!url) {
                    // Track exists but source URL may not be ready yet (e.g. fresh upload).
                    // Keep pending so later track updates can retry autoinsert.
                    return;
                }

                const scriptLayerClip = await insertTrackIntoScriptLayer({
                    trackId,
                    trackType: isVideo ? "video" : "audio",
                    trackTitle: assetTitle,
                    trackResolution: String(tr.resolution || ""),
                    resolveTrackUrl: async () => url,
                });

	                if (scriptLayerClip) {
	                    console.log('[V2Editor] Added recording script layer clip:', scriptLayerClip.id);

                        if (nextPending.workflowGuidanceType) {
                            setWorkflowGuidanceSession({
                                key: `${String(projectId || ProjectAssetsStore.projectId || "").trim()}:${nextPending.workflowGuidanceType}:${nextPending.assetId}`,
                                workflow: nextPending.workflowGuidanceType,
                                projectId: String(projectId || ProjectAssetsStore.projectId || "").trim(),
                                assetId: nextPending.assetId,
                                trackId,
                                clipId: String((scriptLayerClip as any).id || "").trim() || null,
                            });
                        }

	                    if (nextPending.editForClaritySettings) {
                            setPendingEditForClarityAutoRuns((prev) => {
                                if (prev.some((item) => item.assetId === nextPending.assetId || item.trackId === trackId)) {
                                    return prev;
                                }
                                return [
                                    ...prev,
                                    {
                                        assetId: nextPending.assetId,
                                        trackId,
                                        intensity: nextPending.editForClaritySettings.intensity,
                                    },
                                ];
                            });
	                    }

                        if (nextPending.openPropertiesSidebarOnInsert) {
                            if (nextPending.autoEnableStudioSoundOnInsert) {
                                try { ApplicationStore.setPendingStudioSoundAutoEnableClipId(String((scriptLayerClip as any).id)); } catch {}
                            }
                            try { ApplicationStore.setSelectedClipId(String((scriptLayerClip as any).id)); } catch {}
                            setSidebarItemSelected(SidebarItems.PROPERTIES);
                        }

                        if (nextPending.openCaptionsSidebarOnInsert) {
                            setSidebarItemSelected(SidebarItems.CAPTIONS);
                        }
	                }

                setPendingRecordingAssetIds((prev) => prev.filter((p) => p.assetId !== nextPending.assetId));
            } catch (e) {
                console.error("[V2Editor] Failed to auto-create script clip from recording:", e);
                setPendingRecordingAssetIds((prev) => prev.filter((p) => p.assetId !== nextPending.assetId));
            } finally {
                pendingRecordingInFlightRef.current.delete(inflightKey);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        pendingRecordingAssetIds,
        ProjectAssetsStore.projectTracks,
        localTrackAvailability,
        GetTrackUrl,
        isRestoringComposition,
        projectId,
        selectedCompositionId,
        compositionEditingDisabled,
    ]);

    useEffect(() => {
        if (!pendingEditForClarityAutoRuns.length) return;

        const projectTracks = ProjectAssetsStore.projectTracks || [];
        const transcriptWords = Array.isArray(ScriptLayerStore.transcriptWords)
            ? ScriptLayerStore.transcriptWords
            : [];

        for (const pending of pendingEditForClarityAutoRuns) {
            const pendingAssetId = String(pending?.assetId || "").trim();
            const pendingTrackId = String(pending?.trackId || "").trim();
            if (!pendingAssetId && !pendingTrackId) continue;

            const track = projectTracks.find((item: any) => {
                const assetId = String(item?.assetId || "").trim();
                const trackId = String(item?.trackId || item?.id || "").trim();
                return assetId === pendingAssetId || trackId === pendingTrackId;
            });
            if (!track) continue;

            if (isTranscriptionTerminalWithoutTranscript(track?.transcriptionState)) {
                setPendingEditForClarityAutoRuns((prev) =>
                    prev.filter((item) => String(item?.trackId || "").trim() !== pendingTrackId)
                );
                return;
            }

            if (!isTranscriptionCompleted(track?.transcriptionState)) {
                continue;
            }

            const resolvedTrackId = String(track?.trackId || track?.id || pendingTrackId || "").trim();
            if (!resolvedTrackId) continue;

            const hasTranscriptWords = transcriptWords.some(
                (word: any) => String(word?.sourceAssetId || "").trim() === resolvedTrackId
            );
            if (!hasTranscriptWords) {
                continue;
            }

            if (pendingEditForClarityAutoRunInFlightRef.current.has(resolvedTrackId)) {
                return;
            }

            pendingEditForClarityAutoRunInFlightRef.current.add(resolvedTrackId);
            setPendingEditForClarityAutoRuns((prev) =>
                prev.filter((item) => String(item?.trackId || "").trim() !== pendingTrackId)
            );

            editForClarityReview.reset();
            setSidebarItemSelected(SidebarItems.AI_TOOLS);
            setAiToolsView("edit_for_clarity");

            void Promise.resolve(editForClarityReview.detect(pending.intensity)).finally(() => {
                pendingEditForClarityAutoRunInFlightRef.current.delete(resolvedTrackId);
            });
            return;
        }
    }, [
        pendingEditForClarityAutoRuns,
        ProjectAssetsStore.projectTracks,
        ScriptLayerStore.transcriptWords,
        editForClarityReview,
    ]);

    // When a screen recording session's tracks appear, create a Sequence from them and auto-insert a screen-recording script clip.
    useEffect(() => {
        if (compositionEditingDisabled) return;
        if (!pendingScreenRecordingSessions.length) return;
        if (isRestoringComposition) return;
        if (!CompositionService.isInitialized()) return;
        if (!projectId) return;

        const tracks = ProjectAssetsStore.projectTracks || [];
        const findTrackByAssetId = (assetId?: string | null) => {
            if (!assetId) return undefined;
            return tracks.find((t: any) => t.assetId === assetId);
        };

        const nextPending = pendingScreenRecordingSessions.find((s) => {
            const screenTr = findTrackByAssetId(s.screenAssetId);
            const secondaryTr = findTrackByAssetId(s.secondaryAssetId);
            return Boolean(screenTr) && (!s.secondaryAssetId || Boolean(secondaryTr));
        });
        if (!nextPending) return;

        const screenTr = findTrackByAssetId(nextPending.screenAssetId);
        const secondaryTr = findTrackByAssetId(nextPending.secondaryAssetId);
        if (!screenTr) return;

        const sessionKey = `${nextPending.screenAssetId}:${nextPending.secondaryAssetId || ''}`;

        const inflightKey = `${selectedCompositionId || 'none'}:${sessionKey}`;
        if (pendingScreenRecordingInFlightRef.current.has(inflightKey)) return;
        pendingScreenRecordingInFlightRef.current.add(inflightKey);

        void (async () => {
            let shouldRemovePending = false;
            try {
                // Map assetIds -> trackIds (audio/video URLs resolve by trackId).
                const screenTrackId = String(screenTr.trackId || "");
                const secondaryTrackId = secondaryTr?.trackId ? String(secondaryTr.trackId) : "";
                const secondaryIsVideo = Boolean(secondaryTr && IsVideoTrackType(secondaryTr.trackType));
                if (!screenTrackId) {
                    console.warn('[V2Editor] Screen recording: missing screen trackId');
                    return;
                }

                const displayName = authContext.getUserDisplayName?.() || '';
                const firstName = displayName.split(' ')[0] || '';
                const baseName = firstName || displayName;
                const sequenceTitle = baseName ? `${baseName} screen recording` : 'Screen recording';

                const insertedAlready = insertedScreenRecordingSessionsRef.current.has(sessionKey);
                if (!insertedAlready) {
                    // Wait until the track URLs can be resolved (server may still be processing).
                    const screenUrl = await GetTrackUrl({ trackId: screenTrackId });
                    if (!screenUrl) {
                        console.warn('[V2Editor] Screen recording: could not resolve screen track URL');
                    } else {
                        const secondaryUrl = secondaryTr && secondaryTrackId ? await GetTrackUrl({ trackId: secondaryTrackId }) : null;
                        if (secondaryTr && secondaryTrackId && !secondaryUrl) {
                            console.warn('[V2Editor] Screen recording: could not resolve secondary track URL');
                        } else {
                            // 1) Create the screen-recording Script clip and add it to the composition (progressive population)
                            const comp = CompositionService.composition;

                            // First VIDEO sets the composition canvas resolution (and thus max export resolution).
                            let screenW = 0;
                            let screenH = 0;
                            const resMat = String(screenTr.resolution ?? '').match(/(\d+)x(\d+)/);
                            if (resMat) {
                                screenW = parseInt(resMat[1], 10);
                                screenH = parseInt(resMat[2], 10);
                                if (screenW > 0 && screenH > 0) {
                                    maybeLockCanvasToFirstVideo(comp, screenW, screenH);
                                }
                            }

                            const compWidth = comp?.width ?? 1920;
                            const compHeight = comp?.height ?? 1080;

                            const screenInput = new Input({ source: new UrlSource(screenUrl), formats: ALL_FORMATS });

                            const fittedScreen = fitToCanvas(screenW || compWidth, screenH || compHeight, compWidth, compHeight);

                            const inputConfigs: ScriptLayerInputConfig[] = [
                                {
                                    input: screenInput,
                                    assetId: screenTrackId,
                                    name: '',
                                    color: '#3B82F6',
                                    gain: 1.0,
                                    layerIndex: 0,
                                    visual: {
                                        x: compWidth / 2,
                                        y: compHeight / 2,
                                        width: fittedScreen.width,
                                        height: fittedScreen.height,
                                        alpha: 1.0,
                                    },
                                },
                            ];

		                            if (secondaryTr && secondaryTrackId && secondaryUrl) {
		                                const secondaryInput = new Input({ source: new UrlSource(secondaryUrl), formats: ALL_FORMATS });

		                                const secondaryConfig: ScriptLayerInputConfig = {
		                                    input: secondaryInput,
		                                    assetId: secondaryTrackId,
	                                    name: '',
	                                    color: '#3B82F6',
	                                    gain: 1.0,
	                                    layerIndex: 1,
	                                };

			                                if (secondaryIsVideo) {
			                                    const secondaryResMat = String(secondaryTr?.resolution ?? '').match(/(\d+)x(\d+)/);
			                                    const secondaryW = secondaryResMat ? parseInt(secondaryResMat[1], 10) : 0;
			                                    const secondaryH = secondaryResMat ? parseInt(secondaryResMat[2], 10) : 0;
			                                    if (secondaryW > 0 && secondaryH > 0) {
			                                        const fittedSecondary = fitToCanvas(secondaryW, secondaryH, compWidth, compHeight);
			                                        secondaryConfig.visual = {
			                                            x: compWidth / 2,
			                                            y: compHeight / 2,
			                                            width: fittedSecondary.width,
			                                            height: fittedSecondary.height,
			                                            alpha: 1.0,
			                                        };
			                                    }
			                                }

	                                inputConfigs.push(secondaryConfig);
	                            }

                            const scriptLayerClip = await CreateScriptClipForComposition({
                                inputConfigs,
                                composition: comp,
                                stateStore: CompositionStateStore,
                                clipName: sequenceTitle,
                            });

                            if (!scriptLayerClip) {
                                console.warn('[V2Editor] Screen recording: failed to create script clip');
                            } else {
                                if (screenW > 0 && screenH > 0) {
                                    try {
                                        CompositionStateStore.setOriginalMediaResolutionForClip(scriptLayerClip.id, screenW, screenH);
                                    } catch {}
                                }

		                                const addedScriptClip: any = await CompositionService.addToScriptLayer(scriptLayerClip);
		                                // For the screen+webcam recording case, apply the actual Screen layout rather than
		                                // forcing a PiP transform here. This avoids "stacked layout" behavior later when a user
		                                // explicitly applies Screen.
		                                try {
		                                    if (secondaryIsVideo) {
		                                        LayoutService.applySceneLayout(0, "screen");
		                                    }
		                                } catch {}
		                                console.log('[V2Editor] Added screen recording script layer clip:', addedScriptClip?.id ?? scriptLayerClip.id);
		                                insertedScreenRecordingSessionsRef.current.add(sessionKey);
	                            }
                        }
                    }
                }

                // 2) Ensure a Sequence exists and is populated for these assets (idempotent)
                const getTrackDurationSec = (tr: any): number | null => {
                    const raw = tr?.duration ?? tr?.Duration ?? tr?.durationSec ?? tr?.durationSecs ?? tr?.media?.durationSec;
                    const val = typeof raw === 'number' ? raw : Number(raw);
                    if (!Number.isFinite(val) || val <= 0) return null;
                    return val;
                };

                const userId = authContext.getUserId();
                const workspaceId =
                    authContext.getCurrentWorkspaceId?.() ||
                    authContext.getDefaultWorkspaceId?.() ||
                    userId ||
                    null;

                let seqState = screenRecordingSequenceStateRef.current.get(sessionKey);
                if (!seqState) {
                    const seqId = await assetStore.CreateSequence({ projectId: projectId as string, title: sequenceTitle });
                    if (!seqId) {
                        console.warn('[V2Editor] Screen recording: failed to create sequence');
                        return;
                    }
                    seqState = {
                        sequenceId: seqId,
                        screenLayerId: crypto.randomUUID(),
                        secondaryLayerId: secondaryTr ? crypto.randomUUID() : null,
                        screenClipId: crypto.randomUUID(),
                        secondaryClipId: secondaryTr ? crypto.randomUUID() : null,
                        populated: false,
                    };
                    screenRecordingSequenceStateRef.current.set(sessionKey, seqState);
                }

	                if (!seqState.populated) {
	                    const writeUserId = userId || authContext.getUserId();
	                    if (!writeUserId) {
	                        console.warn('[V2Editor] Screen recording: missing userId for sequence persistence');
	                        return;
	                    }

	                    const nowSec = Math.floor(Date.now() / 1000);
	                    const orderedLayerIds = seqState.secondaryLayerId
	                        ? [seqState.secondaryLayerId, seqState.screenLayerId]
	                        : [seqState.screenLayerId];

	                    const seqRef = doc(db, kSequencesCollectionName, seqState.sequenceId);
	                    try {
	                        await updateDoc(seqRef, {
	                            updatedAt: nowSec,
	                            orderedLayerIds,
	                        });
	                    } catch {
	                        await setDoc(
	                            seqRef,
	                            {
	                                sequenceId: seqState.sequenceId,
	                                title: sequenceTitle,
	                                userId: writeUserId,
	                                ...(workspaceId ? { workspaceId } : {}),
	                                projectId: projectId as string,
	                                deleted: false,
	                                createdAt: nowSec,
	                                updatedAt: nowSec,
	                                orderedLayerIds,
	                            },
	                            { merge: true }
	                        );
	                    }

	                    const screenLayerRef = doc(seqRef, kSequenceLayersSubcollectionName, seqState.screenLayerId);
	                    await setDoc(
	                        screenLayerRef,
	                        {
	                            id: seqState.screenLayerId,
	                            sequenceId: seqState.sequenceId,
	                            userId: writeUserId,
	                            ...(workspaceId ? { workspaceId } : {}),
	                            name: 'Screen',
	                            createdAt: nowSec,
	                            updatedAt: nowSec,
	                            orderedClipIds: [seqState.screenClipId],
	                        },
	                        { merge: true }
	                    );

	                    const screenDurationSec = getTrackDurationSec(screenTr) ?? 0;
	                    const screenClipRef = doc(screenLayerRef, kSequenceLayerClipsSubcollectionName, seqState.screenClipId);
	                    await setDoc(
	                        screenClipRef,
	                        {
	                            id: seqState.screenClipId,
	                            sequenceId: seqState.sequenceId,
	                            layerId: seqState.screenLayerId,
	                            userId: writeUserId,
	                            ...(workspaceId ? { workspaceId } : {}),
	                            createdAt: nowSec,
	                            updatedAt: nowSec,
	                            type: 'video',
	                            assetId: screenTrackId,
	                            name: screenTr.title || '',
	                            startSec: 0,
	                            inSec: 0,
	                            outSec: screenDurationSec,
	                            offsetSec: 0,
	                            durationSec: screenDurationSec,
	                            trimmedDurationSec: screenDurationSec,
	                            props: { playbackRate: 1, gain: 1, alpha: 1 },
	                            media: { hasAudioStream: Boolean(screenTr.hasAudioStream) },
	                            waveformColor: null,
	                        },
	                        { merge: true }
	                    );

	                    if (seqState.secondaryLayerId && secondaryTr && seqState.secondaryClipId) {
	                        const secondaryIsVideo = IsVideoTrackType(secondaryTr.trackType);
	                        const secondaryLayerRef = doc(seqRef, kSequenceLayersSubcollectionName, seqState.secondaryLayerId);
	                        await setDoc(
	                            secondaryLayerRef,
	                            {
	                                id: seqState.secondaryLayerId,
	                                sequenceId: seqState.sequenceId,
	                                userId: writeUserId,
	                                ...(workspaceId ? { workspaceId } : {}),
	                                name: secondaryIsVideo ? 'Camera' : 'Audio',
	                                createdAt: nowSec,
	                                updatedAt: nowSec,
	                                orderedClipIds: [seqState.secondaryClipId],
	                            },
	                            { merge: true }
	                        );

	                        const secondaryDurationSec = getTrackDurationSec(secondaryTr) ?? 0;
	                        const secondaryClipRef = doc(secondaryLayerRef, kSequenceLayerClipsSubcollectionName, seqState.secondaryClipId);
	                        await setDoc(
	                            secondaryClipRef,
	                            {
	                                id: seqState.secondaryClipId,
	                                sequenceId: seqState.sequenceId,
	                                layerId: seqState.secondaryLayerId,
	                                userId: writeUserId,
	                                ...(workspaceId ? { workspaceId } : {}),
	                                createdAt: nowSec,
	                                updatedAt: nowSec,
	                                type: secondaryIsVideo ? 'video' : 'audio',
	                                assetId: secondaryTrackId,
	                                name: secondaryTr.title || '',
	                                startSec: 0,
	                                inSec: 0,
	                                outSec: secondaryDurationSec,
	                                offsetSec: 0,
	                                durationSec: secondaryDurationSec,
	                                trimmedDurationSec: secondaryDurationSec,
	                                props: { playbackRate: 1, gain: 1, alpha: 1 },
	                                media: { hasAudioStream: Boolean(secondaryTr.hasAudioStream) },
	                                waveformColor: null,
	                            },
	                            { merge: true }
	                        );
	                    }

	                    seqState.populated = true;
	                }
                shouldRemovePending = insertedScreenRecordingSessionsRef.current.has(sessionKey) && Boolean(seqState?.populated);
	            } catch (e) {
	                console.error('[V2Editor] Failed to create screen recording sequence + script clip:', e);
	            } finally {
                if (shouldRemovePending) {
                    setPendingScreenRecordingSessions((prev) =>
                        prev.filter((s) => s.screenAssetId !== nextPending.screenAssetId)
                    );
                }
                pendingScreenRecordingInFlightRef.current.delete(inflightKey);
            }
        })();
    }, [
        pendingScreenRecordingSessions,
        ProjectAssetsStore.projectTracks,
        GetTrackUrl,
        isRestoringComposition,
        selectedCompositionId,
        projectId,
        authContext,
        assetStore,
        compositionEditingDisabled,
    ]);

    const createStudioSequenceAndScriptClip = useCallback(async (readyTracks: any[]) => {
        if (!readyTracks.length) {
            throw new Error("No ready participant tracks found for this recording.");
        }

        const projectIdForSequence = String(projectId || ProjectAssetsStore.projectId || "");
        if (!projectIdForSequence) {
            throw new Error("Missing project context for sequence creation.");
        }

        const sequenceTitle = (selectedCompositionTitle?.trim() || "Studio recording");
        const newSequenceId = await CreateSequence({ projectId: projectIdForSequence, title: sequenceTitle });
        if (!newSequenceId) {
            throw new Error("Failed to create sequence.");
        }

        const userId = authContext.getUserId();
        const workspaceId =
            authContext.getCurrentWorkspaceId?.() ||
            authContext.getDefaultWorkspaceId?.() ||
            userId ||
            null;
        if (!userId) {
            throw new Error("Missing user identity for sequence persistence.");
        }

        const layerNames = readyTracks.map((track: any, index: number) =>
            String(track?.title || track?.name || `Track ${index + 1}`),
        );
        const result = await populateSequenceFromTracks({
            sequenceId: newSequenceId,
            projectId: projectIdForSequence,
            userId,
            workspaceId,
            tracks: readyTracks,
            layerNames,
        });
        if (!result.orderedLayerIds.length) {
            throw new Error("Could not resolve participant track IDs for this recording.");
        }

        if (!CompositionService.isInitialized()) {
            throw new Error("Composition is still loading. Please try again.");
        }

        const comp = CompositionService.composition;
        const firstVideoTrack = readyTracks.find((track: any) => IsVideoTrackType(track?.trackType));
        if (firstVideoTrack) {
            const match = String(firstVideoTrack?.resolution ?? "").match(/(\d+)x(\d+)/);
            if (match) {
                const width = parseInt(match[1], 10);
                const height = parseInt(match[2], 10);
                if (width > 0 && height > 0) {
                    maybeLockCanvasToFirstVideo(comp, width, height);
                }
            }
        }

        const compWidth = comp?.width ?? 1920;
        const compHeight = comp?.height ?? 1080;
        const inputConfigs: ScriptLayerInputConfig[] = [];

        const parseRecordingStartMs = (value: unknown): number | null => {
            const parsed = typeof value === "number" ? value : Number(value);
            return Number.isFinite(parsed) ? parsed : null;
        };
        const startTimes = readyTracks
            .map((track: any) => parseRecordingStartMs(track?.recordingStartMs))
            .filter((value): value is number => value !== null);
        const earliestRecordingStartMs = startTimes.length > 0 ? Math.min(...startTimes) : null;

        for (let index = 0; index < readyTracks.length; index += 1) {
            const track = readyTracks[index];
            const trackId = String(track?.trackId || track?.id || "");
            if (!trackId) continue;

            const isVideo = IsVideoTrackType(track?.trackType);
            const url = await GetTrackUrl({ trackId });
            if (!url) {
                throw new Error(`Track URL unavailable for ${layerNames[index] || "track"}.`);
            }

            const input = new Input({ source: new UrlSource(url), formats: ALL_FORMATS });
            const recordingStartMs = parseRecordingStartMs(track?.recordingStartMs);
            const offsetSec = earliestRecordingStartMs !== null && recordingStartMs !== null
                ? Math.max(0, (recordingStartMs - earliestRecordingStartMs) / 1000)
                : 0;

            const config: ScriptLayerInputConfig = {
                input,
                assetId: trackId,
                name: layerNames[index] || `Track ${index + 1}`,
                color: "#3B82F6",
                gain: 1.0,
                layerIndex: index,
                offsetSec,
            };

            if (isVideo) {
                const match = String(track?.resolution ?? "").match(/(\d+)x(\d+)/);
                const width = match ? parseInt(match[1], 10) : 0;
                const height = match ? parseInt(match[2], 10) : 0;
                const fitted = fitToCanvas(width || compWidth, height || compHeight, compWidth, compHeight);
                config.visual = {
                    x: compWidth / 2,
                    y: compHeight / 2,
                    width: fitted.width,
                    height: fitted.height,
                    alpha: 1.0,
                };
            }

            inputConfigs.push(config);
        }

        if (!inputConfigs.length) {
            throw new Error("No ready track inputs available for script clip creation.");
        }

        const scriptLayerClip = await CreateScriptClipForComposition({
            inputConfigs,
            composition: comp,
            stateStore: CompositionStateStore,
            clipName: sequenceTitle,
        });
        if (!scriptLayerClip) {
            throw new Error("Failed to create script clip from studio recording.");
        }

        await CompositionService.addToScriptLayer(scriptLayerClip);
    }, [
        CreateSequence,
        GetTrackUrl,
        authContext,
        projectId,
        selectedCompositionTitle,
    ]);

    useEffect(() => {
        if (compositionEditingDisabled) return;
        if (!studioAutocreateSession) return;
        if (studioAutocreateSession.phase !== "waiting") return;
        if (studioAutocreateTrackStatuses.length === 0) return;
        if (studioAutocreateHasFailures) return;
        if (studioAutocreateReadyCount < studioAutocreateTrackStatuses.length) return;
        if (isRestoringComposition) return;
        if (!CompositionService.isInitialized()) return;

        if (selectedCompositionId !== studioAutocreateSession.originCompositionId) {
            setSelectedCompositionId(studioAutocreateSession.originCompositionId);
            return;
        }

        const sessionKey = studioAutocreateSession.sessionKey;
        if (studioAutocreateInFlightRef.current.has(sessionKey)) return;
        studioAutocreateInFlightRef.current.add(sessionKey);
        setStudioAutocreateSession((prev) => {
            if (!prev || prev.sessionKey !== sessionKey) return prev;
            return { ...prev, phase: "creating", errorMessage: null };
        });

        const tracks = studioAutocreateTrackStatuses.map((status) => status.track);
        void (async () => {
            try {
                await createStudioSequenceAndScriptClip(tracks);
                setStudioAutocreateSession((prev) => (prev?.sessionKey === sessionKey ? null : prev));
                setStudioAutocreatePreferredCompositionId((prev) =>
                    prev === studioAutocreateSession.originCompositionId ? null : prev
                );
            } catch (error) {
                console.error("[V2Editor] Failed to auto-create sequence/script from studio recording:", error);
                const message = error instanceof Error && error.message
                    ? error.message
                    : "Failed to create composition from studio recording.";
                setStudioAutocreateSession((prev) => {
                    if (!prev || prev.sessionKey !== sessionKey) return prev;
                    return { ...prev, phase: "error", errorMessage: message };
                });
            } finally {
                studioAutocreateInFlightRef.current.delete(sessionKey);
            }
        })();
    }, [
        studioAutocreateSession,
        studioAutocreateTrackStatuses,
        studioAutocreateReadyCount,
        studioAutocreateHasFailures,
        isRestoringComposition,
        selectedCompositionId,
        createStudioSequenceAndScriptClip,
        compositionEditingDisabled,
    ]);

    const handleCancelStudioAutocreate = useCallback(() => {
        if (!studioAutocreateSession) return;
        if (studioAutocreateSession.phase === "creating") return;
        setStudioAutocreateSession(null);
        setStudioAutocreatePreferredCompositionId(null);
    }, [studioAutocreateSession]);

    const handleTranscriptExporterReady = useCallback((builder: TranscriptExporter | null) => {
        transcriptExporterRef.current = builder;
    }, []);

    const appendTranscriptToken = useCallback((line: string, token: string): string => {
        const trimmed = String(token ?? "").trim();
        if (!trimmed) return line;
        if (!line) return trimmed;
        if (/^[.,!?;:)\]%]/.test(trimmed)) return `${line}${trimmed}`;
        return `${line} ${trimmed}`;
    }, []);

    const buildFallbackTranscriptText = useCallback((options: TranscriptExportOptions): string => {
        const words = [...(ScriptLayerStore.transcriptWords ?? [])]
            .filter((word: any) => {
                const t = Number(word?.startSec ?? word?.endSec ?? 0);
                if (!Number.isFinite(t)) return false;
                return options.includeIgnoredText || !ScriptLayerStore.isTimeInStrikethroughCut(t);
            })
            .sort((a: any, b: any) => Number(a?.startSec ?? 0) - Number(b?.startSec ?? 0));

        const markers = options.includeMarkers
            ? [...(Array.isArray(ScriptLayerStore.transcriptMarkers) ? (ScriptLayerStore.transcriptMarkers as any[]) : [])]
                .sort((a, b) => Number(a?.timeSec ?? 0) - Number(b?.timeSec ?? 0))
            : [];

        const entries: Array<{ type: "speaker" | "marker" | "text"; text: string }> = [];
        let markerIdx = 0;
        let currentSpeaker = "";
        let currentLine = "";
        const EPS = 1e-3;

        const flushLine = () => {
            const trimmed = currentLine.trim();
            if (trimmed) entries.push({ type: "text", text: trimmed });
            currentLine = "";
        };

        const flushMarkersUntil = (timeSec: number) => {
            while (markerIdx < markers.length && Number(markers[markerIdx]?.timeSec ?? 0) <= timeSec + EPS) {
                flushLine();
                const title = String(markers[markerIdx]?.title ?? "").trim();
                if (title) entries.push({ type: "marker", text: `# ${title}` });
                markerIdx += 1;
            }
        };

        for (const word of words) {
            const wordStart = Number(word?.startSec ?? word?.endSec ?? 0);
            flushMarkersUntil(wordStart);

            const speakerName = String(word?.speaker ?? "").trim();
            if (options.includeSpeakerLabels && speakerName && speakerName !== currentSpeaker) {
                flushLine();
                entries.push({ type: "speaker", text: speakerName });
                currentSpeaker = speakerName;
            }

            currentLine = appendTranscriptToken(currentLine, String(word?.text ?? ""));
        }

        flushLine();
        flushMarkersUntil(Number.MAX_SAFE_INTEGER);

        if (!entries.length) return "";
        const lines: string[] = [];
        for (let idx = 0; idx < entries.length; idx++) {
            const current = entries[idx];
            lines.push(current.text);
            const next = entries[idx + 1];
            if (!next) continue;
            if (!(current.type === "speaker" && next.type === "text")) {
                lines.push("");
            }
        }

        return lines.join("\n").trim();
    }, [appendTranscriptToken]);

    const downloadTextFile = useCallback((filename: string, content: string) => {
        const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, []);

    const launchTrialStartConfetti = useCallback(() => {
        if (typeof window === "undefined" || typeof document === "undefined") return;

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        if (viewportWidth <= 0 || viewportHeight <= 0) return;

        const overlay = document.createElement("div");
        overlay.style.position = "fixed";
        overlay.style.inset = "0";
        overlay.style.pointerEvents = "none";
        overlay.style.zIndex = "2200";
        overlay.style.overflow = "hidden";

        const palette = [
            muiTheme.palette.primary.main,
            muiTheme.palette.secondary.main,
            "#22C55E",
            "#F59E0B",
            "#EF4444",
            "#06B6D4",
        ];

        const spawnBurst = ({
            originX,
            originY,
            particleCount,
        }: {
            originX: number;
            originY: number;
            particleCount: number;
        }) => {
            for (let i = 0; i < particleCount; i += 1) {
                const piece = document.createElement("span");
                const isCircle = Math.random() > 0.5;
                const size = 5 + Math.random() * 8;
                const angle = (Math.PI * (0.2 + Math.random() * 0.6)) * (Math.random() > 0.5 ? 1 : -1);
                const distance = 140 + Math.random() * 320;
                const driftX = Math.cos(angle) * distance;
                const driftY = Math.sin(angle) * distance + 220 + Math.random() * 160;
                const rotation = Math.random() * 780 - 390;
                const durationMs = 950 + Math.random() * 700;

                piece.style.position = "absolute";
                piece.style.left = `${originX}px`;
                piece.style.top = `${originY}px`;
                piece.style.width = `${size}px`;
                piece.style.height = `${size * (isCircle ? 1 : 0.62)}px`;
                piece.style.borderRadius = isCircle ? "999px" : "2px";
                piece.style.background = palette[Math.floor(Math.random() * palette.length)];
                piece.style.opacity = "1";
                piece.style.willChange = "transform, opacity";
                overlay.appendChild(piece);

                piece.animate(
                    [
                        { transform: "translate3d(0px, 0px, 0px) rotate(0deg)", opacity: 1 },
                        {
                            transform: `translate3d(${driftX * 0.68}px, ${driftY * 0.52}px, 0px) rotate(${rotation * 0.58}deg)`,
                            opacity: 1,
                            offset: 0.68,
                        },
                        {
                            transform: `translate3d(${driftX}px, ${driftY}px, 0px) rotate(${rotation}deg)`,
                            opacity: 0,
                        },
                    ],
                    {
                        duration: durationMs,
                        easing: "cubic-bezier(0.16, 0.84, 0.32, 1)",
                        fill: "forwards",
                    }
                );
            }
        };

        const originY = Math.max(90, Math.round(viewportHeight * 0.23));
        spawnBurst({
            originX: viewportWidth / 2,
            originY,
            particleCount: 110,
        });
        window.setTimeout(() => {
            spawnBurst({
                originX: viewportWidth * 0.36,
                originY: originY + 8,
                particleCount: 70,
            });
        }, 120);
        window.setTimeout(() => {
            spawnBurst({
                originX: viewportWidth * 0.64,
                originY: originY + 8,
                particleCount: 70,
            });
        }, 190);

        document.body.appendChild(overlay);
        window.setTimeout(() => {
            overlay.remove();
        }, 2500);
    }, [muiTheme.palette.primary.main, muiTheme.palette.secondary.main]);

    const hasExportedAComposition = authContext.authState?.hasExportedAComposition === true;
    const shouldStartTrialOnFirstMediaExport =
        isBeforeTrial &&
        !hasExportedAComposition &&
        !trialStartedForSession;

    /**
     * Comprehensive export handler for video/audio/transcript.
     * Video/audio use ExportStore progress tracking; transcript exports directly to .txt.
     */
    const handleExport = async (opts: ExportRequestArgs) => {
        const { tab, filename, resolution, quality, audioSettings, transcriptOptions } = opts;
        trackSessionEvent("export_started");
        const effectiveProjectId = String(projectId ?? ProjectAssetsStore.projectId ?? "").trim();
        const effectiveCompositionTitle =
            String(selectedCompositionTitle ?? selectedCompositionRecord?.title ?? selectedCompositionRecord?.name ?? "").trim() ||
            undefined;
        let exportLogIdPromise: Promise<string | null> = Promise.resolve(null);
        const effectiveAudioSettings: AudioExportSettings = {
            ...DEFAULT_AUDIO_EXPORT_SETTINGS,
            ...(audioSettings ?? {}),
        };

        if (tab === "transcript") {
            let outfile = filename || "libretto_transcript";
            if (!/\.txt$/i.test(outfile)) {
                outfile = `${outfile}.txt`;
            }

            const options = transcriptOptions ?? DEFAULT_TRANSCRIPT_EXPORT_OPTIONS;
            let transcriptText = "";
            try {
                transcriptText = transcriptExporterRef.current?.(options) ?? "";
            } catch (error) {
                console.error("[V2Editor] Failed to export transcript from editor state:", error);
            }
            if (!transcriptText.trim()) {
                transcriptText = buildFallbackTranscriptText(options);
            }
            exportLogIdPromise = createExportLog({
                axios: fetchContext?.authAxios,
                getToken: authContext?.getToken,
            }, {
                projectId: effectiveProjectId || undefined,
                compositionId: selectedCompositionId || undefined,
                compositionTitle: effectiveCompositionTitle,
                type: "transcript",
                filename: outfile,
                format: "txt",
            });
            downloadTextFile(outfile, transcriptText);
            trackSessionEvent("export_completed");
            void exportLogIdPromise.then((logId) => updateExportLog({
                axios: fetchContext?.authAxios,
                getToken: authContext?.getToken,
            }, {
                logId,
                status: "completed",
                filename: outfile,
                format: "txt",
            }));
            return;
        }

        if (!CompositionService.isInitialized()) {
            console.warn("Composition not initialized yet.");
            return;
        }

        const compId = selectedCompositionId;
        if (!compId) {
            console.warn("No composition selected for export.");
            return;
        }

        let outfile = filename || "libretto_export";
        const normalizedQuality = String(quality ?? ExportStore.selectedQuality ?? "").trim() || undefined;
        if (tab === 'audio') {
            const audioFormat = effectiveAudioSettings.format === "wav" ? "wav" : "m4a";
            const ext = audioFormat === "wav" ? ".wav" : ".m4a";
            const stripped = String(outfile).replace(/\.(wav|m4a|mp4|webm)$/i, "");
            outfile = `${stripped}${ext}`;
            exportLogIdPromise = createExportLog({
                axios: fetchContext?.authAxios,
                getToken: authContext?.getToken,
            }, {
                projectId: effectiveProjectId || undefined,
                compositionId: compId,
                compositionTitle: effectiveCompositionTitle,
                type: "audio",
                filename: outfile,
                format: audioFormat,
            });
        } else {
            if (!/\.(mp4|webm)$/i.test(outfile)) {
                outfile = `${outfile}.mp4`;
            }
            exportLogIdPromise = createExportLog({
                axios: fetchContext?.authAxios,
                getToken: authContext?.getToken,
            }, {
                projectId: effectiveProjectId || undefined,
                compositionId: compId,
                compositionTitle: effectiveCompositionTitle,
                type: "video",
                filename: outfile,
                format: "mp4",
                quality: normalizedQuality,
                width: Number(resolution?.width ?? 0) || undefined,
                height: Number(resolution?.height ?? 0) || undefined,
            });
        }
        let exportComposition: any | null = null;

        try {
            // Best-effort: ensure captions have transcript-derived word timings before export.
            try {
                await CompositionBridge.syncScriptLayerStore();
            } catch {}

            // Captions words are derived and not persisted; capture them from the active composition and
            // seed the detached export composition so exports include captions.
            let captionsWordsByClipId: Record<string, any[]> | undefined = undefined;
            let captionsConfigByClipId: Record<string, any> | undefined = undefined;
            let textConfigByClipId: Record<string, any> | undefined = undefined;
            let liveSnapshot: any | undefined = undefined;

            try {
                const map: Record<string, any[]> = {};
                const cfgMap: Record<string, any> = {};
                const textCfgMap: Record<string, any> = {};
                const clipRecordsById: Record<string, any> = {};
                const orderedLayerIds: string[] = [];
                const orderedClipIdsByLayerId: Record<string, string[]> = {};
                const layerMetaById: Record<string, { name?: string | null; type?: string | null }> = {};

                const liveComp: any = CompositionService.composition as any;
                for (const tr of (liveComp?.tracks ?? []) as any[]) {
                    const layerId = String((tr as any)?.id ?? "");
                    if (layerId) {
                        orderedLayerIds.push(layerId);
                        orderedClipIdsByLayerId[layerId] = [];
                        layerMetaById[layerId] = {
                            name: (CompositionStateStore.layers as any)?.[layerId]?.name ?? null,
                            type: typeof (tr as any)?.type === "string" ? String((tr as any).type) : "base",
                        };
                    }

                    for (const c of (tr?.clips ?? []) as any[]) {
                        if (!c) continue;
                        const type = String(c.type ?? "");
                        const id = String(c.id ?? "");
                        if (!id) continue;

                        if (layerId) {
                            orderedClipIdsByLayerId[layerId].push(id);
                            try {
                                const serialized = CompositionPersistenceService.serializeClipForExportSnapshot(c, { layerId });
                                if (serialized) clipRecordsById[id] = serialized;
                            } catch {}
                        }

                        if (type === "captions") {
                            try {
                                cfgMap[id] = {
                                    scriptClipId: c?.scriptClipId ?? null,
                                    timingScope: c?.timingScope ?? null,
                                    displayMode: c?.displayMode ?? null,
                                    textAlign: c?.textAlign ?? null,
                                    textBaseline: c?.textBaseline ?? null,
                                    textCase: c?.textCase ?? null,
                                    maxWidth: c?.maxWidth ?? null,
                                    textStyle: c?.textStyle ? JSON.parse(JSON.stringify(c.textStyle)) : null,
                                    highlightStyle: c?.highlightStyle ? JSON.parse(JSON.stringify(c.highlightStyle)) : null,
                                    background: c?.background ? JSON.parse(JSON.stringify(c.background)) : null,
                                    phraseConfig: c?.phraseConfig ? JSON.parse(JSON.stringify(c.phraseConfig)) : null,
                                    sceneStyleOverrides: c?.sceneStyleOverrides
                                        ? JSON.parse(JSON.stringify(c.sceneStyleOverrides))
                                        : null,
                                    // Visual props (best-effort for fidelity if persistence lags)
                                    position: c?.position ? JSON.parse(JSON.stringify(c.position)) : null,
                                    width: c?.width ?? null,
                                    height: c?.height ?? null,
                                    scale: c?.scale ? JSON.parse(JSON.stringify(c.scale)) : null,
                                    rotation: c?.rotation ?? null,
                                    alpha: c?.alpha ?? null,
                                };
                            } catch {}

                            const words = (c as any).words;
                            if (!Array.isArray(words) || words.length === 0) continue;
                            map[id] = words.map((w: any) => ({ ...(w ?? {}) }));
                        } else if (type === "text") {
                            try {
                                textCfgMap[id] = {
                                    text: c?.text ?? null,
                                    textAlign: c?.textAlign ?? null,
                                    textBaseline: c?.textBaseline ?? null,
                                    textCase: c?.textCase ?? null,
                                    maxWidth: c?.maxWidth ?? null,
                                    textStyle: c?.textStyle ? JSON.parse(JSON.stringify(c.textStyle)) : null,
                                    sceneStyleOverrides: c?.sceneStyleOverrides
                                        ? JSON.parse(JSON.stringify(c.sceneStyleOverrides))
                                        : null,
                                    // Visual props (best-effort for fidelity if persistence lags)
                                    position: c?.position ? JSON.parse(JSON.stringify(c.position)) : null,
                                    width: c?.width ?? null,
                                    height: c?.height ?? null,
                                    scale: c?.scale ? JSON.parse(JSON.stringify(c.scale)) : null,
                                    rotation: c?.rotation ?? null,
                                    alpha: c?.alpha ?? null,
                                };
                            } catch {}
                        }
                    }
                }

                if (Object.keys(map).length > 0) captionsWordsByClipId = map;
                if (Object.keys(cfgMap).length > 0) captionsConfigByClipId = cfgMap;
                if (Object.keys(textCfgMap).length > 0) textConfigByClipId = textCfgMap;

                liveSnapshot = {
                    display: {
                        canvasWidth: Number(liveComp?.settings?.width ?? liveComp?.width ?? 1920),
                        canvasHeight: Number(liveComp?.settings?.height ?? liveComp?.height ?? 1080),
                        backgroundColor: String(liveComp?.settings?.background ?? liveComp?.background ?? "#000000"),
                    },
                    // Persisted ordering is reverse of composition.tracks (last track first).
                    orderedLayerIds: [...orderedLayerIds].reverse(),
                    orderedClipIdsByLayerId,
                    layerMetaById,
                    clipRecordsById,
                };
            } catch {}

            exportComposition = await createDetachedExportComposition({
                compositionId: compId,
                resolveAssetUrl: ResolveAssetUrl,
                resolveEnhancedAudioUrl: async (trackId: string) => {
                    try {
                        return await GetEnhancedAudioUrl({ trackId });
                    } catch {
                        return undefined;
                    }
                },
                captionsWordsByClipId,
                captionsConfigByClipId,
                textConfigByClipId,
                liveSnapshot,
            });

            const composition = exportComposition;
            const compositionDurationSec = Number(composition?.duration?.seconds ?? 0);
            // Calculate total frames for progress tracking
            const totalFrames = tab === 'video'
                ? Math.floor(composition.duration.seconds * composition.fps)
                : Math.floor(composition.duration.seconds * 1000); // Use milliseconds for audio progress

            // Start export tracking
            const signal = ExportStore.startExport(tab, outfile, totalFrames);

            if (tab === 'audio') {
                // Audio export
                const enc = new MbAudioEncoder(composition, {
                    format: effectiveAudioSettings.format,
                    bitrate: effectiveAudioSettings.bitrate,
                    channels: effectiveAudioSettings.channels,
                    sampleRate: effectiveAudioSettings.sampleRate,
                });

                await enc.render(outfile, {
                    signal,
                    onProgress: (progress, total, remainingMs) => {
                        ExportStore.updateProgress(progress, total, remainingMs);
                    }
                });

                ExportStore.completeExport();
                trackSessionEvent("export_completed");
                void exportLogIdPromise.then((logId) => updateExportLog({
                    axios: fetchContext?.authAxios,
                    getToken: authContext?.getToken,
                }, {
                    logId,
                    status: "completed",
                    filename: outfile,
                    format: effectiveAudioSettings.format === "wav" ? "wav" : "m4a",
                    durationSec: compositionDurationSec || undefined,
                }));
            } else {
                // Video export
                const videoQuality = VIDEO_QUALITY_PRESETS[quality ?? ExportStore.selectedQuality];
                const exportResolution = resolution ?? {
                    width: Number(composition?.settings?.width ?? 1920),
                    height: Number(composition?.settings?.height ?? 1080),
                };

                // Calculate resolution scale factor
                const compWidth = composition.settings.width || 1920;
                const compHeight = composition.settings.height || 1080;
                // Use the larger scale so the long-side (which we round to even in ExportCard)
                // drives the export resolution. Using the smaller scale can produce odd output
                // dimensions (e.g. 853x480) which H.264/AVC rejects.
                const resolutionScale = Math.min(
                    1,
                        Math.max(
                        exportResolution.width / compWidth,
                        exportResolution.height / compHeight
                    )
                );

                const encoder = new MbEncoder(composition, {
                    fps: composition.fps,
                    resolution: resolutionScale,
                    debug: true,
                    videoBitrate: videoQuality.videoBitrate,
                    audioBitrate: videoQuality.audioBitrate,
                });

                const videoRenderResult = await encoder.render(outfile, {
                    signal,
                    onProgress: (progress, total, remainingMs) => {
                        ExportStore.updateProgress(progress, total, remainingMs);
                    }
                });

                ExportStore.completeExport();
                trackSessionEvent("export_completed");
                void exportLogIdPromise.then((logId) => updateExportLog({
                    axios: fetchContext?.authAxios,
                    getToken: authContext?.getToken,
                }, {
                    logId,
                    status: "completed",
                    filename: outfile,
                    format: "mp4",
                    quality: normalizedQuality,
                    width: Number(exportResolution.width ?? 0) || undefined,
                    height: Number(exportResolution.height ?? 0) || undefined,
                    durationSec: compositionDurationSec || undefined,
                    error: videoRenderResult.audioMessage,
                }));
            }
        } catch (e: any) {
            if (e.name === 'AbortError') {
                console.log("Export cancelled by user");
                // ExportStore already set to cancelled via cancelExport()
                void exportLogIdPromise.then((logId) => updateExportLog({
                    axios: fetchContext?.authAxios,
                    getToken: authContext?.getToken,
                }, {
                    logId,
                    status: "cancelled",
                }));
            } else {
                console.error("Export failed:", e);
                const exportErrorMessage = e.message || 'Export failed';
                ExportStore.failExport(exportErrorMessage);
                void exportLogIdPromise.then((logId) => updateExportLog({
                    axios: fetchContext?.authAxios,
                    getToken: authContext?.getToken,
                }, {
                    logId,
                    status: "failed",
                    error: exportErrorMessage,
                }));
            }
        } finally {
            try {
                await exportComposition?.dispose?.();
            } catch {}
        }
    };

    const { reportActivationProgress, reportActivationTrialStarted } = useActivationProgressReporter({
        authAxios: fetchContext?.authAxios,
        getToken: authContext?.getToken,
    });

    const startTrialOnFirstExport = useCallback(async (): Promise<boolean> => {
        if (!shouldStartTrialOnFirstMediaExport) return true;
        if (!fetchContext?.authAxios) {
            throw new Error("Authenticated client unavailable");
        }

        const freshToken = await authContext.getFreshToken?.();
        const token = freshToken || authContext.getToken?.();
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

        await fetchContext.authAxios.post(
            "/subscription/start-trial-on-first-export",
            {},
            { headers }
        );

        reportActivationTrialStarted(
            activationRunId || String(searchParams.get("activationRunId") || "").trim()
        );

        setTrialStartedForSession(true);
        const userId = authContext.getUserId?.();
        if (userId) {
            await authContext.getSubscriptionStatusFromStorage?.({ userId });
        }
        return true;
    }, [
        activationRunId,
        authContext,
        fetchContext?.authAxios,
        reportActivationTrialStarted,
        searchParams,
        shouldStartTrialOnFirstMediaExport,
    ]);

    const handleExportFromCard = useCallback(async (opts: ExportRequestArgs) => {
        const isMediaExport = opts.tab === "video" || opts.tab === "audio";
        if (isStartingTrialForExport) return;

        if (!shouldStartTrialOnFirstMediaExport || !isMediaExport) {
            setExportOpen(false);
            await handleExport(opts);
            return;
        }

        setIsStartingTrialForExport(true);
        setTrialStartErrorToast(null);
        try {
            await startTrialOnFirstExport();
            launchTrialStartConfetti();
            setTrialStartedToastOpen(true);
            setExportOpen(false);
            await handleExport(opts);
        } catch (error: any) {
            console.error("[V2Editor] Failed to start free trial:", error);
            if (error?.response?.status === 401) {
                await authContext.logout?.();
                return;
            }
            setTrialStartErrorToast("Could not start your free trial. Please try again.");
        } finally {
            setIsStartingTrialForExport(false);
        }
    }, [
        authContext,
        handleExport,
        isStartingTrialForExport,
        launchTrialStartConfetti,
        shouldStartTrialOnFirstMediaExport,
        startTrialOnFirstExport,
    ]);

    const handleCreateNewSequence = async () => {
        const newSequenceId = await assetStore.CreateSequence({projectId: projectId, title: "Untitled"});
        setSequenceId(newSequenceId);
        setSequenceEditorOpen(true);
    }

    const handleOpenSequence = (sequenceId: string) => {
        setSequenceId(sequenceId);
        setSequenceEditorOpen(true);
    }

    const updateExportCardPosition = useCallback(() => {
        if (!exportButtonRef.current) return;

        const rect = exportButtonRef.current.getBoundingClientRect();
        const cardWidth = 460;
        const horizontalPadding = 8;
        const maxLeft = window.innerWidth - cardWidth - horizontalPadding;
        const clampedLeft = Math.max(horizontalPadding, Math.min(rect.left, maxLeft));

        setExportCardPosition({
            top: rect.bottom + 6,
            left: clampedLeft,
        });
    }, []);

    const handleExportButtonClick = () => {
        const exportDisabled = isSelectedCompositionEmpty || pageMode === PAGE_MODES.RECORDING_STUDIO;
        if (exportDisabled) return;

        if (exportOpen) {
            setExportOpen(false);
            return;
        }

        updateExportCardPosition();
        setExportOpen(true);
    };

    useEffect(() => {
        if (!exportOpen) return;
        const handleViewportChange = () => updateExportCardPosition();
        window.addEventListener("resize", handleViewportChange);
        window.addEventListener("scroll", handleViewportChange, true);
        return () => {
            window.removeEventListener("resize", handleViewportChange);
            window.removeEventListener("scroll", handleViewportChange, true);
        };
    }, [exportOpen, updateExportCardPosition]);

    const handleUndoClick = async () => {
        if (compositionEditingDisabled || !UndoManager.canUndo) return;
        await UndoManager.undo();
        trackSessionEvent("undo_redo");
    };

    const handleRedoClick = async () => {
        if (compositionEditingDisabled || !UndoManager.canRedo) return;
        await UndoManager.redo();
        trackSessionEvent("undo_redo");
    };

    const handleActivationPreStepAction = useCallback(
        (action: ActivationPreStepAction) => {
            switch (action) {
                case "open_properties_sidebar": {
                    setSidebarItemSelected(SidebarItems.PROPERTIES);
                    break;
                }
                case "activate_filler_words": {
                    setSidebarItemSelected(SidebarItems.AI_TOOLS);
                    setAiToolsView("list");
                    try { GapStore.deactivate(); } catch {}
                    if (!FillerWordStore.isActive) {
                        void FillerWordStore.activateAsync(assetStore.GetTranscript, assetStore.PostAiFillerWords);
                    }
                    break;
                }
                case "activate_gap_removal": {
                    setSidebarItemSelected(SidebarItems.AI_TOOLS);
                    setAiToolsView("list");
                    try { FillerWordStore.deactivate(); } catch {}
                    void GapStore.activateAsync(assetStore.GetTranscript);
                    break;
                }
                case "open_add_chapters_sidebar": {
                    setSidebarItemSelected(SidebarItems.AI_TOOLS);
                    try { FillerWordStore.deactivate(); } catch {}
                    try { GapStore.deactivate(); } catch {}
                    setAiToolsView("add_chapters");
                    break;
                }
                case "open_ai_tools_sidebar": {
                    setSidebarItemSelected(SidebarItems.AI_TOOLS);
                    try { FillerWordStore.deactivate(); } catch {}
                    try { GapStore.deactivate(); } catch {}
                    setAiToolsView("list");
                    break;
                }
                case "open_decompose_sidebar": {
                    setSidebarItemSelected(SidebarItems.AI_TOOLS);
                    try { FillerWordStore.deactivate(); } catch {}
                    try { GapStore.deactivate(); } catch {}
                    setAiToolsView("decompose_clip");
                    break;
                }
                case "open_project_sidebar": {
                    setSidebarItemSelected(SidebarItems.PROJECT);
                    break;
                }
                case "open_edit_for_clarity_sidebar": {
                    setSidebarItemSelected(SidebarItems.AI_TOOLS);
                    try { FillerWordStore.deactivate(); } catch {}
                    try { GapStore.deactivate(); } catch {}
                    setAiToolsView("edit_for_clarity");
                    break;
                }
                default:
                    break;
            }
        },
        [assetStore],
    );

    const studioAutocreateTotalCount = studioAutocreateTrackStatuses.length;
    const studioAutocreateIsCreating = studioAutocreateSession?.phase === "creating";
    const studioAutocreateCanClose = Boolean(studioAutocreateSession && studioAutocreateSession.phase !== "creating");
    const studioAutocreateFailedCount = useMemo(
        () => studioAutocreateTrackStatuses.filter((track) => track.isFailed).length,
        [studioAutocreateTrackStatuses]
    );
    const studioAutocreateProcessingCount = Math.max(
        0,
        studioAutocreateTotalCount - studioAutocreateReadyCount - studioAutocreateFailedCount
    );
    const studioAutocreateProgressPct = studioAutocreateTotalCount > 0
        ? Math.round((studioAutocreateReadyCount / studioAutocreateTotalCount) * 100)
        : 0;
    const studioAutocreateStatusTone = studioAutocreateSession?.phase === "error"
        ? "#B91C1C"
        : studioAutocreateIsCreating
            ? "#111827"
            : "#2563EB";
    const studioAutocreateStatusChipLabel = studioAutocreateSession?.phase === "error"
        ? "Needs attention"
        : studioAutocreateIsCreating
            ? "Creating"
            : "Preparing tracks";
    const studioAutocreateSubtitle = (() => {
        if (!studioAutocreateSession) return "";
        if (studioAutocreateSession.phase === "creating") {
            return "Creating sequence and script clip…";
        }
        if (studioAutocreateSession.phase === "error") {
            return studioAutocreateSession.errorMessage || "Could not finish creating this composition.";
        }
        if (studioAutocreateHasFailures) {
            return "Some tracks failed processing. Stop for now and continue manually.";
        }
        if (studioAutocreateTotalCount === 0) {
            return "Waiting for participant tracks to appear…";
        }
        return `${studioAutocreateReadyCount} of ${studioAutocreateTotalCount} tracks ready`;
    })();

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                background: 'var(--app-bg-canvas)',
                gap: '5px',
                height: '100vh',
                width: '100vw',
                overflow: 'hidden',
            }}
        >
            <OfflineBanner message="Edits save locally and sync when you reconnect. Uploads and transcription are paused." />
            <ContextMenu/>
            <EditorActivationRunner
                runId={activationRunId}
                onFinish={() => setActivationRunId(null)}
                onRunPreStepAction={handleActivationPreStepAction}
                onProgress={reportActivationProgress}
            />
            {/* Top bar (aspect ratio button removed from here) */}
            <Box sx={{
                height: '40px',
                width: '100%',
                paddingY: "8px",
                paddingLeft: "24px",
                paddingRight: "10px",
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                position: 'relative',
            }}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <V2EditorBackButton handleClick={navigateToHome} disabled={false}
                />
                <V2EditorSettingsButton
                    handleClick={() => setIsProjectSettingsOpen(true)}
                    disabled={!(projectId || ProjectAssetsStore.projectId)}
                />
                <Tooltip title="Undo">
                    <span>
                        <IconButton
                            data-tour="editor-undo-button"
                            aria-label="Undo"
                            onClick={handleUndoClick}
                            disabled={compositionEditingDisabled || !canUndo}
                            sx={{
                                p: 1,
                                "&.Mui-disabled": {
                                    opacity: 0.45,
                                },
                            }}
                        >
                            <Box
                                component="img"
                                src="/undo.png"
                                alt=""
                                sx={{
                                    width: 18,
                                    height: 18,
                                    display: "block",
                                }}
                            />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Redo">
                    <span>
                        <IconButton
                            data-tour="editor-redo-button"
                            aria-label="Redo"
                            onClick={handleRedoClick}
                            disabled={compositionEditingDisabled || !canRedo}
                            sx={{
                                p: 1,
                                "&.Mui-disabled": {
                                    opacity: 0.45,
                                },
                            }}
                        >
                            <Box
                                component="img"
                                src="/redo.png"
                                alt=""
                                sx={{
                                    width: 18,
                                    height: 18,
                                    display: "block",
                                }}
                            />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Changes are auto-saved.">
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 20,
                            height: 20,
                            color: "var(--app-text-subtle)",
                            "& svg": { width: 16, height: 16 },
                            "& svg path": { stroke: "var(--app-text-subtle)" },
                        }}
                    >
                        <CheckmarkIcon />
                    </Box>
                </Tooltip>
            </Box>
                
                {/* Center: Project name / Composition selector */}
                <ProjectCompositionSelector
                    projectName={ProjectAssetsStore.projectName || ''}
                    compositions={ProjectAssetsStore.projectCompositions || []}
                    selectedCompositionId={selectedCompositionId}
                    selectedCompositionTitle={selectedCompositionTitle}
                    onCompositionSelect={(compId, compTitle) => {
                        setSelectedCompositionId(compId);
                        setSelectedCompositionTitle(compTitle);
                    }}
                    onCreateComposition={async () => {
                        const newCompId = await CreateComposition({ projectId: projectId as string });
                        if (newCompId) {
                            setSelectedCompositionId(newCompId);
                            setSelectedCompositionTitle('Untitled');
                        }
                    }}
                    onRenameProject={async (newName) => {
                        if (projectId) {
                            await RenameProject({ projectId, newName });
                        }
                    }}
		                    onRenameComposition={async (compositionId, newName) => {
		                        await handleRenameSelectedComposition(compositionId, newName);
		                    }}
                            readOnly={compositionEditingDisabled}
		                />
                
                <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                    <CompositionPresence compositionId={selectedCompositionId}/>
	                    <V2EditorLayoutToggle editorLayoutConfiguration={editorLayoutConfiguration}
	                                          setEditorLayoutConfiguration={setEditorLayoutConfiguration}/>
                    <Box ref={exportButtonRef} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <V2ExportButton onClick={handleExportButtonClick} disabled={isSelectedCompositionEmpty || pageMode === PAGE_MODES.RECORDING_STUDIO}/>
                    </Box>
                </Box>
            </Box>

            <Dialog
                open={Boolean(recordIntoTranscriptSession)}
                onClose={() => requestRecordIntoTranscriptClose()}
                disableEscapeKeyDown={
                    recordIntoTranscriptSession?.phase === "preparing" ||
                    Boolean(recordIntoTranscriptStudioStatus?.busy)
                }
                maxWidth={false}
                PaperProps={{
                    sx: {
                        width: "min(1100px, 94vw)",
                        height: "min(760px, 92vh)",
                        maxWidth: "none",
                        borderRadius: "16px",
                        overflow: "hidden",
                        border: "1px solid var(--app-border-subtle)",
                        backgroundColor: "var(--app-bg-canvas)",
                        boxShadow: "0 24px 80px rgba(15, 23, 42, 0.22)",
                    },
                }}
            >
                <DialogContent sx={{ position: "relative", width: "100%", height: "100%", p: 0 }}>
                    {recordIntoTranscriptSession && (
                        <LocalStudio
                            key={recordIntoTranscriptSession.request.requestId}
                            ref={recordIntoTranscriptStudioRef}
                            embedded
                            allowScreenShare={false}
                            projectId={recordIntoTranscriptSession.originProjectId}
                            recordingMode={
                                recordIntoTranscriptSession.request.mediaKind === "video"
                                    ? LibrettoRecordingMode.Video
                                    : LibrettoRecordingMode.Audio
                            }
                            isScreenRecording={false}
                            initialTitle={selectedCompositionTitle ?? undefined}
                            onRecordingStateChange={setRecordIntoTranscriptStudioStatus}
                            onRequestClose={requestRecordIntoTranscriptClose}
                            onError={handleRecordIntoTranscriptStudioError}
                            onRecordingComplete={handleRecordIntoTranscriptRecordingComplete}
                            onLeave={handleRecordIntoTranscriptStudioLeave}
                        />
                    )}

                    {recordIntoTranscriptSession?.phase !== "recording" && (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                zIndex: 5,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 3,
                                backgroundColor: isDarkMode
                                    ? "rgba(18, 20, 24, 0.94)"
                                    : "rgba(248, 250, 252, 0.94)",
                                backdropFilter: "blur(6px)",
                            }}
                        >
                            {recordIntoTranscriptSession?.phase === "preparing" ? (
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                                    <CircularProgress size={30} thickness={4} />
                                    <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "var(--app-text-primary)" }}>
                                        Adding recording to the transcript…
                                    </Typography>
                                    <Typography sx={{ maxWidth: 440, textAlign: "center", fontSize: "13px", color: "var(--app-text-muted)" }}>
                                        The new scene is being prepared at the selected insertion point.
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, maxWidth: 480 }}>
                                    <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "var(--app-text-primary)" }}>
                                        Recording couldn’t be inserted
                                    </Typography>
                                    <Typography sx={{ textAlign: "center", fontSize: "13px", lineHeight: 1.55, color: "var(--app-text-muted)" }}>
                                        {recordIntoTranscriptSession?.errorMessage ?? "The recording could not be added at this insertion point."}
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                                        {recordIntoTranscriptSession?.canRetry !== false && recordIntoTranscriptSession?.assetId && (
                                            <Button variant="contained" onClick={retryRecordIntoTranscriptInsertion}>
                                                Try again
                                            </Button>
                                        )}
                                        <Button variant="outlined" onClick={clearRecordIntoTranscriptSession}>
                                            Close
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog
                open={recordIntoTranscriptDiscardConfirmOpen}
                onClose={() => setRecordIntoTranscriptDiscardConfirmOpen(false)}
                PaperProps={{
                    sx: {
                        width: "min(420px, 90vw)",
                        borderRadius: "14px",
                        border: "1px solid var(--app-border-subtle)",
                    },
                }}
            >
                <DialogContent sx={{ p: 2.5 }}>
                    <Typography sx={{ fontSize: "17px", fontWeight: 600, color: "var(--app-text-primary)" }}>
                        Discard this recording?
                    </Typography>
                    <Typography sx={{ mt: 0.75, fontSize: "13px", lineHeight: 1.5, color: "var(--app-text-muted)" }}>
                        This take has not been added to the composition. Closing now will permanently discard it.
                    </Typography>
                    <Box sx={{ mt: 2.25, display: "flex", justifyContent: "flex-end", gap: 1 }}>
                        <Button variant="outlined" onClick={() => setRecordIntoTranscriptDiscardConfirmOpen(false)}>
                            Keep recording
                        </Button>
                        <Button color="error" variant="contained" onClick={discardRecordIntoTranscriptRecording}>
                            Discard recording
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            <Dialog
                open={sequenceEditorOpen}
                onClose={() => setSequenceEditorOpen(false)}
                PaperProps={{sx: {width: '95vw', height: '95vh', zIndex: 10}}}
                maxWidth={false}
            >
                <DialogContent sx={{
                    width: '100%', height: '100%',
                    display: 'flex',        // Enable flexbox
                    justifyContent: 'center', // Center horizontally
                    alignItems: 'center',     // Center vertically
                    padding: "0px 0px 0px 0px",
                }}>
                    <SequenceEditor setSequenceEditorOpen={setSequenceEditorOpen} sequenceId={sequenceId}/>
                </DialogContent>
            </Dialog>
            <ProjectSettingsModal
                open={isProjectSettingsOpen}
                onClose={() => setIsProjectSettingsOpen(false)}
                projectId={projectId ?? ProjectAssetsStore.projectId}
                defaultTranscriptionLanguage={ProjectAssetsStore.projectDefaultTranscriptionLanguage}
                defaultAudioExportFormat={ProjectAssetsStore.projectDefaultAudioExportFormat}
                defaultAudioExportSampleRate={ProjectAssetsStore.projectDefaultAudioExportSampleRate}
                showWordlessAudioEventsOnScript={ProjectAssetsStore.projectShowWordlessAudioEventsOnScript}
                applySmartTransitionsToNewScenes={ProjectAssetsStore.projectApplySmartTransitionsToNewScenes}
                onDefaultTranscriptionLanguageChange={(languageCode) => {
                    ProjectAssetsStore.setProjectDefaultTranscriptionLanguage(languageCode);
                }}
                onDefaultAudioExportFormatChange={(value) => {
                    ProjectAssetsStore.setProjectDefaultAudioExportFormat(value);
                }}
                onDefaultAudioExportSampleRateChange={(value) => {
                    ProjectAssetsStore.setProjectDefaultAudioExportSampleRate(value);
                }}
                onShowWordlessAudioEventsOnScriptChange={(value) => {
                    ProjectAssetsStore.setProjectShowWordlessAudioEventsOnScript(value);
                }}
                onApplySmartTransitionsToNewScenesChange={(value) => {
                    ProjectAssetsStore.setProjectApplySmartTransitionsToNewScenes(value);
                }}
            />
            <ZoomImportDialog
                open={zoomImportDialogOpen && zoomImportEnabled}
                onClose={() => setZoomImportDialogOpen(false)}
                projectId={projectId ?? ProjectAssetsStore.projectId}
                onImportStarted={(count) => {
                    setZoomImportNotice(
                        `${count} Zoom ${count === 1 ? "recording" : "recordings"} queued. ${count === 1 ? "It" : "They"} will appear in Project Files.`
                    );
                    openProjectSidebarFiles();
                }}
            />
            <Dialog
                open={Boolean(studioAutocreateSession)}
                onClose={(_event, reason) => {
                    if (!studioAutocreateCanClose) return;
                    if (reason === "backdropClick" || reason === "escapeKeyDown") {
                        handleCancelStudioAutocreate();
                    }
                }}
                disableEscapeKeyDown={!studioAutocreateCanClose}
                PaperProps={{
                    sx: {
                        width: "min(560px, 92vw)",
                        borderRadius: "16px",
                        boxShadow: "0px 20px 60px rgba(15, 23, 42, 0.08)",
                        border: "1px solid var(--app-border-subtle)",
                        backgroundColor: "var(--app-bg-surface)",
                    },
                }}
            >
                <DialogContent sx={{ p: 0 }}>
                    <Box
                        sx={{
                            px: "18px",
                            py: "14px",
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: "12px",
                            borderBottom: "1px solid var(--app-border-subtle)",
                        }}
                    >
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: 0 }}>
                            <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "var(--app-text-primary)", letterSpacing: "-0.01em" }}>
                                Creating your composition
                            </Typography>
                            <Typography sx={{ fontSize: "13px", color: studioAutocreateSession?.phase === "error" ? "#B91C1C" : "var(--app-text-muted)", lineHeight: 1.45 }}>
                                {studioAutocreateSubtitle}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                flexShrink: 0,
                                px: "8px",
                                py: "3px",
                                borderRadius: "999px",
                                border: `1px solid ${studioAutocreateStatusTone}20`,
                                backgroundColor: `${studioAutocreateStatusTone}12`,
                                color: studioAutocreateStatusTone,
                                fontSize: "11px",
                                fontWeight: 500,
                                letterSpacing: "0.01em",
                            }}
                        >
                            {studioAutocreateStatusChipLabel}
                        </Box>
                    </Box>

                    <Box sx={{ px: "18px", pt: "12px", pb: "10px", display: "flex", flexDirection: "column", gap: "10px" }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                            <Typography sx={{ fontSize: "12px", color: "var(--app-text-muted)", fontWeight: 500 }}>
                                {studioAutocreateTotalCount > 0
                                    ? `${studioAutocreateReadyCount} / ${studioAutocreateTotalCount} tracks ready`
                                    : "Waiting for tracks"}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                <Typography sx={{ fontSize: "11px", color: "#166534", fontWeight: 500 }}>
                                    Ready {studioAutocreateReadyCount}
                                </Typography>
                                <Typography sx={{ fontSize: "11px", color: "var(--app-text-muted)", fontWeight: 500 }}>
                                    Processing {studioAutocreateProcessingCount}
                                </Typography>
                                {studioAutocreateFailedCount > 0 && (
                                    <Typography sx={{ fontSize: "11px", color: "#B91C1C", fontWeight: 500 }}>
                                        Failed {studioAutocreateFailedCount}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        {studioAutocreateTotalCount > 0 ? (
                            <LinearProgress
                                variant="determinate"
                                value={Math.max(0, Math.min(100, studioAutocreateProgressPct))}
                                sx={{
                                    height: 6,
                                    borderRadius: 999,
                                    backgroundColor: "var(--app-border-subtle)",
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: studioAutocreateSession?.phase === "error" ? "#DC2626" : "#2B6BFD",
                                        borderRadius: 999,
                                    },
                                }}
                            />
                        ) : (
                            <LinearProgress
                                sx={{
                                    height: 6,
                                    borderRadius: 999,
                                    backgroundColor: "var(--app-border-subtle)",
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: "#2B6BFD",
                                        borderRadius: 999,
                                    },
                                }}
                            />
                        )}
                    </Box>

                    <Box
                        sx={{
                            mx: "18px",
                            mb: "12px",
                            border: "1px solid var(--app-border-subtle)",
                            borderRadius: "10px",
                            overflow: "hidden",
                            maxHeight: "240px",
                            backgroundColor: "var(--app-bg-subtle)",
                        }}
                    >
                        {studioAutocreateTrackStatuses.length === 0 && (
                            <Box sx={{ px: "12px", py: "14px", display: "flex", alignItems: "center", gap: "10px" }}>
                                <CircularProgress size={14} thickness={6} sx={{ color: "var(--app-text-subtle)" }} />
                                <Typography sx={{ fontSize: "13px", color: "var(--app-text-muted)" }}>
                                    Waiting for participant tracks…
                                </Typography>
                            </Box>
                        )}
                        {studioAutocreateTrackStatuses.map((trackStatus, index) => (
                            <Box
                                key={trackStatus.id}
                                sx={{
                                    px: "12px",
                                    py: "10px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: "10px",
                                    borderTop: index === 0 ? "none" : "1px solid var(--app-border-subtle)",
                                }}
                            >
                                <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
                                    <Typography
                                        sx={{
                                            fontSize: "13px",
                                            color: "var(--app-text-primary)",
                                            fontWeight: 500,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {trackStatus.name}
                                    </Typography>
                                    {trackStatus.statusLabel === "Failed" && trackStatus.statusRaw && (
                                        <Typography sx={{ fontSize: "11px", color: "#B91C1C" }}>
                                            {trackStatus.statusRaw}
                                        </Typography>
                                    )}
                                </Box>

                                <Box
                                    sx={{
                                        flexShrink: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "7px",
                                        px: "8px",
                                        py: "3px",
                                        borderRadius: "999px",
                                        border: `1px solid ${
                                            trackStatus.statusLabel === "Ready"
                                                ? "#86EFAC"
                                                : trackStatus.statusLabel === "Failed"
                                                    ? "#FECACA"
                                                    : "var(--app-border-subtle)"
                                        }`,
                                        backgroundColor: trackStatus.statusLabel === "Ready"
                                            ? "#F0FDF4"
                                            : trackStatus.statusLabel === "Failed"
                                                ? "#FEF2F2"
                                                : "var(--app-bg-surface)",
                                    }}
                                >
                                    {trackStatus.statusLabel === "Processing" ? (
                                        <CircularProgress size={10} thickness={7} sx={{ color: "var(--app-text-muted)" }} />
                                    ) : (
                                        <Box
                                            sx={{
                                                width: 7,
                                                height: 7,
                                                borderRadius: "50%",
                                                backgroundColor: trackStatus.statusLabel === "Ready" ? "#22C55E" : "#DC2626",
                                            }}
                                        />
                                    )}
                                    <Typography
                                        sx={{
                                            fontSize: "11px",
                                            fontWeight: 500,
                                            color: trackStatus.statusLabel === "Ready"
                                                ? "#166534"
                                                : trackStatus.statusLabel === "Failed"
                                                    ? "#B91C1C"
                                                    : "var(--app-text-muted)",
                                        }}
                                    >
                                        {trackStatus.statusLabel}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    <Box
                        sx={{
                            px: "18px",
                            py: "12px",
                            borderTop: "1px solid var(--app-border-subtle)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "12px",
                        }}
                    >
                        <Typography sx={{ fontSize: "12px", color: "var(--app-text-muted)", lineHeight: 1.45 }}>
                            {studioAutocreateCanClose
                                ? "You can stop this and continue editing manually."
                                : "Please keep this open while we finish creation."}
                        </Typography>
                        <Button
                            onClick={handleCancelStudioAutocreate}
                            disabled={!studioAutocreateCanClose}
                            sx={{
                                textTransform: "none",
                                fontSize: "13px",
                                fontWeight: 500,
                                color: "var(--app-text-muted)",
                                border: "1px solid var(--app-border-muted)",
                                backgroundColor: "var(--app-bg-surface)",
                                borderRadius: "8px",
                                px: "14px",
                                py: "6px",
                                minWidth: "112px",
                                whiteSpace: "nowrap",
                                '&:hover': {
                                    borderColor: "var(--app-border-muted)",
                                    backgroundColor: "var(--app-bg-subtle)",
                                },
                            }}
                        >
                            {studioAutocreateIsCreating ? "Working…" : "Stop for now"}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
            <EditForClarityCleanupDialog
                open={isEditForClarityCleanupDialogOpen && Boolean(editForClarityCleanupProgress)}
                progress={editForClarityCleanupProgress}
                stopRequested={editForClarityCleanupStopRequested}
                onClose={handleCloseEditForClarityCleanupDialog}
                onStop={handleStopEditForClarityCleanup}
            />
            {exportOpen && (
	            <ExportCard
	              onClose={() => {
	                  setExportOpen(false);
	              }}
	              onExport={handleExportFromCard}
                  defaultAudioSettings={{
                      ...DEFAULT_AUDIO_EXPORT_SETTINGS,
                      format: normalizeAudioExportFormat(ProjectAssetsStore.projectDefaultAudioExportFormat),
                      sampleRate: normalizeAudioExportSampleRate(ProjectAssetsStore.projectDefaultAudioExportSampleRate),
                  }}
                  anchorPosition={exportCardPosition}
	              defaultTitle={selectedCompositionTitle || "libretto_export"}
	              durationSec={CompositionService.isInitialized() ? CompositionService.composition.duration?.seconds || 0 : 0}
                  previewThumbnailUrl={exportPreviewThumbnailUrl}
                  isStartingTrialOnExport={isStartingTrialForExport}
	              canvasSize={{
	                width: CompositionService.isInitialized() ? (CompositionService.composition.settings.width || 1920) : 1920,
	                height: CompositionService.isInitialized() ? (CompositionService.composition.settings.height || 1080) : 1080,
	              }}
	            />
            )}

            <Snackbar
                open={trialStartedToastOpen}
                autoHideDuration={2800}
                onClose={() => setTrialStartedToastOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setTrialStartedToastOpen(false)}
                    severity="success"
                    sx={{ width: "100%" }}
                >
                    Free trial started - your export is starting.
                </Alert>
            </Snackbar>

            <Snackbar
                open={Boolean(trialStartErrorToast)}
                autoHideDuration={3400}
                onClose={() => setTrialStartErrorToast(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setTrialStartErrorToast(null)}
                    severity="error"
                    sx={{ width: "100%" }}
                >
                    {trialStartErrorToast}
                </Alert>
            </Snackbar>

            <Snackbar
                open={Boolean(recordIntoTranscriptNotice)}
                autoHideDuration={recordIntoTranscriptNotice?.severity === "success" ? 3600 : 5200}
                onClose={() => setRecordIntoTranscriptNotice(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setRecordIntoTranscriptNotice(null)}
                    severity={recordIntoTranscriptNotice?.severity ?? "success"}
                    sx={{ width: "100%" }}
                >
                    {recordIntoTranscriptNotice?.message}
                </Alert>
            </Snackbar>

            <Snackbar
                open={Boolean(zoomImportNotice)}
                autoHideDuration={4800}
                onClose={() => setZoomImportNotice(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setZoomImportNotice(null)}
                    severity="success"
                    sx={{ width: "100%" }}
                >
                    {zoomImportNotice}
                </Alert>
            </Snackbar>

            <BackgroundActivityCard
                visible={showBackgroundActivityCard}
                rows={backgroundActivityRows}
                syncingCount={syncingTracksCount}
                transcribingCount={transcribingTracksCount}
                bottom={backgroundActivityCardBottom}
            />

            <DecomposeClipActivityCard
                visible={showDecomposeActivityCard}
                exports={decomposeClip.exports}
                step={decomposeClip.step}
                error={decomposeClip.error}
                bottom={decomposeActivityCardBottom}
                onOpen={() => {
                    setSidebarItemSelected(SidebarItems.AI_TOOLS);
                    setAiToolsView("decompose_clip");
                }}
            />
          
            {/* Export progress indicator - floating in bottom-right corner */}
            <ExportProgressCard floating={true} />

            <Box
                data-tour="editor-workspace-area"
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
	                    flex: 1,
	                    gap: '3px',
	                    minHeight: 0,
	                }}
	            >
                {/* Left area: takes remaining width */}
                <Box
                    sx={{
                        background: 'var(--app-bg-surface)',
                        display: 'flex',
                        marginLeft: "8px",
                        marginBottom: "3px",
                        flex: '1 1 0',
                        minWidth: 0,
                        border: "0.5px solid var(--app-border-subtle)",
                        borderRadius: "12px",
                        transition: 'all 0.3s ease',
                        position: 'relative',
                    }}
                >
	                    {pageMode === PAGE_MODES.EDITOR && isRestoringComposition && !selectedCompositionEmptyFlag && (
	                        <Box
	                            sx={{
	                                position: "absolute",
	                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1.5,
                                backgroundColor: isDarkMode ? "rgba(18,20,24,0.92)" : "rgba(255,255,255,0.92)",
                                zIndex: 5,
                            }}
                        >
                            <CircularProgress size={18} thickness={5} />
                            <Typography sx={{ fontSize: "13px", color: "var(--app-text-muted)", fontWeight: 500 }}>
                                Loading…
                            </Typography>
                        </Box>
	                    )}
			                    {pageMode === PAGE_MODES.EDITOR && (selectedCompositionEmptyFlag || !isRestoringComposition) && isSelectedCompositionEmpty &&
			                        // Empty composition → show create flow
			                        // @ts-ignore
			                        <CreateCompositionViewArea key={`create-${selectedCompositionId || 'none'}`}
			                                                   projectId={projectId}
			                                                   compositionId={selectedCompositionId}
			                                                   setPageMode={setPageMode}
                                                               setIsScreenRecordingMode={setIsScreenRecordingMode}
			                                                   setRecordingMode={(mode) => {
			                                                       setIsScreenRecordingMode(false);
			                                                       setRecordingMode(mode);
			                                                   }}
			                                                   openProjectSidebar={() => setSidebarItemSelected(SidebarItems.PROJECT)}
			                                                   onImportFromZoom={zoomImportEnabled ? () => setZoomImportDialogOpen(true) : undefined}
			                        />
		                    }
	                    {pageMode === PAGE_MODES.EDITOR && !isRestoringComposition && !isSelectedCompositionEmpty &&
	                        <SceneTranscriptCanvasTimelineArea
	                            key={`editor-${selectedCompositionId || 'none'}`}
	                            canvasRef={canvasRef}
	                            containerRef={containerRef}
                                compositionTitle={selectedCompositionTitle}
                                onRenameCompositionTitle={async (newName: string) => {
                                    const compositionId = String(selectedCompositionId || "").trim();
                                    if (!compositionId) return;
                                    await handleRenameSelectedComposition(compositionId, newName);
                                }}
	                                isBeforeTrial={isBeforeTrial}
                                transcriptHighlights={transcriptHighlights}
	                            onDuplicateToNewComposition={handleDuplicateRangeToNewComposition}
	                            // Pass UI controls through so we can render the floating button + card in the canvas region
	                            showClipSizeCard={showClipSizeCard}
	                            openClipSizeCard={() => setShowClipSizeCard(true)}
	                            closeClipSizeCard={() => setShowClipSizeCard(false)}
	                            editorLayoutConfiguration={editorLayoutConfiguration}
	                            getTranscript={assetStore.GetTranscript}
                                postAiFillerWords={assetStore.PostAiFillerWords}
	                                    onTranscriptExporterReady={handleTranscriptExporterReady}
	                                    onCropDoneCapture={decomposeClip.onCropDoneCapture}
			                        />
	                    }
                    {pageMode === PAGE_MODES.RECORDING_STUDIO && (
                        <LocalStudio
                            projectId={projectId}
                            recordingMode={recordingMode}
                            isScreenRecording={isScreenRecordingMode}
                            onLeave={() => {
                                setPageMode(PAGE_MODES.EDITOR);
                            }}
                            initialTitle={selectedCompositionTitle}
                            onRecordingComplete={(result) => {
                                console.log('[V2Editor] Recording complete:', result);
                                // Screen recording sessions: create a Sequence + insert a multi-source script clip.
                                if (result?.screenAssetId) {
                                    setPendingScreenRecordingSessions((prev) => [
                                        ...prev,
                                        {
                                            screenAssetId: result.screenAssetId,
                                            secondaryAssetId: result.secondaryAssetId || null,
                                        },
                                    ]);
                                } else if (result?.assetId) {
                                    // Single-file recording: queue the asset to be auto-inserted into script layer
                                    setPendingRecordingAssetIds((prev) => [
                                        ...prev,
                                        { assetId: result.assetId, filetype: result.filetype || 'audio' }
                                    ]);
                                }
                                setPageMode(PAGE_MODES.EDITOR);
                            }}
                        />
                    )}
                </Box>

                {/* Sidebars */}
                {sidebarItemSelected && (
                    <Box
                        data-tour="editor-open-sidebar-column"
                        sx={{
                            height: '100%',
                            width: '300px',
                            flex: '0 0 300px',
                            minHeight: 0,
                            display: 'flex',
                            transition: 'all 0.3s ease',
                        }}
                    >
                        {sidebarItemSelected === SidebarItems.ASSISTANT && (
                            <V2AssistantSidebar
                                messages={assistantMessages}
                                onSend={sendAssistantMessage}
                                isSending={isAssistantSending}
                                error={assistantError}
                            />
                        )}
                        {/* @ts-ignore */}
                        {sidebarItemSelected === SidebarItems.PROJECT && (
	                            <V2ProjectSidebar
	                                handleCreateNewSequence={handleCreateNewSequence}
	                                handleOpenSequence={handleOpenSequence}
	                                selectedCompositionId={selectedCompositionId}
	                                setSelectedCompositionTitle={setSelectedCompositionTitle}
	                                setSelectedCompositionId={setSelectedCompositionId}
                                    deleteCompositionsPreservingProject={deleteCompositionsPreservingProject}
	                                highlightFilesPanel={highlightProjectFilesPanel}
	                                onImportFromZoom={zoomImportEnabled ? () => setZoomImportDialogOpen(true) : undefined}
	                            />
                        )}
                        {sidebarItemSelected === SidebarItems.PROPERTIES && <V2PropertiesSidebar/>}
                        {sidebarItemSelected === SidebarItems.ELEMENTS && <V2ElementsSidebar/>}
                        {sidebarItemSelected === SidebarItems.STOCK && <V2StockSidebar/>}
                        {sidebarItemSelected === SidebarItems.CAPTIONS && <V2CaptionsSidebar/>}
                        {/* AI Tools - shows sub-flows when active, otherwise main menu */}
                        {sidebarItemSelected === SidebarItems.AI_TOOLS && (
                            FillerWordStore.isActive ? (
                                <FillerWordSidebar onClose={() => FillerWordStore.deactivate()} />
                            ) : GapStore.isActive ? (
                                <GapSidebar onClose={() => GapStore.deactivate()} />
                            ) : aiToolsView === "automatic_multicam" ? (
                                <AutomaticMulticamSidebar
                                    onBack={() => {
                                        setAiToolsView("list");
                                        setApplyMulticamError(null);
                                    }}
                                    onSubmit={(opts) => {
                                        void handleApplyAutomaticMulticam(opts);
                                    }}
                                />
                            ) : aiToolsView === "remove_retakes" ? (
                                <RetakesToolSidebar workflow={retakesReview} />
                            ) : aiToolsView === "edit_for_clarity" ? (
                                <EditForClarityToolSidebar workflow={editForClarityReview} />
                            ) : aiToolsView === "create_clips" ? (
                                <CreateClipsSidebar
                                    onBack={() => {
                                        resetCreateClipsReview();
                                        setAiToolsView("list");
                                    }}
                                    onStopSubmitting={() => {
                                        resetCreateClipsReview();
                                    }}
                                    onSubmit={(settings) => {
                                        void handleCreateClipsSubmit(settings);
                                    }}
                                    isSubmitting={isCreatingClips}
                                />
                            ) : aiToolsView === "create_clips_results" ? (
                                <CreateClipsResultsSidebar
                                    onBack={() => {
                                        setAiToolsView("create_clips");
                                    }}
                                    clips={createClipsDraft}
                                    selectedClipId={selectedCreateClipId}
                                    onSelectClip={handleSelectCreateClip}
                                    onDiscardAll={() => {
                                        void handleDiscardAllCreateClips();
                                    }}
                                    onDone={() => {
                                        resetCreateClipsReview();
                                        setAiToolsView("list");
                                    }}
                                />
                            ) : aiToolsView === "find_highlights" ? (
                                <FindHighlightsSidebar
                                    onBack={() => {
                                        setAiToolsView("list");
                                    }}
                                    isSubmitting={isFindingHighlights}
                                    onSubmit={(settings) => {
                                        void handleFindHighlightsSubmit(settings);
                                    }}
                                />
                            ) : aiToolsView === "find_highlights_results" ? (
                                <FindHighlightsResultsSidebar
                                    onBack={() => {
                                        setAiToolsView("find_highlights");
                                    }}
                                    highlights={findHighlightsDraft}
                                    selectedHighlightId={selectedFindHighlightId}
                                    onSelectHighlight={handleSelectFindHighlight}
                                    onDiscardAll={() => {
                                        resetFindHighlightsReview();
                                        setAiToolsView("list");
                                    }}
                                    onDone={() => {
                                        setAiToolsView("list");
                                    }}
                                />
                            ) : aiToolsView === "add_chapters" ? (
                                <AddChaptersSidebar
                                    onBack={() => {
                                        setAiToolsView("list");
                                        setGenerateChaptersError(null);
                                        setAiChaptersDraft([]);
                                    }}
                                    onSubmit={(count) => {
                                        void handleGenerateAiChapters(count);
                                    }}
                                    onApply={() => handleApplyAiChaptersDraft()}
                                    isLoading={isGeneratingChapters}
                                    error={generateChaptersError}
                                    draft={aiChaptersDraft}
                                />
                            ) : aiToolsView === "decompose_clip" ? (
                                <DecomposeClipSidebar
                                    onBack={() => {
                                        if (decomposeClip.isCropModeActive) {
                                            try { ApplicationStore.exitCropMode(); } catch {}
                                            try { CompositionService.recomputeFrame(); } catch {}
                                        }
                                        setAiToolsView("list");
                                    }}
                                    step={decomposeClip.step}
                                    hasVideo={decomposeClip.hasVideo}
                                    speakers={decomposeClip.speakers}
                                    currentCropSpeakerIndex={decomposeClip.currentCropSpeakerIndex}
                                    isCropModeActive={decomposeClip.isCropModeActive}
                                    exports={decomposeClip.exports}
                                    error={decomposeClip.error}
                                    onValidateAssignments={decomposeClip.validateAssignments}
                                    onStartCrop={decomposeClip.startCurrentCrop}
                                />
                            ) : (
                                <V2AIToolsSidebar
                                    onAddChapters={() => setAiToolsView("add_chapters")}
                                    onAutomaticMulticam={() => setAiToolsView("automatic_multicam")}
                                    onEditForClarity={() => {
                                        editForClarityReview.reset();
                                        setAiToolsView("edit_for_clarity");
                                    }}
                                    onRemoveRetakes={() => {
                                        retakesReview.reset();
                                        setAiToolsView("remove_retakes");
                                    }}
                                    onCreateClips={() => {
                                        setAiToolsView("create_clips");
                                    }}
                                    onFindHighlights={() => {
                                        setAiToolsView(hasFindHighlightsDraft ? "find_highlights_results" : "find_highlights");
                                    }}
                                    onDecomposeClip={() => {
                                        void (async () => {
                                            if (decomposeClip.hasWorkflow && decomposeClip.isWorkflowActive) {
                                                setAiToolsView("decompose_clip");
                                                return;
                                            }
                                            const started = await decomposeClip.startWorkflow();
                                            if (started) setAiToolsView("decompose_clip");
                                        })();
                                    }}
                                    decomposeDisabledReason={decomposeClip.disabledReason}
                                />
                            )
                        )}
                    </Box>
                )}

                <SidebarSpotlightOverlay
                    active={Boolean(isAddMediaSidebarSpotlightActive)}
                    targetSelector='[data-tour="editor-open-sidebar-column"]'
                    padding={8}
                />
                <WorkflowGuidanceCard
                    card={activeWorkflowGuidanceCard}
                    onDismiss={dismissWorkflowGuidanceCard}
                />

                <Box
                    sx={{
                        display: 'flex',
                        width: '80px',
                        flexShrink: 0,
                        overflow: 'auto',
                        flexDirection: 'column',
                        padding: '8px 6px 10px',
                        gap: '12px',
                        borderRadius: '14px',
                        border: '1px solid var(--app-border-subtle)',
                        backgroundColor: 'var(--app-bg-surface)',
                        boxShadow: isDarkMode ? '0 8px 18px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(15, 23, 42, 0.04)',
                        marginRight: '8px',
                        marginBottom: '3px',
                    }}
                >
                    <V2EditorSidebar setSidebarItemSelected={setSidebarItemSelected}
                                     sidebarItemSelected={sidebarItemSelected}
                                     showAssistantCallout={
                                         showAssistantDiscovery &&
                                         sidebarItemSelected !== SidebarItems.ASSISTANT &&
                                         pageMode === PAGE_MODES.EDITOR &&
                                         !activationRunId &&
                                         !activeWorkflowGuidanceCard
                                     }
                                     onAssistantOpen={() => {
                                         markAssistantOpened();
                                         trackSessionEvent("editor_assistant_opened");
                                     }}/>
                </Box>
            </Box>
        </Box>
    )
});

const CreateCompositionViewArea = ({projectId, compositionId, setPageMode, setRecordingMode, setIsScreenRecordingMode, openProjectSidebar, onImportFromZoom}) => {
    const [sizes, setSizes] = useState([100, 0]); // 80% top, 20% bottom

    const toggleTimelineHide = () => {
        setSizes(prev => (prev[1] === 0 ? [80, 20] : [100, 0]));
    };

    return (
        <Box sx={{display: 'flex', height: '100%', width: '100%'}}>
            <Split
                sizes={sizes}
                minSize={30}
                gutterSize={8}
                direction="vertical"
                onDragEnd={setSizes}
                style={{display: 'flex', flexDirection: 'column', height: '100%', width: '100%'}}
                gutter={(_index, direction) => {
                    const gutter = document.createElement('div');
                    gutter.className = `gutter gutter-${direction}`;
                    gutter.style.cursor = 'row-resize';
                    gutter.style.height = '10px';
                    gutter.style.width = '100%';
                    gutter.style.position = 'relative';
                    gutter.style.backgroundColor = 'transparent';
                    gutter.style.zIndex = '10';
                    gutter.style.display = 'flex';
                    gutter.style.alignItems = 'center';
                    gutter.style.justifyContent = 'center';

                    const line = document.createElement('div');
                    line.style.position = 'absolute';
                    line.style.left = '0';
                    line.style.top = '50%';
                    line.style.transform = 'translateY(-50%)';
                    line.style.width = '100%';
                    line.style.height = '1px';
                    line.style.backgroundColor = 'var(--app-border-subtle)';
                    line.style.transition = 'background-color 0.15s ease';

                    const handle = document.createElement('div');
                    handle.style.position = 'relative';
                    handle.style.zIndex = '1';
                    handle.style.width = '36px';
                    handle.style.height = '4px';
                    handle.style.borderRadius = '2px';
                    handle.style.backgroundColor = 'var(--app-border-muted)';
                    handle.style.transition = 'background-color 0.15s ease, width 0.15s ease';

                    gutter.appendChild(line);
                    gutter.appendChild(handle);

                    gutter.addEventListener('mouseover', () => {
                        line.style.backgroundColor = 'var(--app-border-muted)';
                        handle.style.backgroundColor = 'var(--app-text-muted)';
                        handle.style.width = '48px';
                    });
                    gutter.addEventListener('mouseout', () => {
                        line.style.backgroundColor = 'var(--app-border-subtle)';
                        handle.style.backgroundColor = 'var(--app-border-muted)';
                        handle.style.width = '36px';
                    });
                    return gutter;
                }}
            >
	                {/* Top pane */}
	                <div style={{display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0}}>
	                    <CreateCompositionView
	                        projectId={projectId as string}
	                        compositionId={compositionId as string}
	                        setPageMode={setPageMode}
	                        setRecordingMode={setRecordingMode}
                            setIsScreenRecordingMode={setIsScreenRecordingMode}
	                        openProjectSidebar={openProjectSidebar}
                            onImportFromZoom={onImportFromZoom}
	                    />
	                </div>

                {/* Bottom pane (timeline) */}
                <div style={{overflow: 'hidden', minHeight: 0, minWidth: 0, width: '100%', paddingTop: "30px", backgroundColor: 'var(--app-bg-surface)'}}>
                    <Timeline timelineHidden={true} toggleTimelineHide={() => {}} disabled={true}/>
                </div>
            </Split>
        </Box>
    );
}

const SceneTranscriptCanvasTimelineArea = ({
				                                               canvasRef,
				                                               containerRef,
                                                           compositionTitle,
                                                           onRenameCompositionTitle,
	                                                           isBeforeTrial,
                                                           transcriptHighlights,
				                                               onDuplicateToNewComposition,
		                                                       onTranscriptExporterReady,
		                                               showClipSizeCard,
		                                               openClipSizeCard,
			                                               closeClipSizeCard,
			                                               editorLayoutConfiguration,
			                                               getTranscript,
                                                           postAiFillerWords,
	                                                   onCropDoneCapture,
		                                           }) => {
    const [sizes, setSizes] = useState([60, 40]); // 60% top, 40% bottom

    const toggleTimelineHide = () => {
        setSizes(prev => (prev[1] === 0 ? [60, 40] : [100, 0]));
    };

    return (
        <Box sx={{display: 'flex', height: '100%', width: '100%'}}>
            <Split
                sizes={sizes}
                minSize={sizes[1] === 0 ? [0, 0] : [0, 260]}
                gutterSize={8}
                direction="vertical"
                onDragEnd={setSizes}
                style={{display: 'flex', flexDirection: 'column', height: '100%', width: '100%'}}
                gutter={(_index, direction) => {
                    const gutter = document.createElement('div');
                    gutter.className = `gutter gutter-${direction}`;
                    gutter.style.cursor = 'row-resize';
                    gutter.style.height = '10px';
                    gutter.style.width = '100%';
                    gutter.style.position = 'relative';
                    gutter.style.backgroundColor = 'transparent';
                    gutter.style.zIndex = '10';
                    gutter.style.display = 'flex';
                    gutter.style.alignItems = 'center';
                    gutter.style.justifyContent = 'center';

                    const line = document.createElement('div');
                    line.style.position = 'absolute';
                    line.style.left = '0';
                    line.style.top = '50%';
                    line.style.transform = 'translateY(-50%)';
                    line.style.width = '100%';
                    line.style.height = '1px';
                    line.style.backgroundColor = 'var(--app-border-subtle)';
                    line.style.transition = 'background-color 0.15s ease';

                    const handle = document.createElement('div');
                    handle.style.position = 'relative';
                    handle.style.zIndex = '1';
                    handle.style.width = '36px';
                    handle.style.height = '4px';
                    handle.style.borderRadius = '2px';
                    handle.style.backgroundColor = 'var(--app-border-muted)';
                    handle.style.transition = 'background-color 0.15s ease, width 0.15s ease';

                    gutter.appendChild(line);
                    gutter.appendChild(handle);

                    gutter.addEventListener('mouseover', () => {
                        line.style.backgroundColor = 'var(--app-border-muted)';
                        handle.style.backgroundColor = 'var(--app-text-muted)';
                        handle.style.width = '48px';
                    });
                    gutter.addEventListener('mouseout', () => {
                        line.style.backgroundColor = 'var(--app-border-subtle)';
                        handle.style.backgroundColor = 'var(--app-border-muted)';
                        handle.style.width = '36px';
                    });
                    return gutter;
                }}
            >
                {/* Top pane */}
		                <div style={{display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0}}>
			                    <SceneTranscriptCanvasArea
			                        canvasRef={canvasRef}
			                        containerRef={containerRef}
                                    compositionTitle={compositionTitle}
                                    onRenameCompositionTitle={onRenameCompositionTitle}
	                                isBeforeTrial={isBeforeTrial}
                                    transcriptHighlights={transcriptHighlights}
			                        showClipSizeCard={showClipSizeCard}
		                        openClipSizeCard={openClipSizeCard}
				                        closeClipSizeCard={closeClipSizeCard}
				                        editorLayoutConfiguration={editorLayoutConfiguration}
				                        getTranscript={getTranscript}
                                        postAiFillerWords={postAiFillerWords}
			                        onDuplicateToNewComposition={onDuplicateToNewComposition}
                                onTranscriptExporterReady={onTranscriptExporterReady}
                                onCropDoneCapture={onCropDoneCapture}
				                    />
			                </div>

                {/* Bottom pane (timeline) */}
                <div style={{overflow: 'hidden', minHeight: 0, minWidth: 0, width: '100%', paddingTop: "30px", backgroundColor: 'var(--app-bg-surface)'}}>
                    <Timeline timelineHidden={sizes[1] === 0} toggleTimelineHide={toggleTimelineHide}/>
                </div>
            </Split>
        </Box>
    );
};

// @ts-ignore
export const SceneTranscriptCanvasArea = observer(({
		                                              canvasRef,
		                                              containerRef,
                                                      compositionTitle,
                                                      onRenameCompositionTitle,
	                                                      isBeforeTrial,
                                                          transcriptHighlights,
			                                              showClipSizeCard,
		                                              openClipSizeCard,
			                                              closeClipSizeCard,
			                                              editorLayoutConfiguration,
			                                              getTranscript,
                                                          postAiFillerWords,
			                                              onDuplicateToNewComposition,
	                                                      onTranscriptExporterReady,
	                                                      onCropDoneCapture,
		                                          } : any) => {
    const [sizes, setSizes] = useState<number[]>([55, 45]);
    useEffect(() => {
        if (editorLayoutConfiguration === 'transcript') {
            setSizes([100, 0]);
        } else if (editorLayoutConfiguration === 'canvas') {
            setSizes([0, 100]);
        } else {
            setSizes(prev => (prev[0] === 0 || prev[1] === 0) ? [55, 45] : prev);
        }
    }, [editorLayoutConfiguration]);

    const [selectedSceneId, setSelectedSceneId] = useState<string | null>(null);

    // Map ScriptLayerStore.scenes to SceneItem format for SceneBar
    // Access versions to ensure reactivity when thumbnails/markers update
    const _version = ScriptLayerStore.scenesVersion;
    const _markersVersion = ScriptLayerStore.transcriptMarkersVersion;

    const toSeekTimeSec = (srcTimeSec: number): number => {
        const mapped = ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(srcTimeSec);
        return mapped != null ? mapped : srcTimeSec / (ScriptLayerStore.playbackRate || 1);
    };

    const transcriptMarkers: any[] = Array.isArray(ScriptLayerStore.transcriptMarkers)
        ? (ScriptLayerStore.transcriptMarkers as any[])
        : [];

    const scenes: SceneItem[] = ScriptLayerStore.scenes.map((scene, sceneIdx) => {
        const isLast = sceneIdx === ScriptLayerStore.scenes.length - 1;
        const startSec = Number(scene.startSec ?? 0);
        const endSec = Number(scene.endSec ?? 0);
        const EPS = 1e-3;

        const markersForScene = transcriptMarkers
            .filter((m) => {
                const t = Number(m?.timeSec ?? 0);
                if (!Number.isFinite(t)) return false;
                if (isLast) return t >= startSec - EPS && t <= endSec + EPS;
                return t >= startSec - EPS && t < endSec - EPS;
            })
            .sort((a, b) => Number(a?.timeSec ?? 0) - Number(b?.timeSec ?? 0))
            .map((m) => ({
                id: String(m?.id ?? ""),
                title: String(m?.title ?? "Marker") || "Marker",
                seekTimeSec: toSeekTimeSec(Number(m?.timeSec ?? 0)),
            }))
            .filter((m) => m.id);

        return {
            id: scene.id,
            thumbnailUrl: scene.thumbnailUrl ?? '',
            label: scene.index + 1,
            meta: {
                startSec,
                endSec,
                markers: markersForScene,
            },
        };
    });

    const displayW = Number(CompositionStateStore.display?.width ?? 0);
    const displayH = Number(CompositionStateStore.display?.height ?? 0);
    const sceneThumbnailAspectRatio =
        Number.isFinite(displayW) && Number.isFinite(displayH) && displayW > 0 && displayH > 0
            ? `${displayW} / ${displayH}`
            : "16 / 9";
    
    // Visual content can come from the script clip (video sources) OR any non-script visual clip.
    // Use this to decide whether the SceneBar should be shown (scenes are most useful when there's visuals).
    const hasVisualContent =
        ScriptLayerStore.hasVideo ||
        Object.values(CompositionStateStore.clips || {}).some((c: any) => {
            const t = String(c?.type ?? '').toLowerCase();
            return t === 'video' || t === 'image' || t === 'captions' || t === 'subtitles';
        });

    const handleSceneSelect = (sceneId: string) => {
        setSelectedSceneId(sceneId);
        // Seek to scene start time
        const scene = ScriptLayerStore.scenes.find(s => s.id === sceneId);
        if (scene) {
            // Seek slightly after the boundary so time-domain comparisons don't land "on the fence".
            CompositionService.seek(toSeekTimeSec(Number(scene.startSec ?? 0)) + 0.001);
        }
    };

    // Keep the SceneBar highlight in sync with the playhead (seek + playback).
    // We intentionally do NOT touch ApplicationStore.selectedSceneIndex here; this is purely a visual
    // affordance to show "what scene am I in" at the current playhead time.
    useEffect(() => {
        const EPS = 1e-3;

        const computeSceneIdAtTime = (timelineTimeSec: number): string | null => {
            const list = ScriptLayerStore.scenes;
            if (!Array.isArray(list) || list.length === 0) return null;

            const t = Number(timelineTimeSec ?? 0);
            if (!Number.isFinite(t)) return null;

            const srcT = ScriptLayerStore.mapTimelineTimeToSourceTime(t);
            if (srcT != null && Number.isFinite(srcT)) {
                for (let i = 0; i < list.length; i++) {
                    const scene = list[i];
                    const startSec = Number(scene?.startSec ?? 0);
                    const endSec = Number(scene?.endSec ?? 0);
                    const isLast = i === list.length - 1;
                    if (isLast) {
                        if (srcT >= startSec - EPS && srcT <= endSec + EPS) return String(scene.id);
                    } else {
                        if (srcT >= startSec - EPS && srcT < endSec - EPS) return String(scene.id);
                    }
                }
            }

            // Fallback: choose by mapped scene start times in timeline-domain.
            let bestId = String(list[0].id);
            let bestStart = Number.NEGATIVE_INFINITY;
            for (const scene of list) {
                const startSec = Number(scene?.startSec ?? 0);
                if (!Number.isFinite(startSec)) continue;
                const startTimeline = ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(startSec);
                if (startTimeline == null || !Number.isFinite(startTimeline)) continue;
                if (startTimeline <= t + EPS && startTimeline >= bestStart - EPS) {
                    bestStart = startTimeline;
                    bestId = String(scene.id);
                }
            }
            return bestId || null;
        };

        const disposer = reaction(
            () => {
                // Track both playhead and scene list changes. The function returns a stable sceneId
                // so the effect only causes React updates when the scene actually changes.
                const _scenesVersion = ScriptLayerStore.scenesVersion;
                const _cutsVersion = ScriptLayerStore.cutsVersion;
                const _playbackRate = ScriptLayerStore.playbackRate;
                const t = Number(CompositionStateStore.currentTime ?? 0);
                return computeSceneIdAtTime(t);
            },
            (sceneId) => {
                setSelectedSceneId((prev) => (prev === sceneId ? prev : sceneId));
            },
            { fireImmediately: true },
        );

        return () => disposer();
    }, []);

    const handleCanvasWorkspaceMouseDownCapture = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (event.button !== 0) return;
        if (ApplicationStore.isCropMode) return;
        if (!ApplicationStore.selectedClipId && !ApplicationStore.selectedVisualElement) return;

        const target = event.target as HTMLElement | null;
        if (target?.closest("[data-canvas-interactive='true']")) return;

        const canvas = canvasRef.current;
        if (canvas) {
            const rect = canvas.getBoundingClientRect();
            const inCanvas =
                event.clientX >= rect.left &&
                event.clientX <= rect.right &&
                event.clientY >= rect.top &&
                event.clientY <= rect.bottom;
            if (inCanvas) return;
        }

        ApplicationStore.clearSelectedClipId();
    }, []);

    return (
        <Box sx={{display: 'flex', flex: 1, minHeight: 0, minWidth: 0, height: "100%", width: "100%"}}>
            <Split
                sizes={sizes}
                minSize={[0, 0]}
                gutterSize={8}
                direction="horizontal"
                onDragEnd={setSizes}
                style={{display: 'flex', width: '100%', height: '100%', flexDirection: 'row'}}
                gutter={(_index, direction) => {
                    const gutter = document.createElement('div');
                    gutter.className = `gutter gutter-${direction}`;
                    gutter.style.cursor = 'col-resize';
                    gutter.style.height = '100%';
                    gutter.style.width = '10px';
                    gutter.style.position = 'relative';
                    gutter.style.backgroundColor = 'transparent';
                    gutter.style.zIndex = '10';
                    gutter.style.display = 'flex';
                    gutter.style.alignItems = 'center';
                    gutter.style.justifyContent = 'center';

                    const line = document.createElement('div');
                    line.style.position = 'absolute';
                    line.style.left = '50%';
                    line.style.top = '0';
                    line.style.transform = 'translateX(-50%)';
                    line.style.width = '1px';
                    line.style.height = '100%';
                    line.style.backgroundColor = 'var(--app-border-subtle)';
                    line.style.transition = 'background-color 0.15s ease';

                    const handle = document.createElement('div');
                    handle.style.position = 'relative';
                    handle.style.zIndex = '1';
                    handle.style.width = '4px';
                    handle.style.height = '36px';
                    handle.style.borderRadius = '2px';
                    handle.style.backgroundColor = 'var(--app-border-muted)';
                    handle.style.transition = 'background-color 0.15s ease, height 0.15s ease';

                    gutter.appendChild(line);
                    gutter.appendChild(handle);

                    gutter.addEventListener('mouseover', () => {
                        line.style.backgroundColor = 'var(--app-border-muted)';
                        handle.style.backgroundColor = 'var(--app-text-muted)';
                        handle.style.height = '48px';
                    });
                    gutter.addEventListener('mouseout', () => {
                        line.style.backgroundColor = 'var(--app-border-subtle)';
                        handle.style.backgroundColor = 'var(--app-border-muted)';
                        handle.style.height = '36px';
                    });
                    return gutter;
                }}
            >
                {/* Left pane (Transcript + optional SceneBar) */}
                <div style={{display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, height: '100%', position: 'relative'}}>
                    {/* Filler word toolbar - overlayed on top of transcript */}
                    {FillerWordStore.isActive && (
                        <Box sx={{
                            position: 'absolute',
                            top: 12,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 100,
                            width: '640px',
                            maxWidth: 'calc(100% - 24px)',
                            borderRadius: '14px',
                            boxShadow: '0px 20px 60px rgba(15, 23, 42, 0.08)',
                            border: '1px solid var(--app-border-subtle)',
                            overflow: 'hidden',
                        }}>
	                            <FillerWordToolbar
	                                onRemoveCurrent={() => {
	                                    trackSessionEvent("filler_word_removal");
	                                    // Get context BEFORE removing (so we can still find it in allWords)
	                                    const currentOccurrence = FillerWordStore.currentOccurrence;
                                    const contextData = currentOccurrence 
                                        ? FillerWordStore.getContextWordsWithTimestamps(currentOccurrence)
                                        : null;
                                    
                                    const occurrence = FillerWordStore.removeCurrent();
                                    if (occurrence) {
                                        // Apply the cut using script-clip local times (stable across cuts) + dynamic padding
                                        // CRITICAL: Never cut beyond adjacent word boundaries!
                                        const mode = FillerWordStore.cutMode;
                                        const pad = occurrence.paddingSec;
                                        
                                        // Extend cut by padding, but CLAMP to not exceed neighbor boundaries
                                        let cutStart = occurrence.startSec - pad;
                                        let cutEnd = occurrence.endSec + pad;
                                        
                                        // Never cut before the end of the previous word
                                        if (occurrence.prevWordEndSec !== null) {
                                            cutStart = Math.max(cutStart, occurrence.prevWordEndSec);
                                        }
                                        // Never cut after the start of the next word
                                        if (occurrence.nextWordStartSec !== null) {
                                            cutEnd = Math.min(cutEnd, occurrence.nextWordStartSec);
                                        }
                                        
                                        // ========== DETAILED DEBUG LOGGING ==========
                                        const MICROFADE_MS = 15; // From script.interfaces.ts
                                        const fadeSec = MICROFADE_MS / 1000;
                                        
                                        // Fade-out happens at the END of the segment BEFORE the cut
                                        // (on the KEPT audio, leading up to cutStart)
                                        const fadeOutStart = cutStart - fadeSec;
                                        const fadeOutEnd = cutStart;
                                        
                                        // Fade-in happens at the START of the segment AFTER the cut
                                        // (on the KEPT audio, starting from cutEnd)
                                        const fadeInStart = cutEnd;
                                        const fadeInEnd = cutEnd + fadeSec;
                                        
                                        console.log('\n========== FILLER WORD REMOVAL DEBUG ==========');
                                        
                                        if (contextData) {
                                            console.log('📝 WORDS BEFORE (5):');
                                            contextData.wordsBefore.forEach((w, i) => {
                                                console.log(`   ${i+1}. "${w.text}" [${w.startSec.toFixed(3)}s - ${w.endSec.toFixed(3)}s]`);
                                            });
                                            
                                            console.log('🎯 FILLER WORD:');
                                            console.log(`   "${contextData.fillerWord.text}" [${contextData.fillerWord.startSec.toFixed(3)}s - ${contextData.fillerWord.endSec.toFixed(3)}s]`);
                                            
                                            console.log('📝 WORDS AFTER (5):');
                                            contextData.wordsAfter.forEach((w, i) => {
                                                console.log(`   ${i+1}. "${w.text}" [${w.startSec.toFixed(3)}s - ${w.endSec.toFixed(3)}s]`);
                                            });
                                        }
                                        
                                        if (mode === 'delete' || mode === 'ignore') {
                                            // Use async version with zero-crossing analysis
                                            CompositionService.cutScriptLayerRangeWithAnalysis(
                                                cutStart,
                                                cutEnd,
                                                occurrence.sourceIndex,
                                                { 
                                                    transcriptMode: mode === 'ignore' ? 'strikethrough' : 'delete',
                                                    prevWordEndSec: occurrence.prevWordEndSec,
                                                    nextWordStartSec: occurrence.nextWordStartSec,
                                                }
                                            ).catch(err => console.error('[FillerWord] Analysis cut failed:', err));
                                        } else if (mode === 'gap') {
                                            CompositionService.addScriptLayerGap(cutStart, cutEnd);
                                        }
                                    }
	                                }}
		                                onRemoveAll={async () => {
		                                    trackSessionEvent("filler_word_removal");
		                                    const occurrences = FillerWordStore.getAllForRemoval();
	                                    const mode = FillerWordStore.cutMode;
	                                    const total = occurrences.length;
	                                    
	                                    console.log('[FillerWord] Removing all:', total, 'occurrences with analysis');
	                                    
	                                    // Start progress tracking
	                                    FillerWordStore.startRemoval();
	                                    CompositionService.beginScriptLayerBatch();
	                                    
		                                    try {
		                                        // Process cuts sequentially for analysis
	                                        // Process cuts - use batching to reduce UI updates and help with background tabs
	                                        const BATCH_SIZE = 10; // Update UI every 10 items
	                                        
	                                        // Helper to yield to event loop (helps with background tab processing)
	                                        const yieldToEventLoop = () => new Promise<void>(resolve => {
	                                            // Use MessageChannel for faster scheduling than setTimeout in background tabs
	                                            const channel = new MessageChannel();
	                                            channel.port1.onmessage = () => resolve();
	                                            channel.port2.postMessage(null);
	                                        });
	                                        
	                                        // Process in REVERSE order (from end to start)
	                                        // This ensures earlier cuts don't affect later occurrence positions
	                                        const reversedOccurrences = [...occurrences].reverse();
	                                        
	                                        let processed = 0;
	                                        for (const occurrence of reversedOccurrences) {
	                                            const pad = occurrence.paddingSec;
	                                            
	                                            // Extend cut by padding, but CLAMP to not exceed neighbor boundaries
	                                            let cutStart = occurrence.startSec - pad;
	                                            let cutEnd = occurrence.endSec + pad;
	                                            
	                                            // Never cut before the end of the previous word
	                                            if (occurrence.prevWordEndSec !== null) {
	                                                cutStart = Math.max(cutStart, occurrence.prevWordEndSec);
	                                            }
	                                            // Never cut after the start of the next word
	                                            if (occurrence.nextWordStartSec !== null) {
	                                                cutEnd = Math.min(cutEnd, occurrence.nextWordStartSec);
	                                            }
	                                            
	                                            if (mode === 'delete' || mode === 'ignore') {
	                                                try {
	                                                    await CompositionService.cutScriptLayerRangeWithAnalysis(
	                                                        cutStart,
	                                                        cutEnd,
	                                                        occurrence.sourceIndex,
	                                                        { 
	                                                            transcriptMode: mode === 'ignore' ? 'strikethrough' : 'delete',
	                                                            prevWordEndSec: occurrence.prevWordEndSec,
	                                                            nextWordStartSec: occurrence.nextWordStartSec,
	                                                        }
	                                                    );
	                                                } catch (err) {
	                                                    console.error('[FillerWord] Analysis cut failed for occurrence, using fallback:', err);
	                                                    CompositionService.cutScriptLayerRange(
	                                                        cutStart,
	                                                        cutEnd,
	                                                        { transcriptMode: mode === 'ignore' ? 'strikethrough' : 'delete' }
	                                                    );
	                                                }
	                                            } else if (mode === 'gap') {
	                                                CompositionService.addScriptLayerGap(cutStart, cutEnd);
	                                            }
	                                            
	                                            processed++;
	                                            
	                                            // Update progress in batches to reduce overhead
	                                            if (processed % BATCH_SIZE === 0 || processed === total) {
	                                                FillerWordStore.updateRemovalProgress((processed / total) * 100);
	                                                // Yield to event loop periodically to stay responsive
	                                                await yieldToEventLoop();
		                                            }
		                                        }
		                                    } finally {
		                                        try {
		                                            // Apply expensive engine + UI updates once (avoids UI jank).
		                                            await CompositionService.endScriptLayerBatch();
		                                        } finally {
		                                            FillerWordStore.finishRemoval();
		                                            FillerWordStore.deactivate();
		                                        }
		                                    }
		                                }}
		                                onClose={() => FillerWordStore.deactivate()}
		                            />
	                        </Box>
	                    )}
                    
                    {/* Gap toolbar - overlayed on top of transcript */}
                    {GapStore.isActive && (
                        <Box sx={{
                            position: 'absolute',
                            top: 12,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 100,
                            width: '610px',
                            maxWidth: 'calc(100% - 24px)',
                            borderRadius: '14px',
                            boxShadow: '0px 20px 60px rgba(15, 23, 42, 0.08)',
                            border: '1px solid var(--app-border-subtle)',
                            overflow: 'hidden',
                        }}>
	                            <GapToolbar
	                                onShortenCurrent={() => {
	                                    trackSessionEvent("silence_removal");
	                                    const gap = GapStore.shortenCurrent();
                                    if (gap) {
                                        // Calculate how much to cut
                                        // We want to keep shortenToSeconds of the gap
                                        const targetDuration = GapStore.shortenToSeconds;
                                        const currentDuration = gap.durationSec;
                                        
                                        if (currentDuration > targetDuration) {
                                            // Cut from the middle of the gap
                                            // Keep targetDuration/2 at start and targetDuration/2 at end
                                            const keepAtStart = targetDuration / 2;
                                            const keepAtEnd = targetDuration / 2;
                                            
                                            const cutStart = gap.startSec + keepAtStart;
                                            const cutEnd = gap.endSec - keepAtEnd;
                                            
                                            console.log('[Gap] Shortening gap:', {
                                                original: `${gap.startSec.toFixed(2)}s - ${gap.endSec.toFixed(2)}s (${currentDuration.toFixed(2)}s)`,
                                                cutRange: `${cutStart.toFixed(2)}s - ${cutEnd.toFixed(2)}s`,
                                                remaining: `${targetDuration.toFixed(2)}s`,
                                            });
                                            
                                            // Apply the cut - use simple cut (no audio analysis needed for silences)
                                            CompositionService.cutScriptLayerRange(
                                                cutStart,
                                                cutEnd,
                                                { transcriptMode: 'delete' }
                                            );
                                        }
                                    }
	                                }}
	                                onShortenAll={async () => {
	                                    trackSessionEvent("silence_removal");
	                                    const gaps = GapStore.getAllGaps();
                                    const targetDuration = GapStore.shortenToSeconds;
                                    const total = gaps.length;
                                    
                                    console.log('[Gap] Shortening all:', total, 'gaps to', targetDuration, 's');
                                    
                                    GapStore.startRemoval();
                                    
                                    const BATCH_SIZE = 10;
                                    const yieldToEventLoop = () => new Promise<void>(resolve => {
                                        const channel = new MessageChannel();
                                        channel.port1.onmessage = () => resolve();
                                        channel.port2.postMessage(null);
                                    });
                                    
                                    // Process gaps in REVERSE order (from end to start)
                                    // This ensures earlier cuts don't affect later gap positions
                                    const reversedGaps = [...gaps].reverse();
                                    
                                    let processed = 0;
                                    for (const gap of reversedGaps) {
                                        const currentDuration = gap.durationSec;
                                        
                                        if (currentDuration > targetDuration) {
                                            const keepAtStart = targetDuration / 2;
                                            const keepAtEnd = targetDuration / 2;
                                            
                                            const cutStart = gap.startSec + keepAtStart;
                                            const cutEnd = gap.endSec - keepAtEnd;
                                            
                                            // Use simple cut - no audio analysis needed for silences
                                            CompositionService.cutScriptLayerRange(
                                                cutStart,
                                                cutEnd,
                                                { transcriptMode: 'delete' }
                                            );
                                        }
                                        
                                        processed++;
                                        
                                        if (processed % BATCH_SIZE === 0 || processed === total) {
                                            GapStore.updateRemovalProgress((processed / total) * 100);
                                            await yieldToEventLoop();
                                        }
                                    }
                                    
                                    GapStore.finishRemoval();
                                    await GapStore.rescan(getTranscript);
                                }}
                                onClose={() => GapStore.deactivate()}
                            />
                        </Box>
                    )}
                    
	                    <Box sx={{display: 'flex', flexDirection: 'row', width: '100%', flex: 1, minHeight: 0}}>
	                        {/* Scene bar - always show scenes (thumbnails when available) */}
                        {hasVisualContent && scenes.length > 0 && (
                            <Box sx={{display: 'flex', width: 'clamp(120px, 12vw, 180px)', flexShrink: 0}}>
	                                <SceneBar
	                                    scenes={scenes}
                                        thumbnailAspectRatio={sceneThumbnailAspectRatio}
	                                    selectedSceneId={selectedSceneId}
                                    onSelect={handleSceneSelect}
                                    onMarkerSelect={(marker: SceneMarkerItem) => {
                                        CompositionService.seek(Number(marker?.seekTimeSec ?? 0));
                                    }}
	                                    buildContextMenu={(scene) => {
	                                        let sceneInfo = ScriptLayerStore.scenes.find((s) => s.id === scene.id);
	                                        if (!sceneInfo) {
	                                            const fallbackStartSec = Number(scene.meta?.startSec ?? NaN);
	                                            const fallbackEndSec = Number(scene.meta?.endSec ?? NaN);
	                                            if (Number.isFinite(fallbackStartSec) && Number.isFinite(fallbackEndSec)) {
	                                                const EPS = 1e-3;
	                                                sceneInfo =
	                                                    ScriptLayerStore.scenes.find(
	                                                        (s) =>
	                                                            Math.abs(Number(s?.startSec ?? NaN) - fallbackStartSec) <= EPS &&
	                                                            Math.abs(Number(s?.endSec ?? NaN) - fallbackEndSec) <= EPS,
	                                                    ) ?? sceneInfo;
	                                            }
	                                        }
	                                        const idx = sceneInfo?.index ?? null;
	                                        if (idx == null) return [];

			                                        const options: any[] = [];
			                                        const scriptClip: any = CompositionService.getScriptLayerClip?.();
			                                        const sceneStartSec = Number(sceneInfo?.startSec ?? scene.meta?.startSec ?? NaN);
			                                        const sceneEndSec = Number(sceneInfo?.endSec ?? scene.meta?.endSec ?? NaN);
			                                        const selectedSceneRange =
			                                            Number.isFinite(sceneStartSec) &&
			                                            Number.isFinite(sceneEndSec) &&
			                                            sceneEndSec > sceneStartSec
			                                                ? { startSec: sceneStartSec, endSec: sceneEndSec }
			                                                : null;
				                                        const canAddMediaToScene =
				                                            Boolean(scriptClip && selectedSceneRange && isScriptSceneEmpty(scriptClip, selectedSceneRange));
                                                        const clipboardChunk = getCompatibleScriptChunkClipboard(scriptClip);
                                                        const canPasteIntoScene = Boolean(canAddMediaToScene && selectedSceneRange && clipboardChunk);
			                                        options.push({
			                                            title: "Change Layout",
			                                            onClick: () => {
		                                                // Seek into the target scene so the layout change is immediately visible.
		                                                try {
		                                                    const startSrc = Number(sceneInfo?.startSec ?? 0);
		                                                    if (Number.isFinite(startSrc)) {
		                                                        CompositionService.seek(toSeekTimeSec(startSrc) + 0.001);
		                                                    }
		                                                } catch {}
		                                                try {
		                                                    window.dispatchEvent(
		                                                        new CustomEvent("openPropertiesSidebarLayout", { detail: { sceneIndex: idx } })
		                                                    );
		                                                } catch {}
		                                                ContextMenuStore.hide();
		                                            },
		                                        });
				                                        if (canAddMediaToScene && selectedSceneRange) {
				                                            options.push({
				                                                title: "Add media",
			                                                onClick: () => {
			                                                    beginScriptAddMediaFlow({
			                                                        atClipLocalSec: Number(selectedSceneRange.startSec ?? 0),
			                                                        preferExistingEmptyScene: true,
			                                                        source: "scene-bar-empty-scene",
			                                                        sceneIndex: idx,
			                                                        sceneStartSec: Number(selectedSceneRange.startSec ?? 0),
			                                                        sceneEndSec: Number(selectedSceneRange.endSec ?? 0),
			                                                    });
			                                                    ContextMenuStore.hide();
			                                                },
				                                            });
			                                        }
				                                        if (canPasteIntoScene && selectedSceneRange && clipboardChunk) {
				                                            options.push({
				                                                title: "Paste",
				                                                onClick: () => {
				                                                    ContextMenuStore.hide();
				                                                    void pasteScriptChunkIntoScene({
				                                                        scene: {
				                                                            startSec: Number(selectedSceneRange.startSec ?? 0),
				                                                            endSec: Number(selectedSceneRange.endSec ?? 0),
				                                                        },
				                                                        chunk: clipboardChunk,
				                                                    });
				                                                },
				                                            });
			                                        }
			                                        options.push({
			                                            title: "Add scene above",
			                                            onClick: () => {
		                                                const at = Number(sceneInfo?.startSec ?? 0);
		                                                CompositionService.insertEmptyScriptScene(at, 5);
	                                                ContextMenuStore.hide();
	                                            },
	                                        });
	                                        options.push({
	                                            title: "Add scene below",
	                                            onClick: () => {
	                                                const at = Number(sceneInfo?.endSec ?? 0);
	                                                CompositionService.insertEmptyScriptScene(at, 5);
	                                                ContextMenuStore.hide();
	                                            },
	                                        });
	                                        if (idx > 0) {
	                                            options.push({
	                                                title: "Merge with scene above",
	                                                onClick: () => {
	                                                    const ok = CompositionService.mergeScriptLayerSceneWithPrevious(idx);
                                                    void ok;
                                                    ContextMenuStore.hide();
                                                },
                                            });
                                        }
                                        if (idx < ScriptLayerStore.scenes.length - 1) {
                                            options.push({
                                                title: "Merge with scene below",
                                                onClick: () => {
                                                    const ok = CompositionService.mergeScriptLayerSceneWithNext(idx);
                                                    void ok;
                                                    ContextMenuStore.hide();
                                                },
                                            });
                                        }
                                        options.push({
                                            title: "Delete Scene",
                                            onClick: () => {
                                                try {
                                                    const clip: any = CompositionService.getScriptLayerClip?.();
                                                    const clipInSec = Number(clip?.range?.[0]?.seconds ?? 0);
                                                    const clipOutSec = Number(clip?.range?.[1]?.seconds ?? clip?.duration?.seconds ?? clipInSec);
                                                    const EPS = 1e-3;
                                                    const engineScenes = typeof clip?.getScenes === "function" ? clip.getScenes() : [];
                                                    const engineScene = engineScenes.find((s: any) => s?.index === idx);
                                                    if (engineScene) {
                                                        const start = Number(engineScene.startSec ?? 0);
                                                        const end = Number(engineScene.endSec ?? start);
                                                        const coversWholeClip =
                                                            Number.isFinite(start) &&
                                                            Number.isFinite(end) &&
                                                            Math.abs(start - clipInSec) <= EPS &&
                                                            Math.abs(end - clipOutSec) <= EPS;

                                                        if (coversWholeClip) {
                                                            // If the user deletes the last remaining scene, delete the script clip entirely
                                                            // so the composition returns to the true "empty" state.
                                                            CompositionService.deleteClipById(String(clip?.id));
                                                        } else {
                                                            CompositionService.deleteScriptLayerScene(idx, {
                                                                transcriptMode: "delete",
                                                            });
                                                        }
                                                    }
                                                } catch {}
                                                ContextMenuStore.hide();
                                            },
                                        });
                                        return options;
                                    }}
                                />
                            </Box>
	                        )}
	                        <Box sx={{display: 'flex', flex: 1, minWidth: 0, minHeight: 0, height: '100%', overflow: 'hidden'}}>
					                            <V2TextEditor
					                                words={ScriptLayerStore.transcriptWords}
                                                    title={compositionTitle || "Untitled"}
                                                    onRenameTitle={onRenameCompositionTitle}
	                                                isBeforeTrial={Boolean(isBeforeTrial)}
                                                    transcriptHighlights={transcriptHighlights}
					                                onDuplicateToNewComposition={onDuplicateToNewComposition}
	                                            onTranscriptExporterReady={onTranscriptExporterReady}
					                            />
				                        </Box>
			                    </Box>
			                </div>

                {/* Right pane (Canvas region) */}
                <div
                    data-tour="editor-canvas-workspace"
                    onMouseDownCapture={handleCanvasWorkspaceMouseDownCapture}
                    style={{overflow: 'hidden', minWidth: 0, height: '100%', width: '100%', position: 'relative'}}
                >
                    {/* Floating Aspect Ratio Button (top-left on canvas region) */}
                    <Box sx={{
                        position: 'absolute',
                        top: 12,
                        left: 12,
                        zIndex: 30,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        backgroundColor: 'var(--app-bg-surface)',
                        boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
                        border: '1px solid var(--app-border-subtle)',
                        cursor: 'pointer',
                        '&:hover': {
                            boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
                            backgroundColor: 'var(--app-bg-subtle)',
                        },
                    }}
                         data-tour="editor-aspect-ratio-button"
                         data-canvas-interactive="true"
                         onClick={openClipSizeCard}
                    >
                        <AspectRatio fontSize="small"/>
                    </Box>

                    {/* Aspect ratio dropdown, positioned under the button */}
                    {/* @ts-ignore */}
                    <AspectRatioSelectCard visible={showClipSizeCard}
                        guestEditor={false}
                        handleClose={closeClipSizeCard}
                        topPosition={56}
                        leftPosition={12}
                        containerRef={containerRef}
                        canvasRef={canvasRef}
                    />
                    {/* Floating clip toolbar in the canvas region */}
                    {/* Shows CanvasClipToolbar for video/image clips, CaptionsCanvasToolbar for captions clips */}
                    <CanvasClipToolbar containerRef={containerRef}/>
                    <CaptionsCanvasToolbar containerRef={containerRef}/>
                    <TextCanvasToolbar containerRef={containerRef}/>
                    <CanvasSceneScopeToggle />
                    <TextClipEditorOverlay />

                    {/* Actual canvas */}
                    {/* @ts-ignore */}
                    <CompositionCanvasSection
                        canvasRef={canvasRef}
                        containerRef={containerRef}
                        onCropDoneCapture={onCropDoneCapture}
                    />
                </div>
            </Split>
        </Box>
    );
});

export default V2Editor;
